/**
 * CarOptions — Installation Flow popup (AI built)
 *
 * 4-staps boekingsflow op basis van het Anthropic Designer handoff design
 * (Boekingsflow). Pixel-faithful port van planning/project/app.jsx, in
 * vanilla JS zodat we hem in het Shopify theme kunnen gebruiken.
 *
 * Cart-button swap → opent popup → Agenda → Gegevens → Betaling → Afronden.
 * State persisteert in localStorage. Backend-stubs voor toekomstige
 * lead/order creatie staan gemarkeerd.
 */
(function () {
  'use strict';

  var FLOW_MODE = Object.freeze({
    LEGACY_DIRECT: 'legacy_direct',
    PERSONAL_CONTACT: 'personal_contact',
    DEFAULT_CART: 'default_cart'
  });
  var LEGACY_LOCATION_SLUGS = Object.freeze({
    'CarOptions - Bleiswijk': 'bleiswijk',
    'CarOptions - Enkhuizen': 'enkhuizen'
  });
  var PLANNING_LOCATION_CONFIG_PATH = '/rest/v1/planning_locations?select=slug,selector_value,planning_mode&is_active=eq.true';
  var CONTACT_FINALIZE_PATH = '/functions/v1/contact-booking-finalize-ai';
  var CONTACT_SOURCE = 'website_contactflow';
  var CONTACT_SUMMARY_TEXT = 'We stemmen de afspraak af';
  var CONTACT_COPY = Object.freeze({
    step1Heading: 'We plannen jouw installatie persoonlijk',
    step1Promise: 'Je hoeft nu nog geen datum te kiezen. Binnen één werkdag nemen we contact met je op om samen een geschikt installatiemoment te kiezen.',
    step1Cta: 'Verder met aanvraag',
    step2Chip: 'Wij nemen binnen één werkdag contact met je op',
    step3Cta: 'Installatie aanvragen',
    successHeading: 'Aanvraag ontvangen'
  });

  function getExactConfiguredLocation(selectedGpoValue, rows) {
    if (!Array.isArray(rows)) return null;
    var matches = rows.filter(function (row) {
      return row
        && row.is_active !== false
        && row.selector_value === selectedGpoValue;
    });
    if (matches.length !== 1) return null;
    var match = matches[0];
    if (typeof match.slug !== 'string' || match.slug.trim() === '') return null;
    if (match.planning_mode !== 'direct_calendar' && match.planning_mode !== 'personal_contact') return null;
    return match;
  }

  function resolveFlowMode(selectedGpoValue, rows) {
    if (Object.prototype.hasOwnProperty.call(LEGACY_LOCATION_SLUGS, selectedGpoValue)) {
      return FLOW_MODE.LEGACY_DIRECT;
    }
    var match = getExactConfiguredLocation(selectedGpoValue, rows);
    if (!match) return FLOW_MODE.DEFAULT_CART;
    return match.planning_mode === 'personal_contact'
      ? FLOW_MODE.PERSONAL_CONTACT
      : FLOW_MODE.LEGACY_DIRECT;
  }

  function resolveLocationSlug(selectedGpoValue, rows) {
    if (Object.prototype.hasOwnProperty.call(LEGACY_LOCATION_SLUGS, selectedGpoValue)) {
      return LEGACY_LOCATION_SLUGS[selectedGpoValue];
    }
    var match = getExactConfiguredLocation(selectedGpoValue, rows);
    return match ? match.slug : null;
  }

  function resolveFlowModeFromConfig(selectedGpoValue, loadRows) {
    var legacyMode = resolveFlowMode(selectedGpoValue, null);
    if (legacyMode === FLOW_MODE.LEGACY_DIRECT) return Promise.resolve(legacyMode);
    if (typeof loadRows !== 'function') return Promise.resolve(FLOW_MODE.DEFAULT_CART);
    return Promise.resolve()
      .then(loadRows)
      .then(function (rows) { return resolveFlowMode(selectedGpoValue, rows); })
      .catch(function () { return FLOW_MODE.DEFAULT_CART; });
  }

  function getFlowCta(mode, defaultText) {
    if (mode === FLOW_MODE.LEGACY_DIRECT) return 'Afspraak inplannen';
    if (mode === FLOW_MODE.PERSONAL_CONTACT) return 'Installatie regelen';
    return defaultText;
  }

  function getContactFlowCopy() {
    return {
      step1Heading: CONTACT_COPY.step1Heading,
      step1Promise: CONTACT_COPY.step1Promise,
      step1Cta: CONTACT_COPY.step1Cta,
      step2Chip: CONTACT_COPY.step2Chip,
      step3Cta: CONTACT_COPY.step3Cta,
      successHeading: CONTACT_COPY.successHeading
    };
  }

  function getContactLeadFields(locationId) {
    return {
      location_id: locationId,
      source: CONTACT_SOURCE,
      appointment_date: null,
      appointment_slot: null,
      duration_minutes: null,
      max_concurrent: null
    };
  }

  function createStorefrontCustomerForm() {
    return {
      voornaam: '',
      achternaam: '',
      bedrijf: '',
      email: '',
      tel: ''
    };
  }

  function requireFinalizationRevision(input) {
    if (!Number.isSafeInteger(input.expectedRevision) || input.expectedRevision < 1) {
      throw new Error('Controleer en bewaar je gegevens opnieuw voordat je afrondt.');
    }
  }

  function requestDirectFinalization(fetchImpl, baseUrl, anonKey, input) {
    requireFinalizationRevision(input);
    return fetchImpl(baseUrl + '/rest/v1/rpc/planning_storefront_finalize_v1', {
      method: 'POST',
      headers: { apikey: anonKey, Authorization: 'Bearer ' + anonKey, 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ p_id: input.appointmentId, p_edit_token: input.editToken,
        p_payment_method: input.paymentMethod, p_expected_revision: input.expectedRevision })
    }).then(function (res) {
      return res.text().then(function (text) {
        var rows; try { rows = JSON.parse(text); } catch (_) { throw new Error('Onbekende aanvraagstatus'); }
        if (!res.ok) { var error = new Error('Het bevestigen is niet gelukt.'); error.code = rows && rows.message; error.sqlState = res.status >= 400 && res.status < 500 ? rows && rows.code : null; throw error; }
        var row = Array.isArray(rows) && rows.length === 1 ? rows[0] : null;
        if (!row || row.id !== input.appointmentId || row.payment_method !== input.paymentMethod) return { ok: false, reason: 'invalid_receipt' };
        if (row.outcome === 'already_processed' && ['open', 'bevestigd', 'garantie'].indexOf(row.status) !== -1) {
          return { ok: input.paymentMethod === 'after_install', reason: 'already_processed' };
        }
        return { ok: row.status === 'onbevestigd' && ['saved', 'replayed'].indexOf(row.outcome) !== -1 };
      });
    });
  }

  function requestContactFinalization(fetchImpl, baseUrl, input) {
    requireFinalizationRevision(input);
    var requestBody = {
      appointment_id: input.appointmentId,
      edit_token: input.editToken,
      payment_method: input.paymentMethod,
      expected_revision: input.expectedRevision
    };
    return fetchImpl(baseUrl + CONTACT_FINALIZE_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
      credentials: 'omit'
    }).then(function (res) {
      return res.text().then(function (txt) {
        var data = null;
        try { data = txt ? JSON.parse(txt) : null; } catch (e) {}
        if (!res.ok && res.status >= 400 && res.status < 500 && data && data.code === '22023' && data.message === 'storefront_finalize_revision_changed') {
          return { status: 'failed', reason: 'revision_changed' };
        }
        var status = data && data.status;
        if (status === 'succeeded' || status === 'checkout_ready' || status === 'processing'
            || status === 'pending_review' || status === 'failed') {
          return { status: status };
        }
        return { status: 'pending_review' };
      });
    }).catch(function () {
      return { status: 'pending_review' };
    });
  }

  function getContactFinalizationOutcome(status) {
    if (status === 'succeeded') {
      return { kind: 'succeeded', showSuccess: true, startCheckout: false, retryable: false };
    }
    if (status === 'checkout_ready') {
      return { kind: 'checkout_ready', showSuccess: false, startCheckout: true, retryable: false };
    }
    if (status === 'failed') {
      return { kind: 'failed', showSuccess: false, startCheckout: false, retryable: true };
    }
    return { kind: 'waiting', showSuccess: false, startCheckout: false, retryable: false };
  }

  function getCartAttributes(appointmentId, paymentMethod, mode) {
    var attributes = {};
    if (appointmentId) attributes.planning_appointment_id = appointmentId;
    attributes.planning_payment_method = paymentMethod;
    if (mode === FLOW_MODE.PERSONAL_CONTACT) {
      attributes.planning_flow_mode = FLOW_MODE.PERSONAL_CONTACT;
    }
    return attributes;
  }

  function getFunnelContext(mode, locationSlug, extraMetadata) {
    var metadata = {};
    var sourceMetadata = extraMetadata || {};
    for (var key in sourceMetadata) {
      if (Object.prototype.hasOwnProperty.call(sourceMetadata, key)) metadata[key] = sourceMetadata[key];
    }
    if (mode === FLOW_MODE.PERSONAL_CONTACT) {
      metadata.source = CONTACT_SOURCE;
      metadata.planning_mode = FLOW_MODE.PERSONAL_CONTACT;
    }
    return { location_slug: locationSlug, metadata: metadata };
  }

  function getViewedFunnelEvent(mode, step) {
    if (mode === FLOW_MODE.PERSONAL_CONTACT && step === 2) return null;
    return 'step_' + step + '_viewed';
  }

  function buildFlowIdentity(input) {
    if (!input || typeof input !== 'object') return null;
    var selectedLocationValue = typeof input.selectedLocationValue === 'string' ? input.selectedLocationValue : '';
    var locationSlug = typeof input.locationSlug === 'string' ? input.locationSlug : '';
    var planningMode = typeof input.planningMode === 'string' ? input.planningMode : '';
    var productHandle = typeof input.productHandle === 'string' ? input.productHandle : '';
    var variantId = typeof input.variantId === 'string' ? input.variantId : '';
    if (!selectedLocationValue || !locationSlug || !productHandle || !variantId) return null;
    if (planningMode !== FLOW_MODE.LEGACY_DIRECT && planningMode !== FLOW_MODE.PERSONAL_CONTACT) return null;
    return JSON.stringify({
      version: 1,
      selected_location_value: selectedLocationValue,
      location_slug: locationSlug,
      planning_mode: planningMode,
      product_handle: productHandle,
      variant_id: variantId
    });
  }

  function resetFlowBoundState(flowState) {
    flowState.step = 1;
    flowState.completed = false;
    flowState.selectedDate = null;
    flowState.selectedSlot = null;
    flowState.viewMonth = null;
    flowState.payment = 'na';
    flowState.formErrors = {};
    flowState.formErrorMsg = null;
    flowState.finalizationInFlight = false;
    flowState.finalizationStatus = null;
    flowState.finalizationMessage = null;
    flowState.helpOpen = false;
    flowState.appointmentId = null;
    flowState.editToken = null;
    flowState.leadSaveDraft = null;
    flowState.savedRevision = null;
    flowState.finalizationAttempt = null;
    flowState.locationId = null;
    flowState.locationSlug = null;
    flowState.locationCompany = null;
    flowState.locationFullAddress = null;
    flowState.locationHours = null;
    flowState.dateOverrides = null;
    flowState.holidayMap = null;
    flowState.capacityRules = null;
    flowState.defaultDurationMinutes = null;
    flowState.maxPerHour = 2;
    flowState.autoBlockHolidays = true;
    flowState.variantId = null;
    flowState.availability = {};
    flowState.slotCounts = null;
    flowState.slotMaxMap = {};
    flowState.funnelSessionId = null;
  }

  function bindFlowIdentity(flowState, currentIdentity) {
    if (flowState.flowIdentity === currentIdentity) {
      return { changed: false, identity: currentIdentity };
    }
    advanceFlowGeneration(flowState);
    resetFlowBoundState(flowState);
    flowState.flowIdentity = currentIdentity;
    return { changed: true, identity: currentIdentity };
  }

  function advanceFlowGeneration(flowState) {
    var current = Number.isInteger(flowState.flowGeneration) ? flowState.flowGeneration : 0;
    flowState.flowGeneration = current + 1;
    flowState.asyncRequestVersions = {};
    return flowState.flowGeneration;
  }

  function beginFlowOperation(flowState, scope, details) {
    details = details || {};
    if (!flowState.asyncRequestVersions || typeof flowState.asyncRequestVersions !== 'object') {
      flowState.asyncRequestVersions = {};
    }
    var nextVersion = (flowState.asyncRequestVersions[scope] || 0) + 1;
    flowState.asyncRequestVersions[scope] = nextVersion;
    var parent = details.parent || null;
    return {
      expectedIdentity: parent ? parent.expectedIdentity : flowState.flowIdentity,
      flowGeneration: parent ? parent.flowGeneration : (flowState.flowGeneration || 0),
      scope: scope,
      operationGeneration: nextVersion,
      parentScope: parent ? parent.scope : null,
      parentGeneration: parent ? parent.operationGeneration : null,
      targetLocationSlug: details.targetLocationSlug || (parent && parent.targetLocationSlug) || null,
      cachedLocationId: flowState.locationId || null,
      cachedLocationSlug: flowState.locationSlug || null,
      variantId: details.variantId || (parent && parent.variantId) || null
    };
  }

  function isFlowOperationCurrent(flowState, operation, isIdentityCurrent) {
    if (!operation || !operation.expectedIdentity) return false;
    if (flowState.flowIdentity !== operation.expectedIdentity) return false;
    if ((flowState.flowGeneration || 0) !== operation.flowGeneration) return false;
    var versions = flowState.asyncRequestVersions || {};
    if (versions[operation.scope] !== operation.operationGeneration) return false;
    if (operation.parentScope && versions[operation.parentScope] !== operation.parentGeneration) return false;
    return typeof isIdentityCurrent === 'function'
      && isIdentityCurrent(operation.expectedIdentity);
  }

  function commitFlowOperation(flowState, operation, isIdentityCurrent, mutation) {
    if (!isFlowOperationCurrent(flowState, operation, isIdentityCurrent)) return false;
    mutation();
    return true;
  }

  function buildContactLeadPayload(basePayload, locationId, completedAt) {
    var payload = {};
    var base = basePayload || {};
    for (var key in base) {
      if (Object.prototype.hasOwnProperty.call(base, key) && key !== 'step2_at') {
        payload[key] = base[key];
      }
    }
    payload.status = 'lead';
    var contactFields = getContactLeadFields(locationId);
    for (var contactKey in contactFields) {
      if (Object.prototype.hasOwnProperty.call(contactFields, contactKey)) {
        payload[contactKey] = contactFields[contactKey];
      }
    }
    if (typeof completedAt === 'string' && completedAt) payload.step2_at = completedAt;
    return payload;
  }

  function restorePersistedFlowState(flowState, parsed) {
    if (!flowState || !parsed || typeof parsed !== 'object') return;
    if (parsed.selectedDate) flowState.selectedDate = parsed.selectedDate;
    if (parsed.selectedSlot) flowState.selectedSlot = parsed.selectedSlot;
    if (parsed.payment) flowState.payment = parsed.payment;
    if (typeof parsed.flowIdentity === 'string') flowState.flowIdentity = parsed.flowIdentity;
    if (parsed.completed === true) {
      flowState.completed = true;
      flowState.step = 4;
    } else {
      flowState.completed = false;
      if (Number.isInteger(parsed.step) && parsed.step >= 1 && parsed.step <= 3) {
        flowState.step = parsed.step;
      }
    }
    if (parsed.appointmentId) flowState.appointmentId = parsed.appointmentId;
    if (parsed.editToken) flowState.editToken = parsed.editToken;
    var draft = parsed.leadSaveDraft;
    var uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (draft && draft.identity === flowState.flowIdentity &&
        uuidPattern.test(draft.id) && uuidPattern.test(draft.editToken) &&
        Number.isSafeInteger(draft.revision) && draft.revision > 0 &&
        draft.payload && typeof draft.payload === 'object' && !Array.isArray(draft.payload) &&
        typeof draft.fingerprint === 'string') {
      flowState.leadSaveDraft = draft;
      if (Number.isSafeInteger(parsed.savedRevision) && parsed.savedRevision > 0 &&
          parsed.savedRevision === draft.revision && parsed.appointmentId === draft.id && parsed.editToken === draft.editToken) {
        flowState.savedRevision = parsed.savedRevision;
      }
      var attempt = parsed.finalizationAttempt;
      if (attempt && attempt.identity === flowState.flowIdentity && attempt.appointmentId === draft.id &&
          attempt.editToken === draft.editToken && attempt.expectedRevision === flowState.savedRevision &&
          ['pay_now', 'after_install'].indexOf(attempt.paymentMethod) !== -1) {
        flowState.finalizationAttempt = attempt;
        flowState.payment = attempt.paymentMethod === 'pay_now' ? 'nu' : 'na';
        if (!flowState.completed) flowState.step = 3;
      }
    }
    if (parsed.locationId) flowState.locationId = parsed.locationId;
    if (parsed.locationSlug) flowState.locationSlug = parsed.locationSlug;
    if (parsed.form && typeof parsed.form === 'object' && flowState.form) {
      Object.keys(flowState.form).forEach(function (key) {
        if (typeof parsed.form[key] === 'string') flowState.form[key] = parsed.form[key];
      });
    }
  }

  function getFunnelSessionForEvent(eventName, currentSessionId, createSessionId) {
    if (currentSessionId) return currentSessionId;
    if (eventName === 'popup_closed') return null;
    return typeof createSessionId === 'function' ? createSessionId() : null;
  }

  function resolveLocationIdForIdentity(input) {
    var expectedIdentity = input.expectedIdentity;
    function identityIsCurrent() {
      return !!expectedIdentity
        && typeof input.isIdentityCurrent === 'function'
        && input.isIdentityCurrent(expectedIdentity);
    }
    if (!identityIsCurrent()) return Promise.resolve({ kind: 'identity_changed' });
    if (input.cachedLocationId && input.cachedLocationSlug === input.targetLocationSlug) {
      return Promise.resolve({
        kind: 'resolved',
        locationId: input.cachedLocationId,
        locationSlug: input.targetLocationSlug
      });
    }
    if (!input.targetLocationSlug || typeof input.fetchLocation !== 'function') {
      return Promise.reject(new Error('Locatie niet gevonden: ' + (input.targetLocationSlug || '')));
    }
    return Promise.resolve()
      .then(function () {
        if (!identityIsCurrent()) return { __identityChanged: true };
        return input.fetchLocation(input.targetLocationSlug);
      })
      .then(function (rows) {
        if ((rows && rows.__identityChanged) || !identityIsCurrent()) {
          return { kind: 'identity_changed' };
        }
        if (rows && rows[0] && rows[0].id) {
          return {
            kind: 'resolved',
            locationId: rows[0].id,
            locationSlug: input.targetLocationSlug
          };
        }
        throw new Error('Locatie niet gevonden: ' + input.targetLocationSlug);
      });
  }

  function runSaveLeadOrchestration(input) {
    var flowState = input.state;
    var operation = {
      expectedIdentity: flowState.flowIdentity,
      appointmentId: flowState.appointmentId || null,
      editToken: flowState.editToken || null,
      cachedLocationId: flowState.locationId || null,
      cachedLocationSlug: flowState.locationSlug || null,
      targetLocationSlug: input.targetLocationSlug
    };
    var mismatchNotified = false;
    function identityIsCurrent() {
      return !!operation.expectedIdentity
        && typeof input.isIdentityCurrent === 'function'
        && input.isIdentityCurrent(operation.expectedIdentity);
    }
    function targetIsStillOwned() {
      return flowState.appointmentId === operation.appointmentId
        && flowState.editToken === operation.editToken;
    }
    function identityChanged() {
      if (!mismatchNotified && typeof input.onIdentityMismatch === 'function') {
        mismatchNotified = true;
        input.onIdentityMismatch();
      }
      return { kind: 'identity_changed', rows: null };
    }
    function superseded() {
      return { kind: 'superseded', rows: null };
    }
    function applyRows(rows, location) {
      if (!identityIsCurrent()) return identityChanged();
      if (!targetIsStillOwned()) return superseded();
      if (rows && rows[0]) {
        flowState.appointmentId = rows[0].id;
        flowState.editToken = rows[0].edit_token || null;
        flowState.locationId = location.locationId;
        flowState.locationSlug = location.locationSlug;
        if (typeof input.persistState === 'function') input.persistState();
      }
      return { kind: 'saved', rows: rows || null };
    }
    if (flowState.completed === true) return Promise.resolve({ kind: 'ignored', rows: null });
    if (!identityIsCurrent()) return Promise.resolve(identityChanged());

    var resolvedLocation;
    var payload;
    return Promise.resolve()
      .then(function () {
        return input.resolveLocationId(operation);
      })
      .then(function (location) {
        if (!identityIsCurrent() || !location || location.kind === 'identity_changed') {
          return { __stop: identityChanged() };
        }
        resolvedLocation = location;
        return input.buildPayload(location, operation);
      })
      .then(function (builtPayload) {
        if (builtPayload && builtPayload.__stop) return builtPayload;
        if (!identityIsCurrent()) return { __stop: identityChanged() };
        if (!targetIsStillOwned()) return { __stop: superseded() };
        payload = builtPayload;
        if (operation.appointmentId && operation.editToken) {
          return input.patchLead({
            appointmentId: operation.appointmentId,
            editToken: operation.editToken,
            payload: payload
          }).then(function (rows) {
            return { __writeKind: 'patch', rows: rows };
          });
        }
        return input.insertLead({ payload: payload }).then(function (rows) {
          return { __writeKind: 'insert', rows: rows };
        });
      })
      .then(function (writeResult) {
        if (writeResult && writeResult.__stop) return writeResult.__stop;
        if (!identityIsCurrent()) return identityChanged();
        if (!targetIsStillOwned()) return superseded();
        if (writeResult.__writeKind === 'patch' && (!writeResult.rows || writeResult.rows.length === 0)) {
          if (!identityIsCurrent()) return identityChanged();
          if (!targetIsStillOwned()) return superseded();
          return input.insertLead({ payload: payload }).then(function (rows) {
            if (!identityIsCurrent()) return identityChanged();
            if (!targetIsStillOwned()) return superseded();
            return applyRows(rows, resolvedLocation);
          });
        }
        return applyRows(writeResult.rows, resolvedLocation);
      });
  }

  function runStepOrchestration(input) {
  var flowState = input.state, expectedIdentity = flowState.flowIdentity,
      expectedGeneration = flowState.flowGeneration,
      isContact = flowState.flowMode === FLOW_MODE.PERSONAL_CONTACT;
  if (flowState.completed === true) return Promise.resolve({ kind: "ignored" });
  function identityIsCurrent() { return flowState.flowGeneration === expectedGeneration && typeof input.isIdentityCurrent === "function" && input.isIdentityCurrent(expectedIdentity); }
  function identityChanged() {
    if (flowState.flowGeneration === expectedGeneration && typeof input.onIdentityMismatch === "function") input.onIdentityMismatch();
    return Promise.resolve({ kind: "identity_changed" });
  }
  function saveFailed() {
    if (typeof input.onSaveError === "function") input.onSaveError();
    return { kind: "save_failed" };
  }
  if (!identityIsCurrent()) return identityChanged();
  if (flowState.step === 1) {
    if (isContact) { input.goToStep(2); return Promise.resolve({ kind: "advanced" }); }
    if (!input.hasDate || !input.hasSlot) {
      if (typeof input.onMissingSelection === "function") input.onMissingSelection();
      return Promise.resolve({ kind: "invalid" });
    }
    input.goToStep(2);
    if (typeof input.triggerSaveLead === "function") input.triggerSaveLead();
    return Promise.resolve({ kind: "advanced" });
  }
  if (flowState.step !== 2) return Promise.resolve({ kind: "ignored" });
  if (typeof input.syncForm === 'function') input.syncForm();
  var validationMessage = input.validateStep2();
  if (validationMessage) {
    if (typeof input.onValidationError === "function") input.onValidationError(validationMessage);
    return Promise.resolve({ kind: "invalid" });
  }
  var submittedForm = JSON.stringify(flowState.form);
  var saveOptions = { markStep2Complete: isContact }, timer;
  if (typeof submittedForm === "string") saveOptions.expectedForm = submittedForm;
  if (typeof input.setBusy === "function") input.setBusy(true);
  var saving = Promise.resolve().then(function () {
    return input.saveLead(saveOptions);
  });
  var deadline = new Promise(function (resolve) {
    timer = setTimeout(function () { saveOptions.cancelled = true; resolve(); }, input.saveDeadlineMs || 10000);
  });
  return Promise.race([saving, deadline]).then(function (rows) {
    if (!identityIsCurrent()) return identityChanged();
    if (flowState.step !== 2) return { kind: 'ignored' };
    if (typeof input.syncForm === 'function') input.syncForm();
    if (!Array.isArray(rows) || rows.length !== 1 || !rows[0] ||
        !flowState.appointmentId || !flowState.editToken ||
        rows[0].id !== flowState.appointmentId || rows[0].edit_token !== flowState.editToken ||
        JSON.stringify(flowState.form) !== submittedForm) return saveFailed();
    input.goToStep(3);
    return { kind: "advanced" };
  }).catch(function () {
    return identityIsCurrent() ? saveFailed() : identityChanged();
  }).finally(function () {
    clearTimeout(timer);
    if (identityIsCurrent() && typeof input.setBusy === "function") input.setBusy(false);
  });
}

  function runFinalizationOrchestration(input) {
    var flowState = input.state;
    var expectedIdentity = flowState.flowIdentity;
    if (flowState.completed === true) return Promise.resolve({ kind: 'ignored' });
    if (flowState.step !== 3) return Promise.resolve({ kind: 'ignored' });
    if (flowState.finalizationInFlight) return Promise.resolve({ kind: 'ignored' });
    function identityIsCurrent() {
      return typeof input.isIdentityCurrent === 'function'
        && input.isIdentityCurrent(expectedIdentity);
    }
    function identityChanged() {
      flowState.finalizationInFlight = false;
      if (typeof input.onIdentityMismatch === 'function') input.onIdentityMismatch();
      return { kind: 'identity_changed' };
    }
    if (!identityIsCurrent()) return Promise.resolve(identityChanged());

    flowState.finalizationInFlight = true;
    flowState.finalizationStatus = null;
    flowState.finalizationMessage = null;
    var isContact = flowState.flowMode === FLOW_MODE.PERSONAL_CONTACT;
    var operation;

    if (!isContact) {
      operation = Promise.resolve()
        .then(function () { return input.confirmBooking(); })
        .then(function (result) {
          if (!identityIsCurrent()) return identityChanged();
          if (!result || !result.ok) {
            if (typeof input.onDirectFailure === 'function') input.onDirectFailure(result);
            return { kind: 'failed' };
          }
          if (typeof input.track === 'function') input.track('booking_confirmed');
          input.goToStep(4);
          return { kind: 'succeeded' };
        });
    } else {
      operation = Promise.resolve()
        .then(function () {
          if (!identityIsCurrent()) return { __identityChanged: true };
          return input.requestContactFinalization();
        })
        .then(function (result) {
          if ((result && result.__identityChanged) || !identityIsCurrent()) return identityChanged();
          var outcome = getContactFinalizationOutcome(result && result.status);
          flowState.finalizationStatus = result && result.status;
          if (outcome.showSuccess) {
            if (typeof input.track === 'function') input.track('contact_request_submitted');
            input.goToStep(4);
            return { kind: 'succeeded' };
          }
          if (outcome.startCheckout) {
            if (typeof input.track === 'function') input.track('contact_checkout_ready');
            return Promise.resolve(input.startCheckout(expectedIdentity)).then(function (checkoutResult) {
              if (checkoutResult && checkoutResult.reason === 'identity_changed') {
                return identityChanged();
              }
              if (!checkoutResult || !checkoutResult.ok) {
                flowState.finalizationMessage = 'Het doorsturen naar betalen is niet gelukt. Probeer opnieuw; je aanvraag blijft bewaard.';
                return { kind: 'checkout_failed' };
              }
              return { kind: 'checkout_ready' };
            });
          }
          if (outcome.retryable) {
            flowState.finalizationMessage = 'Het afronden is niet gelukt. Probeer opnieuw; je aanvraag blijft bewaard.';
            return { kind: 'failed' };
          }
          flowState.finalizationMessage = 'We verwerken je aanvraag nog. Probeer over een moment opnieuw om de status te controleren.';
          return { kind: 'waiting' };
        });
    }

    return operation
      .catch(function () {
        if (isContact) {
          flowState.finalizationMessage = 'We konden je aanvraagstatus niet controleren. Probeer het over een moment opnieuw.';
        } else if (typeof input.onDirectFailure === 'function') {
          input.onDirectFailure();
        }
        return { kind: 'failed' };
      })
      .finally(function () {
        flowState.finalizationInFlight = false;
        if (flowState.step === 3 && typeof input.render === 'function') input.render();
      });
  }

  function parseMandatoryCartResponse(response, operation, validateShape) {
    if (
      response && response.headers && typeof response.headers.get === 'function'
      && response.headers.get('cf-mitigated') === 'challenge'
    ) {
      return Promise.reject(new Error(operation + '_challenge'));
    }
    if (!response || response.ok !== true || typeof response.text !== 'function') {
      var status = response && response.status;
      return Promise.reject(new Error(operation + '_http_' + (status || 'unknown')));
    }
    return response.text().then(function (text) {
      var data;
      try { data = JSON.parse(text); } catch (e) { throw new Error(operation + '_invalid_json'); }
      if (!validateShape(data)) throw new Error(operation + '_invalid_shape');
      return data;
    });
  }

  function buildCartPermalink(context, checkoutUrl) {
    if (!context || !Array.isArray(context.items) || context.items.length === 0) return null;
    var itemParts = [];
    for (var i = 0; i < context.items.length; i++) {
      var item = context.items[i] || {};
      var variantId = parseInt(item.id || item.variant_id, 10);
      var quantity = parseInt(item.quantity, 10);
      if (!variantId || !quantity || quantity < 1) return null;
      itemParts.push(variantId + ':' + quantity);
    }

    var params = [];
    var firstProperties = context.items[0] && context.items[0].properties;
    if (firstProperties && Object.keys(firstProperties).length > 0) {
      var json = JSON.stringify(firstProperties);
      var binary = unescape(encodeURIComponent(json));
      var encoded = btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      params.push('properties=' + encodeURIComponent(encoded));
    }

    var attributes = context.attributes || {};
    for (var key in attributes) {
      if (!Object.prototype.hasOwnProperty.call(attributes, key)) continue;
      params.push(
        encodeURIComponent('attributes[' + key + ']')
        + '=' + encodeURIComponent(String(attributes[key]))
      );
    }

    var checkoutQuery = String(checkoutUrl || '').split('?')[1];
    if (checkoutQuery) params.push(checkoutQuery);
    return '/cart/' + itemParts.join(',') + (params.length ? '?' + params.join('&') : '');
  }

  function fetchMandatoryCart(fetchImpl, operation, url, init) {
    return Promise.resolve()
      .then(function () { return fetchImpl(url, init); })
      .catch(function () { throw new Error(operation + '_network'); });
  }

  function runMandatoryCartCheckout(input) {
    var context = input.context;
    function fail(reason) {
      if (typeof input.onFailure === 'function') input.onFailure(reason);
      return { ok: false, reason: reason };
    }
    if (!context || !Array.isArray(context.items) || context.items.length === 0) {
      return Promise.resolve(fail('missing_context'));
    }
    var attributes = context.attributes;
    var requireContactAttributes = context.requireContactAttributes !== false;
    var hasAppointment = attributes && typeof attributes.planning_appointment_id === 'string'
      && attributes.planning_appointment_id.length > 0;
    var hasPayment = attributes && attributes.planning_payment_method === 'pay_now';
    var hasContactMode = !requireContactAttributes
      || attributes.planning_flow_mode === FLOW_MODE.PERSONAL_CONTACT;
    if (!hasAppointment || !hasPayment || !hasContactMode) {
      return Promise.resolve(fail('missing_attributes'));
    }
    if (!input.expectedIdentity || !input.isIdentityCurrent(input.expectedIdentity)) {
      return Promise.resolve(fail('identity_changed'));
    }
    var attributesToWrite = {};
    for (var attributeKey in attributes) {
      if (Object.prototype.hasOwnProperty.call(attributes, attributeKey)) {
        attributesToWrite[attributeKey] = attributes[attributeKey];
      }
    }
    if (!requireContactAttributes) attributesToWrite.planning_flow_mode = '';

    return Promise.resolve()
      .then(function () {
        return fetchMandatoryCart(input.fetchImpl, 'cart_clear', '/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: '{}'
        });
      })
      .then(function (response) {
        return parseMandatoryCartResponse(response, 'cart_clear', function (data) {
          return !!data && Array.isArray(data.items);
        });
      })
      .then(function () {
        return fetchMandatoryCart(input.fetchImpl, 'cart_add', '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: context.items })
        });
      })
      .then(function (response) {
        return parseMandatoryCartResponse(response, 'cart_add', function (data) {
          return !!data && Array.isArray(data.items) && data.items.length > 0;
        });
      })
      .then(function () {
        return fetchMandatoryCart(input.fetchImpl, 'cart_update', '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attributesToWrite })
        });
      })
      .then(function (response) {
        return parseMandatoryCartResponse(response, 'cart_update', function (data) {
          if (!data || !data.attributes || typeof data.attributes !== 'object') return false;
          for (var key in attributesToWrite) {
            if (!Object.prototype.hasOwnProperty.call(attributesToWrite, key)) continue;
            if (!requireContactAttributes && key === 'planning_flow_mode') {
              var effectiveMode = data.attributes[key];
              if (effectiveMode != null && String(effectiveMode).trim() !== '') return false;
              continue;
            }
            if (String(data.attributes[key]) !== String(attributesToWrite[key])) return false;
          }
          return true;
        });
      })
      .then(function () {
        if (!input.isIdentityCurrent(input.expectedIdentity)) return fail('identity_changed');
        input.redirect();
        return { ok: true };
      })
      .catch(function (error) {
        var reason = (error && error.message) || 'cart_failed';
        if (
          /^cart_(clear|add|update)_(challenge|http_429|network)$/.test(reason)
          && typeof input.fallbackRedirect === 'function'
        ) {
          try {
            input.fallbackRedirect(reason);
            return { ok: true, reason: reason, fallback: true };
          } catch (fallbackError) {
            return fail('cart_permalink_failed');
          }
        }
        return fail(reason);
      });
  }

  function canNavigateBackward(currentStep, targetStep, completed) {
    if (completed === true || currentStep === 4) return false;
    return Number.isInteger(targetStep) && targetStep >= 1 && targetStep <= 4 && targetStep < currentStep;
  }

  function getContactPaymentKeyboardTarget(key, currentIndex, optionCount) {
    if (!Number.isInteger(currentIndex) || !Number.isInteger(optionCount)
        || optionCount < 1 || currentIndex < 0 || currentIndex >= optionCount) return null;
    if (key === 'ArrowRight' || key === 'ArrowDown') return (currentIndex + 1) % optionCount;
    if (key === 'ArrowLeft' || key === 'ArrowUp') return (currentIndex - 1 + optionCount) % optionCount;
    if (key === 'Home') return 0;
    if (key === 'End') return optionCount - 1;
    if (key === ' ' || key === 'Spacebar' || key === 'Enter') return currentIndex;
    return null;
  }

  function getStep2FitPlan(input) {
    var tight = !!(input && input.initialOverflow === true);
    return {
      tight: tight,
      compact: tight && !!(input && input.tightOverflow === true)
    };
  }

  // Test-only CommonJS boundary. Browser initialization must continue even
  // when a theme/app happens to define a module global.
  if (typeof document === 'undefined' && typeof module !== 'undefined' && module.exports) {
    module.exports = {
      FLOW_MODE: FLOW_MODE,
      PLANNING_LOCATION_CONFIG_PATH: PLANNING_LOCATION_CONFIG_PATH,
      CONTACT_FINALIZE_PATH: CONTACT_FINALIZE_PATH,
      resolveFlowMode: resolveFlowMode,
      resolveFlowModeFromConfig: resolveFlowModeFromConfig,
      resolveLocationSlug: resolveLocationSlug,
      getFlowCta: getFlowCta,
      getContactFlowCopy: getContactFlowCopy,
      getContactLeadFields: getContactLeadFields,
      createStorefrontCustomerForm: createStorefrontCustomerForm,
      requestContactFinalization: requestContactFinalization,
      requestDirectFinalization: requestDirectFinalization,
      getContactFinalizationOutcome: getContactFinalizationOutcome,
      getCartAttributes: getCartAttributes,
      getFunnelContext: getFunnelContext,
      getViewedFunnelEvent: getViewedFunnelEvent,
      buildFlowIdentity: buildFlowIdentity,
      bindFlowIdentity: bindFlowIdentity,
      advanceFlowGeneration: advanceFlowGeneration,
      beginFlowOperation: beginFlowOperation,
      isFlowOperationCurrent: isFlowOperationCurrent,
      commitFlowOperation: commitFlowOperation,
      buildContactLeadPayload: buildContactLeadPayload,
      restorePersistedFlowState: restorePersistedFlowState,
      getFunnelSessionForEvent: getFunnelSessionForEvent,
      resolveLocationIdForIdentity: resolveLocationIdForIdentity,
      runSaveLeadOrchestration: runSaveLeadOrchestration,
      runStepOrchestration: runStepOrchestration,
      runFinalizationOrchestration: runFinalizationOrchestration,
      runMandatoryCartCheckout: runMandatoryCartCheckout,
      buildCartPermalink: buildCartPermalink,
      canNavigateBackward: canNavigateBackward,
      getContactPaymentKeyboardTarget: getContactPaymentKeyboardTarget,
      getStep2FitPlan: getStep2FitPlan
    };
    return;
  }

  // ============================================================
  // CONFIG
  // ============================================================

  // Supabase REST endpoints — popup belt direct, dashboard zit achter portal-auth.
  // Override via window.CO_AI_SUPABASE_URL / window.CO_AI_SUPABASE_ANON_KEY indien gewenst.
  var SUPABASE_URL = (typeof window !== 'undefined' && window.CO_AI_SUPABASE_URL)
    ? window.CO_AI_SUPABASE_URL
    : 'https://ukhajobbyvkyryaavadh.supabase.co';
  var SUPABASE_ANON_KEY = (typeof window !== 'undefined' && window.CO_AI_SUPABASE_ANON_KEY)
    ? window.CO_AI_SUPABASE_ANON_KEY
    : ''; // ⚠ Vul aan in theme.liquid: window.CO_AI_SUPABASE_ANON_KEY = 'eyJ...'

  // Niet-legacy locaties starten fail-closed en worden pas actief nadat de
  // minimale publieke configuratie exact en eenduidig is geladen.
  var ACTIVE_LOCATION_CONFIGS = null;
  var LOCATION_CONFIG_PROMISE = null;
  var LOCATION_CONFIG_REQUEST_GENERATION = 0;
  var PLANNING_LOCATION_INTENT_GENERATION = 0;

  // ai code: gating per variant — popup + cart-button swap activeren ALLEEN
  // voor varianten in deze lijst. Voor andere varianten gewoon de normale
  // Shopify-checkout houden (geen interferentie). Override via:
  //   window.CO_AI_ALLOWED_VARIANT_IDS = ['49147225047374', '...'];
  // in theme.liquid voor flexibele config zonder JS-aanpassing.
  var ALLOWED_VARIANT_IDS = (typeof window !== 'undefined' && Array.isArray(window.CO_AI_ALLOWED_VARIANT_IDS))
    ? window.CO_AI_ALLOWED_VARIANT_IDS.map(String)
    : []; // leeg = popup actief op ALLE varianten

  // ai code: producten waar de popup NOOIT actief mag zijn. Het losse
  // "CarPlayBox installatie" product heeft locaties als varianten — popup
  // daarop = corrupt order (2 installatie-regels, geen box; incident
  // #232014989: variant Alkmaar + GPO Bleiswijk, box ontbrak).
  var EXCLUDED_PRODUCT_HANDLES = ['carplaybox-installatie'];

  function getCurrentProductHandle() {
    var m = (window.location.pathname || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  // Product-scoped context is emitted only by this product's Liquid page.
  function getVwScreenBookingContext() {
    var context = window.CO_AI_VW_SCREEN_PRODUCT;
    if (!context || String(context.productId) !== '16074328506702'
        || context.handle !== 'volkswagen-carplay-scherm'
        || getCurrentProductHandle() !== context.handle) return null;
    return { brand: 'Volkswagen', serviceLabel: 'Volkswagen-scherm installatie' };
  }

  function getVwScreenRadioProperties() {
    if (!getVwScreenBookingContext()) return null;
    var field = document.querySelector('#carplayboxform [name="properties[Huidige radio]"]');
    if (!field || field.disabled) return null;
    var radioLabel = String(field.value || '').trim();
    if (!radioLabel) return null;
    var properties = { 'Huidige radio': radioLabel };
    var idField = document.querySelector('#carplayboxform [name="properties[_co_vw_radio_id]"]');
    if (idField && !idField.disabled) {
      var radioId = String(idField.value || '').trim();
      if (radioId) properties._co_vw_radio_id = radioId;
    }
    return properties;
  }

  var SWITCH_SELECTOR = 'input[data-type="switch"][name="Montage en Installatie"]';
  var SUBMIT_SELECTOR = '.t4s-product-form__submit[name="add"]';
  var BTN_TEXT_SELECTOR = '.t4s-btn-atc_text';
  var BTN_ICON_SELECTOR = '.add-to-cart__icon';
  var LOCATION_SELECT_SELECTOR = '.location_selector select';
  var STATE_ATTR = 'data-co-ai-mode';
  var ORIG_TEXT_ATTR = 'data-co-ai-original-text';
  var ORIG_ICON_ATTR = 'data-co-ai-original-icon';
  var APPOINTMENT_TEXT = getFlowCta(FLOW_MODE.LEGACY_DIRECT, '');
  var APPOINTMENT_ICON_CLASS = 'la-calendar-alt';
  var POPUP_ID = 'co-ai-planner';
  var STORAGE_KEY = 'co-ai-planner-state';

  // Slot-interval = elk uur tussen open en close
  var SLOT_INTERVAL_MINUTES = 60;
  // Fallback wanneer config niet geladen kan worden
  var FALLBACK_OPEN_HOUR = 9;
  var FALLBACK_CLOSE_HOUR = 17;
  var FALLBACK_CLOSED_WEEKDAYS = [0, 6]; // za/zo
  var DATE_BADGES = {}; // niet meer gebruikt, kan later toegevoegd voor SALE etc.

  var MONTH_NAMES = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
  var DAY_NAMES = ['Zondag','Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag'];

  // ============================================================
  // STATE
  // ============================================================

  var state = {
    step: 1,
    completed: false,
    flowMode: FLOW_MODE.LEGACY_DIRECT,
    flowIdentity: null,
    flowGeneration: 0,
    asyncRequestVersions: {},
    selectedDate: null,        // 'YYYY-MM-DD'
    selectedSlot: null,        // 'HH:MM'
    viewMonth: null,           // Date — eerste dag van zichtbare maand
    payment: 'na',             // 'na' | 'nu'
    form: createStorefrontCustomerForm(),
    formErrors: {},            // { fieldKey: true } — door validateStep2 ingevuld
    formErrorMsg: null,        // banner-tekst bij submit-fout
    finalizationInFlight: false,
    finalizationStatus: null,
    finalizationMessage: null,
    helpOpen: false,           // in-popup help-pagina (3 stappen + bel-CTA)
    viewMode: 'strip',         // 'strip' (default 14-dagen horizontal) | 'calendar' (volledige maand)
    appointmentId: null,       // gevuld na eerste lead-save (idempotentie)
    editToken: null,           // token vereist voor latere UPDATE
    savedRevision: null,
    finalizationAttempt: null,
    leadSaveDraft: null,       // duurzame pending identiteit; geen bewijs van bevestigde opslag
    locationId: null,          // Supabase UUID van geselecteerde locatie
    locationSlug: null,        // ai code: slug waar locationId bij hoort — cache-validatie bij locatie-wissel
    availability: {},          // { 'YYYY-MM-DD': { status, reason? } }
    // Config geladen vanuit Supabase (echte openingstijden + uitzonderingen + feestdagen)
    locationHours: null,       // Map weekday → { isOpen, openTime, closeTime }
    dateOverrides: null,       // Map 'YYYY-MM-DD' → override
    autoBlockHolidays: true,   // toggle van locatie
    holidayMap: null,          // Map 'YYYY-MM-DD' → name (NL feestdagen)
    maxPerHour: 2,             // van planning_capacity
    slotCounts: null           // { 'YYYY-MM-DD|HH:MM:SS': occupied_count }
  };

  // 7 dagen TTL op opgeslagen state — daarna behandelen we de browser als nieuw
  var STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function loadState() {
    try {
      var raw = saveStorageRead();
      if (!raw) return;
      var parsed = JSON.parse(raw);
      // TTL check — verlopen state niet herstellen
      if (parsed.savedAt && (Date.now() - parsed.savedAt) > STORAGE_TTL_MS) {
        try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
        return;
      }
      restorePersistedFlowState(state, parsed);
    } catch (e) {}
  }

  function persistState() {
    try {
      return saveStorageWrite(JSON.stringify({
        selectedDate: state.selectedDate,
        selectedSlot: state.selectedSlot,
        payment: state.payment,
        step: state.step,
        flowIdentity: state.flowIdentity,
        completed: state.completed,
        form: state.form,
        appointmentId: state.appointmentId,
        editToken: state.editToken,
        leadSaveDraft: state.leadSaveDraft,
        savedRevision: state.savedRevision,
        finalizationAttempt: state.finalizationAttempt,
        locationId: state.locationId,
        locationSlug: state.locationSlug,
        savedAt: Date.now()
      }));
    } catch (e) { return false; }
  }

  function saveStorageRead() {
    var local = null, session = null;
    try { local = localStorage.getItem(STORAGE_KEY); } catch (e) {}
    try { session = sessionStorage.getItem(STORAGE_KEY); } catch (e) {}
    if (!local) return session;
    if (!session) return local;
    try { return JSON.parse(session).savedAt >= JSON.parse(local).savedAt ? session : local; }
    catch (e) { return session || local; }
  }

  function saveStorageWrite(value) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
      return true;
    } catch (e) {}
    try { sessionStorage.setItem(STORAGE_KEY, value); return true; } catch (e) {}
    // Restricted browsers can still save/retry in memory; reload recovery requires storage.
    return false;
  }

  // Stap 2 wordt opnieuw passend gemaakt na render, resize en font-load.
  var step2FitScheduled = false;

  // ============================================================
  // SUPABASE REST API (direct, anon key)
  // ============================================================

  function getLocationSlug() {
    return resolveLocationSlug(getCurrentLocation(), ACTIVE_LOCATION_CONFIGS);
  }

  function sbHeaders(extra) {
    var h = {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': 'Bearer ' + SUPABASE_ANON_KEY
    };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) h[k] = extra[k];
    return h;
  }

  function sbFetch(path, opts) {
    opts = opts || {};
    var headers = sbHeaders(opts.body ? { 'Content-Type': 'application/json' } : {});
    if (opts.headers) for (var k in opts.headers) if (Object.prototype.hasOwnProperty.call(opts.headers, k)) headers[k] = opts.headers[k];
    return fetch(SUPABASE_URL + path, {
      method: opts.method || 'GET',
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'omit'
    }).then(function (res) {
      return res.text().then(function (txt) {
        var data = txt ? JSON.parse(txt) : null;
        if (!res.ok) {
          var err = new Error((data && (data.message || data.error)) || ('Supabase error ' + res.status));
          err.status = res.status;
          err.detail = data;
          throw err;
        }
        return data;
      });
    });
  }

  function loadActiveLocationConfigs(forceRefresh) {
    if (!forceRefresh && Array.isArray(ACTIVE_LOCATION_CONFIGS)) return Promise.resolve(ACTIVE_LOCATION_CONFIGS);
    if (!forceRefresh && LOCATION_CONFIG_PROMISE) return LOCATION_CONFIG_PROMISE;
    if (!SUPABASE_ANON_KEY) {
      if (forceRefresh) ACTIVE_LOCATION_CONFIGS = [];
      return Promise.resolve([]);
    }
    var requestGeneration = ++LOCATION_CONFIG_REQUEST_GENERATION;
    LOCATION_CONFIG_PROMISE = sbFetch(PLANNING_LOCATION_CONFIG_PATH)
      .then(function (rows) {
        var normalizedRows = Array.isArray(rows) ? rows : [];
        if (requestGeneration !== LOCATION_CONFIG_REQUEST_GENERATION) return [];
        ACTIVE_LOCATION_CONFIGS = normalizedRows;
        return normalizedRows;
      })
      .catch(function () {
        if (requestGeneration !== LOCATION_CONFIG_REQUEST_GENERATION) return [];
        ACTIVE_LOCATION_CONFIGS = [];
        return ACTIVE_LOCATION_CONFIGS;
      });
    return LOCATION_CONFIG_PROMISE;
  }

  // ai code: funnel tracking. Schiet anonymous events naar onze eigen
  // /planning/api/funnel-track-ai endpoint (Next.js API route). Die proxy:
  //   - leest x-forwarded-for / cf-connecting-ip headers (echte client-IP)
  //   - skipt blacklisted IPs (test-IPs van Ticho) → 200 maar geen insert
  //   - insert via service-role in planning_funnel_events
  // Voordeel t.o.v. direct Supabase REST: IP-filter werkt server-side,
  // popup hoeft niks te weten van filtering.
  // Fire-and-forget — failures mogen NOOIT de popup blokkeren.
  // ai code: sinds 10 juni 2026 direct naar Supabase Edge Function. De oude
  // route via dashboard.caroptions.nl werd door de dashboard login-proxy
  // naar /login geredirect (sinds 27 mei) waardoor alle events verloren
  // gingen. De EF doet dezelfde IP-blacklist + service-role insert.
  var FUNNEL_TRACK_URL = 'https://ukhajobbyvkyryaavadh.supabase.co/functions/v1/funnel-track-ai';
  function trackFunnel(eventName, extraMetadata, operation) {
    try {
      if (operation && !currentFlowOperation(operation)) return;
      state.funnelSessionId = getFunnelSessionForEvent(eventName, state.funnelSessionId, function () {
        return (window.crypto && typeof window.crypto.randomUUID === 'function')
          ? window.crypto.randomUUID()
          : 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).substring(2, 10);
      });
      // Een identity-reset wist de oude sessie. Een daaropvolgende close mag
      // geen nieuwe analytics-sessie maken met alleen popup_closed.
      if (!state.funnelSessionId) return;
      // step_number afleiden uit naam: step_N_viewed / step_N_completed
      var stepMatch = /^step_(\d)_/.exec(eventName);
      var stepNumber = stepMatch ? parseInt(stepMatch[1], 10) : null;
      var productHandle = null;
      try {
        productHandle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || null;
      } catch (e) {}
      var funnelContext = getFunnelContext(state.flowMode, getLocationSlug(), extraMetadata);
      var body = {
        session_id: state.funnelSessionId,
        event_name: eventName,
        location_slug: funnelContext.location_slug,
        variant_id: (state.variantId != null) ? Number(state.variantId) : null,
        product_handle: productHandle,
        step_number: stepNumber,
        metadata: funnelContext.metadata
      };
      fetch(FUNNEL_TRACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'omit',
        keepalive: true  // werkt ook als pagina sluit (popup_closed)
      }).catch(function () { /* nooit fataal */ });
    } catch (e) { /* nooit fataal */ }
  }

  // Locatie-ID identity-gebonden resolven. De operation snapshot bepaalt de
  // enige geldige cache; deze resolver muteert geen gedeelde flowstate. De
  // identity-binder verzorgt een locatie-/productwissel vóór deze grens.
  function beginCurrentFlowOperation(scope, parent) {
    // Een verouderde parent mag zelfs geen nieuwe scope-versie claimen: dat
    // zou een inmiddels actieve loader van dezelfde (nieuwe) flow ongeldig
    // kunnen maken voordat de child zijn eerste identity-check uitvoert.
    if (parent && !currentFlowOperation(parent)) return null;
    return beginFlowOperation(state, scope, {
      parent: parent || null,
      targetLocationSlug: parent && parent.targetLocationSlug
        ? parent.targetLocationSlug
        : getLocationSlug(),
      variantId: parent && parent.variantId
        ? parent.variantId
        : getCurrentVariantIdGlobal()
    });
  }

  function currentFlowOperation(operation) {
    return isFlowOperationCurrent(state, operation, isFlowIdentityCurrent);
  }

  function commitCurrentFlowOperation(operation, mutation) {
    return commitFlowOperation(state, operation, isFlowIdentityCurrent, mutation);
  }

  function staleFlowOperationResult() {
    return { kind: 'identity_changed' };
  }

  function resolveLocationId(expectedIdentity, operation) {
    var capturedIdentity = expectedIdentity || state.flowIdentity;
    var targetLocationSlug = operation && operation.targetLocationSlug
      ? operation.targetLocationSlug
      : getLocationSlug();
    var cachedLocationId = operation
      ? operation.cachedLocationId
      : state.locationId;
    var cachedLocationSlug = operation
      ? operation.cachedLocationSlug
      : state.locationSlug;
    return resolveLocationIdForIdentity({
      expectedIdentity: capturedIdentity,
      cachedLocationId: cachedLocationId,
      cachedLocationSlug: cachedLocationSlug,
      targetLocationSlug: targetLocationSlug,
      isIdentityCurrent: isFlowIdentityCurrent,
      fetchLocation: function (slug) {
        return sbFetch('/rest/v1/planning_locations?select=id&slug=eq.' + encodeURIComponent(slug) + '&limit=1');
      }
    });
  }

  function requireLocationId(expectedIdentity, operation) {
    return resolveLocationId(expectedIdentity, operation).then(function (result) {
      if (!result || result.kind !== 'resolved') {
        var error = new Error('Flow-identiteit gewijzigd tijdens locatie-resolutie');
        error.code = 'identity_changed';
        throw error;
      }
      return result.locationId;
    });
  }

  // ============================================================
  // NL FEESTDAGEN (client-side mirror van dashboard logica)
  // ============================================================

  function easterSunday(year) {
    var a = year % 19, b = Math.floor(year / 100), c = year % 100;
    var d = Math.floor(b / 4), e = b % 4;
    var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
    var h = (19 * a + b - d - g + 15) % 30;
    var i = Math.floor(c / 4), k = c % 4;
    var L = (32 + 2 * e + 2 * i - h - k) % 7;
    var m = Math.floor((a + 11 * h + 22 * L) / 451);
    var month = Math.floor((h + L - 7 * m + 114) / 31);
    var day = ((h + L - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
  }
  function addDays(d, days) {
    var r = new Date(d); r.setDate(r.getDate() + days); return r;
  }
  function computeHolidays(year) {
    var easter = easterSunday(year);
    var kingsday = new Date(year, 3, 27);
    if (kingsday.getDay() === 0) kingsday = new Date(year, 3, 26);
    return [
      [new Date(year, 0, 1), 'Nieuwjaarsdag'],
      [addDays(easter, -2), 'Goede Vrijdag'],
      [easter, 'Eerste Paasdag'],
      [addDays(easter, 1), 'Tweede Paasdag'],
      [kingsday, 'Koningsdag'],
      [new Date(year, 4, 5), 'Bevrijdingsdag'],
      [addDays(easter, 39), 'Hemelvaartsdag'],
      [addDays(easter, 49), 'Eerste Pinksterdag'],
      [addDays(easter, 50), 'Tweede Pinksterdag'],
      [new Date(year, 11, 25), 'Eerste Kerstdag'],
      [new Date(year, 11, 26), 'Tweede Kerstdag']
    ];
  }
  function ensureHolidayMap(year) {
    if (!state.holidayMap) state.holidayMap = {};
    if (state.holidayMap['_year_' + year]) return;
    state.holidayMap['_year_' + year] = true;
    computeHolidays(year).forEach(function (h) {
      state.holidayMap[fmtDateKey(h[0])] = h[1];
    });
  }

  // ============================================================
  // CONFIG LOAD (openingstijden + overrides)
  // ============================================================

  function loadLocationConfig(parentOperation) {
    if (!SUPABASE_ANON_KEY) return Promise.resolve({ kind: 'skipped' });
    var operation = beginCurrentFlowOperation('location_config', parentOperation);
    if (!currentFlowOperation(operation)) return Promise.resolve(staleFlowOperationResult());
    return requireLocationId(operation.expectedIdentity, operation).then(function (locId) {
      if (!currentFlowOperation(operation)) return { __stale: true };
      return Promise.all([
        sbFetch('/rest/v1/planning_locations?select=auto_block_holidays,company_name,address,postcode,city&id=eq.' + locId + '&limit=1'),
        sbFetch('/rest/v1/planning_location_hours?select=weekday,is_open,open_time,close_time&location_id=eq.' + locId),
        sbFetch('/rest/v1/planning_date_overrides?select=override_date,is_open,open_time,close_time,reason&location_id=eq.' + locId),
        sbFetch('/rest/v1/planning_capacity?select=max_per_hour,default_minutes&location_id=eq.' + locId + '&limit=1'),
        // ai code: capacity rules nodig om client-side te schatten hoe lang een
        // afspraak duurt o.b.v. de gekozen extras (bv. camera = 180 min).
        // Wordt gebruikt om slots te verbergen die buiten sluitingstijd eindigen.
        sbFetch('/rest/v1/planning_capacity_rules?select=minutes,priority,conditions&location_id=eq.' + locId + '&is_enabled=eq.true&order=priority.desc'),
      ]);
    }).then(function (results) {
      if ((results && results.__stale) || !currentFlowOperation(operation)) {
        return staleFlowOperationResult();
      }
      var locRow = (results[0] || [])[0] || {};
      // ai code: volledig adres voor step 4 overzicht — "Bedrijf, Adres,
      // Postcode Plaats". Valt terug op alleen plaats als niets ingevuld.
      var addrParts = [];
      if (locRow.address) addrParts.push(locRow.address);
      if (locRow.postcode || locRow.city) addrParts.push((locRow.postcode || '') + (locRow.postcode && locRow.city ? ' ' : '') + (locRow.city || ''));

      var hoursMap = {};
      (results[1] || []).forEach(function (h) { hoursMap[h.weekday] = h; });

      var overrides = {};
      (results[2] || []).forEach(function (o) { overrides[o.override_date] = o; });

      var capRow = (results[3] || [])[0] || {};
      if (!commitCurrentFlowOperation(operation, function () {
        state.autoBlockHolidays = locRow.auto_block_holidays !== false;
        state.locationFullAddress = addrParts.length ? addrParts.join(', ') : null;
        state.locationCompany = locRow.company_name || null;
        state.locationHours = hoursMap;
        state.dateOverrides = overrides;
        state.maxPerHour = capRow.max_per_hour || 2;
        state.defaultDurationMinutes = capRow.default_minutes || 60;
        state.capacityRules = results[4] || [];
      })) return staleFlowOperationResult();
      return { kind: 'loaded' };
    }).catch(function (e) {
      if (!currentFlowOperation(operation)) return staleFlowOperationResult();
      console.warn('[CO-AI] config load fout, gebruik fallback', e);
      return { kind: 'failed' };
    });
  }

  // ai code: schat hoe lang de afspraak gaat duren o.b.v. de match-engine
  // die ook in apple-cal-create-ai server-side draait. We bouwen een voorspelde
  // titel (zelfde format als de echte event) en runnen de regels ertegen.
  // Hoogste priority match wint. Geen match → default_minutes uit capacity.
  function estimateBookingMinutes() {
    var fallback = state.defaultDurationMinutes || 60;
    var rules = state.capacityRules;
    if (!rules || !rules.length) return fallback;

    var info = readProductInfo();
    // Voorspelde titel = "installatie {brand} + {extras}" lowercased.
    // De server-side titel begint met "INSTALLATIE::" dus we matchen op "installatie".
    var brand = info.vehicleBrand ? info.vehicleBrand.toLowerCase()
      : (info.title || '').replace(/^CarPlayBox voor\s*/i, '').replace(/^SmartScreen voor\s*/i, '').toLowerCase();
    var extras = (info.extras || []).map(function (e) { return String(e).toLowerCase(); }).join(' ');
    var predictedTitle = ('installatie ' + brand + ' ' + extras).replace(/\s+/g, ' ').trim();
    var predictedDesc = (info.title || '').toLowerCase();

    for (var i = 0; i < rules.length; i++) {
      var r = rules[i];
      if (!r.conditions || !r.conditions.length) continue;
      var match = true;
      for (var j = 0; j < r.conditions.length; j++) {
        var c = r.conditions[j];
        var hay = c.field === 'description' ? predictedDesc : predictedTitle;
        var needle = (c.value || '').toLowerCase();
        var ok;
        switch (c.operator) {
          case 'contains':     ok = hay.indexOf(needle) !== -1; break;
          case 'not_contains': ok = hay.indexOf(needle) === -1; break;
          case 'exact':        ok = hay === needle; break;
          case 'not_exact':    ok = hay !== needle; break;
          case 'starts_with':  ok = hay.indexOf(needle) === 0; break;
          case 'ends_with':    ok = hay.length >= needle.length && hay.lastIndexOf(needle) === hay.length - needle.length; break;
          default: ok = false;
        }
        if (!ok) { match = false; break; }
      }
      if (match) return r.minutes || fallback;
    }
    return fallback;
  }

  // ai code: slot-bezetting + capaciteit voor de komende 90 dagen ophalen.
  // RPC returnt ALLEEN slots waar capaciteit > 0 (monteur ingepland), met
  // occupied count + effective_max per slot.
  function loadSlotCounts(parentOperation) {
    if (!SUPABASE_ANON_KEY) return Promise.resolve({ kind: 'skipped' });
    var operation = beginCurrentFlowOperation('slot_counts', parentOperation);
    if (!currentFlowOperation(operation)) return Promise.resolve(staleFlowOperationResult());
    var today = startOfDay(new Date());
    var endDate = new Date(today);
    endDate.setDate(today.getDate() + 90);
    var fromStr = fmtDateKey(today);
    var toStr = fmtDateKey(endDate);
    var variantNum = operation.variantId ? parseInt(operation.variantId, 10) : null;
    return requireLocationId(operation.expectedIdentity, operation).then(function (locId) {
      if (!currentFlowOperation(operation)) return { __stale: true };
      return sbFetch('/rest/v1/rpc/planning_slot_counts', {
        method: 'POST',
        body: {
          p_location_id: locId,
          p_from: fromStr,
          p_to: toStr,
          p_variant_id: (variantNum && !isNaN(variantNum)) ? variantNum : null,
        }
      });
    }).then(function (rows) {
      if ((rows && rows.__stale) || !currentFlowOperation(operation)) {
        return staleFlowOperationResult();
      }
      // ai code: RPC v3 retourneert nu effective_max per slot (min van caps
      // van alle overlappende afspraken + dag-override + locatie-default).
      // We bewaren beide: occupied EN effective_max per slot.
      var map = {};
      var maxMap = {};
      (rows || []).forEach(function (r) {
        var slot = (r.appointment_slot || '').slice(0, 5);
        var key = r.appointment_date + '|' + slot;
        map[key] = r.occupied;
        if (typeof r.effective_max === 'number') {
          maxMap[key] = r.effective_max;
        }
      });
      if (!commitCurrentFlowOperation(operation, function () {
        state.slotCounts = map;
        state.slotMaxMap = maxMap;
      })) return staleFlowOperationResult();
      return { kind: 'loaded' };
    }).catch(function (e) {
      if (!currentFlowOperation(operation)) return staleFlowOperationResult();
      console.warn('[CO-AI] slot counts load fout', e);
      if (!commitCurrentFlowOperation(operation, function () {
        state.slotCounts = {};
        state.slotMaxMap = {};
      })) return staleFlowOperationResult();
      return { kind: 'failed' };
    });
  }

  // Bepaal voor een datum: { isOpen, openHour, closeHour, reason }
  function getDayConfig(date) {
    var key = fmtDateKey(date);
    var year = date.getFullYear();
    ensureHolidayMap(year);

    // 1. Override?
    var override = state.dateOverrides && state.dateOverrides[key];
    if (override) {
      if (!override.is_open) return { isOpen: false, reason: override.reason || 'Dicht' };
      return {
        isOpen: true,
        openHour: parseInt(override.open_time, 10),
        closeHour: parseInt(override.close_time, 10),
      };
    }
    // 2. Feestdag?
    if (state.autoBlockHolidays && state.holidayMap && state.holidayMap[key]) {
      return { isOpen: false, reason: state.holidayMap[key] };
    }
    // 3. Weekdag-default
    if (state.locationHours) {
      var h = state.locationHours[date.getDay()];
      if (!h || !h.is_open) return { isOpen: false, reason: null };
      return {
        isOpen: true,
        openHour: parseInt(h.open_time, 10),
        closeHour: parseInt(h.close_time, 10),
      };
    }
    // 4. Fallback (config nog niet geladen)
    var weekday = date.getDay();
    if (FALLBACK_CLOSED_WEEKDAYS.indexOf(weekday) !== -1) {
      return { isOpen: false, reason: null };
    }
    return { isOpen: true, openHour: FALLBACK_OPEN_HOUR, closeHour: FALLBACK_CLOSE_HOUR };
  }

  // ai code: nieuwe bron — slots komen uit planning_capacity_block via RPC.
  // De RPC retourneert ALLEEN slots waar capaciteit > 0 (monteur werkt).
  // Geen werk-blokken in capaciteit-kalender = geen boekbare slots.
  function getSlotsForDate(date) {
    var key = fmtDateKey(date);
    var counts = state.slotCounts || {};
    var slots = [];
    for (var k in counts) {
      if (k.indexOf(key + '|') === 0) {
        slots.push(k.substring(key.length + 1));
      }
    }
    slots.sort();

    // Filter slots die op vandaag al voorbij zijn
    var now = new Date();
    var isToday = date.getFullYear() === now.getFullYear()
               && date.getMonth() === now.getMonth()
               && date.getDate() === now.getDate();
    if (isToday) {
      var nowMinutes = now.getHours() * 60 + now.getMinutes();
      slots = slots.filter(function (t) {
        var parts = t.split(':');
        var slotMin = parseInt(parts[0], 10) * 60 + parseInt(parts[1] || '0', 10);
        return slotMin >= nowMinutes;
      });
    }

    // ai code: filter slots die buiten sluitingstijd zouden eindigen.
    // Bv. camera-installatie = 180 min, sluiting 17:00 → 15:00 slot eindigt
    // om 18:00 → NIET tonen. Klant kan dan max 14:00 kiezen.
    // Geen estimateBookingMinutes call wanneer readProductInfo niet werkt
    // (bv. tijdens initial render zonder DOM-context); fallback default.
    var cfg = getDayConfig(date);
    if (cfg.isOpen && typeof cfg.closeHour === 'number') {
      var closeMin = cfg.closeHour * 60;
      var dur;
      try { dur = estimateBookingMinutes(); }
      catch (_) { dur = state.defaultDurationMinutes || 60; }
      slots = slots.filter(function (t) {
        var parts = t.split(':');
        var startMin = parseInt(parts[0], 10) * 60 + parseInt(parts[1] || '0', 10);
        return (startMin + dur) <= closeMin;
      });
    }
    return slots;
  }

  // ai code: oude config-based slot generator behouden voor backward-compat
  // maar niet meer gebruikt. Verwijderbaar in toekomstige cleanup.
  function getSlotsForDate_OLD(date) {
    var cfg = getDayConfig(date);
    if (!cfg.isOpen) return [];
    var slots = [];
    var interval = SLOT_INTERVAL_MINUTES;
    var minutes = cfg.openHour * 60;
    var endMinutes = cfg.closeHour * 60;

    var now = new Date();
    var isToday = date.getFullYear() === now.getFullYear()
               && date.getMonth() === now.getMonth()
               && date.getDate() === now.getDate();
    var nowMinutes = isToday ? (now.getHours() * 60 + now.getMinutes()) : -1;

    while (minutes + interval <= endMinutes) {
      if (!isToday || minutes >= nowMinutes) {
        var h = Math.floor(minutes / 60);
        var m = minutes % 60;
        slots.push((h < 10 ? '0' : '') + h + ':' + (m < 10 ? '0' : '') + m);
      }
      minutes += interval;
    }
    return slots;
  }

  // Beschikbaarheid voor de huidige zichtbare maand ophalen.
  // Combineert blocked_dates + location_settings; "full" detectie skippen we voor nu.
  function loadAvailability(parentOperation) {
    if (!state.viewMonth) return Promise.resolve({ kind: 'skipped' });
    if (!SUPABASE_ANON_KEY) {
      console.warn('[CO-AI] SUPABASE_ANON_KEY niet geconfigureerd — fallback naar mock');
      return Promise.resolve({ kind: 'skipped' });
    }
    var operation = beginCurrentFlowOperation('availability', parentOperation);
    if (!currentFlowOperation(operation)) return Promise.resolve(staleFlowOperationResult());
    var month = state.viewMonth;
    var first = new Date(month.getFullYear(), month.getMonth(), 1);
    var last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    var fromStr = fmtDateKey(first);
    var toStr = fmtDateKey(last);

    return requireLocationId(operation.expectedIdentity, operation).then(function (locId) {
      if (!currentFlowOperation(operation)) return { __stale: true };
      var blockedQ = '/rest/v1/planning_blocked_dates'
        + '?select=blocked_date,blocked_slot,reason'
        + '&location_id=eq.' + locId
        + '&blocked_date=gte.' + fromStr
        + '&blocked_date=lte.' + toStr;
      return sbFetch(blockedQ);
    }).then(function (blocked) {
      if ((blocked && blocked.__stale) || !currentFlowOperation(operation)) {
        return staleFlowOperationResult();
      }
      var map = {};
      (blocked || []).forEach(function (b) {
        if (!b.blocked_slot) {
          map[b.blocked_date] = { status: 'blocked', reason: b.reason || '' };
        }
      });
      if (!commitCurrentFlowOperation(operation, function () {
        state.availability = map;
      })) return staleFlowOperationResult();
      return { kind: 'loaded' };
    }).catch(function (err) {
      if (!currentFlowOperation(operation)) return staleFlowOperationResult();
      console.warn('[CO-AI] availability fout — fallback naar mock', err);
      return { kind: 'failed' };
    });
  }

  // ai code: zelfde line-items als pay-now naar de cart pusht — main variant
  // + installatie-variant + extras (camera etc.) — voor stabiele Shopify order create.
  function collectCartLineItems() {
    if (typeof window.syncBentleyIncludedInstallation === 'function') window.syncBentleyIncludedInstallation();
    var items = [];
    var added = {};

    // ai code: verified install-only flow — de order is exact 1 regel: de
    // installatie-variant van de pagina, met afspraak-props + origineel
    // ordernummer. Geen GPO-regels, geen extras.
    if (window.CO_AI_INSTALL_VERIFIED === true) {
      var vNodes = document.querySelectorAll('[name="id"]');
      var vId = null;
      for (var vi = 0; vi < vNodes.length; vi++) {
        var vn = vNodes[vi];
        if (vn.tagName === 'SELECT') {
          var vopt = vn.options[vn.selectedIndex];
          if (vopt && vopt.value) { vId = vopt.value; break; }
        } else if (vn.value) { vId = vn.value; break; }
      }
      if (!vId) {
        var vum = (window.location.search || '').match(/[?&]variant=(\d+)/);
        if (vum) vId = vum[1];
      }
      if (vId) {
        var vProps = {};
        if (state.selectedDate && state.selectedSlot) {
          vProps['Afspraak'] = formatDateLabel(state.selectedDate) + ' om ' + state.selectedSlot;
        }
        var vLoc = getCurrentLocation().replace(/^CarOptions\s*-\s*/, '');
        if (vLoc) vProps['Locatie'] = vLoc;
        if (window.CO_AI_INSTALL_ONLY_ORDER && window.CO_AI_INSTALL_ONLY_ORDER.orderName) {
          vProps['Originele order'] = window.CO_AI_INSTALL_ONLY_ORDER.orderName;
        }
        return [{ variant_id: parseInt(vId, 10), quantity: 1, properties: vProps }];
      }
      return [];
    }

    function add(variantId, properties) {
      if (!variantId) return;
      var idNum = parseInt(variantId, 10);
      if (!idNum || added[idNum]) return;
      added[idNum] = true;
      var item = { variant_id: idNum, quantity: 1 };
      if (properties) item.properties = properties;
      items.push(item);
    }

    // Main variant
    var mainNodes = document.querySelectorAll('[name="id"]');
    var mainId = null;
    for (var i = 0; i < mainNodes.length; i++) {
      var n = mainNodes[i];
      if (n.tagName === 'SELECT') {
        var opt = n.options[n.selectedIndex];
        if (opt && opt.value) { mainId = opt.value; break; }
      } else if (n.value) { mainId = n.value; break; }
    }
    if (!mainId) {
      var um = (window.location.search || '').match(/[?&]variant=(\d+)/);
      if (um) mainId = um[1];
    }
    if (isBentleyInstallationProduct()) mainId = getCurrentVariantIdGlobal() || mainId;
    // ai code: vangnet — als het "hoofdproduct" zelf een installatie-variant
    // is (alle opties van de GPO locatie-kiezer zijn installatie-varianten),
    // dan NIET als main toevoegen. Anders krijgt een order 2 installatie-
    // regels en geen box (incident #232014989).
    var installVariantIds = {};
    var locSelAll = document.querySelector('.location_selector select');
    if (locSelAll) {
      for (var oi = 0; oi < locSelAll.options.length; oi++) {
        var ovid = locSelAll.options[oi].getAttribute('gpo-data-variant-id');
        if (ovid) installVariantIds[parseInt(ovid, 10)] = true;
      }
    }
    var mainWasInstall = !!(mainId && installVariantIds[parseInt(mainId, 10)]);
    if (mainWasInstall) mainId = null;

    var mainProps = getVwScreenRadioProperties() || {};
    if (state.selectedDate && state.selectedSlot) {
      mainProps['Afspraak'] = formatDateLabel(state.selectedDate) + ' om ' + state.selectedSlot;
    }
    var locName = getCurrentLocation().replace(/^CarOptions\s*-\s*/, '');
    if (locName) mainProps['Locatie'] = locName;

    if (mainId) {
      add(mainId, Object.keys(mainProps).length ? mainProps : null);
    }

    // Installatie-variant (gekozen locatie uit GPO select). Als de main is
    // weggevallen (was zelf een installatie-variant) dragen de afspraak-
    // properties over op deze regel zodat ze zichtbaar blijven in checkout.
    var locSel = document.querySelector('.location_selector select');
    var locOpt = locSel && locSel.options[locSel.selectedIndex];
    if (locOpt) add(locOpt.getAttribute('gpo-data-variant-id'), (mainWasInstall && Object.keys(mainProps).length) ? mainProps : null);

    // Extras via GPO checkboxes
    document.querySelectorAll('input[type="checkbox"][gpo-data-variant-id]').forEach(function (cb) {
      if (cb.checked) add(cb.getAttribute('gpo-data-variant-id'));
    });
    // Extras via GPO radios
    document.querySelectorAll('input[type="radio"][gpo-data-variant-id]:checked').forEach(function (r) {
      add(r.getAttribute('gpo-data-variant-id'));
    });

    return items;
  }

  // Save-only transport: bounds fetch AND body consumption, even without AbortController.
  // Checkout/finalization continue using their existing transport and are never retried here.
  function saveScopedFetch(path, opts, timeoutMs, isCurrent) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      function finish(error, data) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (error) reject(error); else resolve(data);
      }
      var timer = setTimeout(function () {
        var error = new Error('Lead save request timed out');
        error.code = 'save_timeout'; error.transient = true;
        finish(error);
        if (controller) controller.abort();
      }, timeoutMs);
      var request = {
        method: opts.method || 'GET',
        headers: sbHeaders(opts.body ? { 'Content-Type': 'application/json' } : {}),
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        credentials: 'omit'
      };
      if (controller) request.signal = controller.signal;
      Promise.resolve().then(function () {
        if (settled || (isCurrent && !isCurrent())) {
          var cancelled = new Error('Lead save cancelled'); cancelled.code = 'save_cancelled'; cancelled.transient = false;
          throw cancelled;
        }
        return fetch(SUPABASE_URL + path, request);
      }).then(function (res) {
        return res.text().then(function (text) {
          if (!res.ok) {
            var error = new Error('Lead save HTTP ' + res.status);
            error.status = res.status;
            error.transient = res.status === 408 || res.status === 429 || res.status >= 500;
            try {
              var failure = JSON.parse(text);
              if (failure && failure.code === '22023' && failure.message === 'storefront_save_revision_conflict') error.code = 'revision_conflict';
              if (failure && failure.code === '42501') error.transient = false;
              if (failure && failure.code === '55P03') error.transient = true;
            } catch (e) {}
            throw error;
          }
          try { return text ? JSON.parse(text) : null; }
          catch (e) { var error = new Error('Invalid lead save response'); error.transient = true; throw error; }
        });
      }).then(function (data) { finish(null, data); }, function (error) {
        // Network failures have no HTTP status. Never include response/customer data in logs.
        if (typeof error.transient !== 'boolean') error.transient = !error.status;
        finish(error);
      });
    });
  }

  function saveLeadRequest(path, opts, operation, readOnly) {
    var attempts = 0;
    function attempt() {
      if (!operation.isCurrent()) {
        var cancelled = new Error('Lead save cancelled'); cancelled.code = 'save_cancelled';
        return Promise.reject(cancelled);
      }
      var remaining = operation.deadline - Date.now();
      if (remaining <= 0) {
        var expired = new Error('Lead save budget exhausted'); expired.code = 'save_timeout'; expired.transient = true;
        return Promise.reject(expired);
      }
      attempts++;
      return saveScopedFetch(path, opts, Math.min(4000, remaining), operation.isCurrent).then(function (data) {
        if (typeof opts.validateResponse === 'function') opts.validateResponse(data);
        return data;
      }).catch(function (error) {
        if (!error.transient || attempts >= (readOnly ? 2 : 3) || !operation.isCurrent() ||
            operation.deadline - Date.now() <= 150) throw error;
        operation.retries++;
        return new Promise(function (resolve) { setTimeout(resolve, 150); }).then(attempt);
      });
    }
    return attempt();
  }

  function createSaveUuid() {
    var secure = typeof crypto !== 'undefined' ? crypto : window.crypto;
    if (!secure || typeof secure.getRandomValues !== 'function') throw new Error('Secure random unavailable');
    if (typeof secure.randomUUID === 'function') return secure.randomUUID();
    var bytes = new Uint8Array(16);
    secure.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    var hex = Array.prototype.map.call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
    return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
  }

  function notifyLeadSaveOutcome(name) {
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  }

  function runDurableLeadSave(input) {
    var expectedIdentity = state.flowIdentity, expectedGeneration = state.flowGeneration;
    var expectedForm = JSON.stringify(state.form), draft = null;
    var operation = {
      deadline: Date.now() + 14000,
      retries: 0,
      isCurrent: function () {
        return !input.options.cancelled && !state.completed && !state.finalizationAttempt && state.flowGeneration === expectedGeneration &&
          isFlowIdentityCurrent(expectedIdentity) && JSON.stringify(state.form) === expectedForm &&
          (!input.options.expectedForm || input.options.expectedForm === expectedForm);
      }
    };
    return resolveLocationIdForIdentity({
      expectedIdentity: expectedIdentity,
      targetLocationSlug: input.targetLocationSlug,
      cachedLocationId: state.locationId,
      cachedLocationSlug: state.locationSlug,
      isIdentityCurrent: operation.isCurrent,
      fetchLocation: function (slug) {
        return saveLeadRequest('/rest/v1/planning_locations?select=id&slug=eq.' + encodeURIComponent(slug) + '&limit=1', {}, operation, true);
      }
    }).then(function (location) {
      if (!location || location.kind !== 'resolved' || !operation.isCurrent()) return;
      return input.buildPayload(location, operation).then(function (payload) {
        if (!operation.isCurrent()) return;
        payload.status = 'lead';
        var previous = state.leadSaveDraft;
        if (previous && previous.identity !== expectedIdentity) previous = null;
        if (previous && previous.needsExplicitRetry && !input.options.expectedForm) return;
        // Once completed, later autosave must not erase the completion timestamp.
        if (previous && previous.payload.step2_at && !payload.step2_at) payload.step2_at = previous.payload.step2_at;
        var comparable = Object.assign({}, payload);
        if (comparable.step2_at) comparable.step2_at = true;
        var fingerprint = JSON.stringify(comparable);
        if (previous && previous.fingerprint === fingerprint && !previous.needsExplicitRetry) {
          draft = previous; // identical body/revision, including timestamp, after timeout or reload
        } else {
          var revision = previous ? Math.max(previous.revision, previous.supersededRevision || 0) + 1 : 1;
          if (!Number.isSafeInteger(revision)) throw new Error('Lead revision limit');
          draft = {
            identity: expectedIdentity,
            id: previous ? previous.id : (state.appointmentId || createSaveUuid()),
            editToken: previous ? previous.editToken : (state.editToken || createSaveUuid()),
            revision: revision, fingerprint: fingerprint, payload: payload,
            hadTransientFailure: !!(previous && previous.hadTransientFailure)
          };
        }
        state.leadSaveDraft = draft;
        persistState(); // pending identity and immutable snapshot exist before any write dispatch
        var body = { p_id: draft.id, p_edit_token: draft.editToken, p_revision: draft.revision, p_payload: draft.payload };
        return saveLeadRequest('/rest/v1/rpc/planning_storefront_save_lead_v1', {
          method: 'POST', body: body,
          validateResponse: function (rows) {
            var row = Array.isArray(rows) && rows.length === 1 ? rows[0] : null;
            if (row && ((row.id && row.id !== draft.id) || (row.edit_token && row.edit_token !== draft.editToken))) {
              throw new Error('Lead receipt ownership mismatch');
            }
            if (!row || !row.id || !row.edit_token || !Number.isSafeInteger(row.save_revision) || !row.outcome) {
              var missing = new Error('Missing lead save receipt'); missing.transient = true; throw missing;
            }
          }
        }, operation, false).then(function (rows) {
          if (!operation.isCurrent() || state.leadSaveDraft !== draft) return;
          var row = Array.isArray(rows) && rows.length === 1 ? rows[0] : null;
          if (!row || row.id !== draft.id || row.edit_token !== draft.editToken || !Number.isSafeInteger(row.save_revision)) return;
          if (row.outcome === 'superseded' && row.save_revision > draft.revision) {
            draft.supersededRevision = row.save_revision;
            draft.needsExplicitRetry = true;
            persistState();
            return; // never count another snapshot as confirmation of this form
          }
          if (row.save_revision !== draft.revision || (row.outcome !== 'saved' && row.outcome !== 'replayed')) return;
          state.appointmentId = draft.id;
          state.editToken = draft.editToken;
          state.savedRevision = row.save_revision;
          state.locationId = location.locationId;
          state.locationSlug = location.locationSlug;
          if (operation.retries || draft.hadTransientFailure) notifyLeadSaveOutcome('installation_save_recovered');
          draft.hadTransientFailure = false;
          persistState();
          return rows;
        });
      });
    }).catch(function (error) {
      if (draft && state.leadSaveDraft === draft && error.code === 'revision_conflict') {
        // The RPC's exact conflict branch proves this revision already has another body.
        // Only a fresh, explicit Next may rebase the then-current form. Never autosave it.
        draft.supersededRevision = draft.revision;
        draft.needsExplicitRetry = true;
        persistState();
      }
      if (draft && state.leadSaveDraft === draft && error.transient) {
        draft.hadTransientFailure = true;
        persistState();
      }
      if (operation.isCurrent() && error.transient) notifyLeadSaveOutcome('installation_save_failed');
      // No customer details, tokens, server response or payload in diagnostics.
      return undefined;
    });
  }

  // Serialized versioned saves. Each operation settles within its own 14-second budget.
  var _saveLeadPromise = Promise.resolve();
  function saveLead(options) {
  var expectedIdentity = state.flowIdentity, expectedGeneration = state.flowGeneration;
  var operation = _saveLeadPromise.then(function () {
    if (!isFlowIdentityCurrent(expectedIdentity) || state.flowGeneration !== expectedGeneration) return;
    if (options && options.cancelled) return;
    if (options && typeof options.expectedForm === "string" && JSON.stringify(state.form) !== options.expectedForm) return;
    return saveLeadNow(options);
  });
  _saveLeadPromise = operation.catch(function () {});
  return operation;
}
  function saveLeadNow(options) {
    options = options || {};
    if (!SUPABASE_ANON_KEY) {
      console.warn('[CO-AI] SUPABASE_ANON_KEY niet geconfigureerd — lead niet opgeslagen');
      return Promise.resolve();
    }
    var info = readProductInfo();
    var totalEur = parseEuro(info.priceText);
    var totalCents = totalEur ? Math.round(totalEur * 100) : null;
    var pathHandle = (window.location.pathname || '')
      .replace(/^.*\/products\//, '').replace(/[?#].*$/, '');
    var lineItems = collectCartLineItems();
    var isPersonalContact = state.flowMode === FLOW_MODE.PERSONAL_CONTACT;
    var snapshot = {
      selectedDate: state.selectedDate,
      selectedSlot: state.selectedSlot,
      form: {
        voornaam: state.form.voornaam,
        achternaam: state.form.achternaam,
        bedrijf: state.form.bedrijf,
        email: state.form.email,
        tel: state.form.tel
      }
    };
    var completedAt = (isPersonalContact && !options.markStep2Complete)
      ? null
      : new Date().toISOString();

    function buildPayload(location, operation) {
      var locId = location.locationId;
      function makePayload(durMin, maxC) {
        var fullName = (snapshot.form.voornaam + ' ' + snapshot.form.achternaam).trim();
        var vehicleTitle = info.title || '';
        var payload = {
          location_id: locId,
          // ai code: source-tracking — popup boekingen krijgen 'shopify' label
          // in /afspraken dashboard ("Via" kolom). Dashboard manual wizard zet
          // 'medewerker'. NULL = onbekend / pre-tracking.
          source: 'shopify',
          appointment_date: snapshot.selectedDate,
          appointment_slot: snapshot.selectedSlot,
          shopify_line_items: lineItems.length > 0 ? lineItems : null,
          customer_name: fullName || null,
          customer_first_name: snapshot.form.voornaam || null,
          customer_last_name: snapshot.form.achternaam || null,
          customer_company: snapshot.form.bedrijf || null,
          customer_email: snapshot.form.email,
          customer_phone: snapshot.form.tel,
          license_plate: info.kenteken || null,
          // ai code: strip alle product-prefixen zodat vehicle_brand puur het merk is.
          vehicle_brand: info.vehicleBrand || vehicleTitle
            .replace(/^CarPlay\s*&\s*Android\s*Auto\s+voor\s+/i, '')
            .replace(/^CarPlayBox\s+voor\s+/i, '')
            .replace(/^CarPlayBox\s*-\s*/i, '')
            .replace(/^SmartScreen\s+voor\s+/i, '')
            .trim() || null,
          vehicle_model: info.model || null,
          vehicle_compatible: !!info.kenteken,
          product_handle: pathHandle || null,
          product_title: info.title || null,
          product_variant: info.model || null,
          extras: info.extras || [],
          total_price_cents: totalCents,
          duration_minutes: durMin,
          max_concurrent: maxC
        };
        if (isPersonalContact) {
          return buildContactLeadPayload(payload, locId, completedAt);
        }
        payload.step2_at = completedAt;
        return payload;
      }

      if (isPersonalContact) return Promise.resolve(makePayload(null, null));
      // Beide rule-resultaten worden opgeslagen voor multi-slot-capaciteit.
      var titleForRules = (info.title || '') + (info.model ? ' ' + info.model : '');
      var descForRules = (info.extras || []).join(' ');
      return saveLeadRequest('/rest/v1/rpc/calculate_rule_match', {
        method: 'POST',
        body: {
          p_location_id: locId,
          p_title: titleForRules,
          p_description: descForRules
        }
      }, operation, true).then(function (data) {
        var row = Array.isArray(data) ? data[0] : data;
        var durMin = (row && typeof row.minutes === 'number' && row.minutes > 0) ? row.minutes : null;
        var maxC = (row && typeof row.max_concurrent === 'number' && row.max_concurrent > 0) ? row.max_concurrent : null;
        return makePayload(durMin, maxC);
      }).catch(function () {
        return makePayload(null, null);
      });
    }

    return runDurableLeadSave({
      options: options,
      targetLocationSlug: getLocationSlug(),
      buildPayload: buildPayload
    });
  }

  // "Nu betalen" → cart fillen met main variant + installatie-variant + redirect naar /checkout.
  //
  // GPO werkt zo: per locatie is er een aparte "carplaybox-installatie" variant.
  // Die variant ID staat op de <option> als gpo-data-variant-id. Wanneer je
  // normaal de cart-knop klikt voegt GPO die als extra line item toe. Wij doen
  // dat hier zelf via /cart/add.js zodat we daarna direct naar /checkout kunnen.
  function checkoutAndRedirect(options) {
    options = options || {};
    var expectedIdentity = options.expectedIdentity || state.flowIdentity;
    var btn = options.triggerButton || document.querySelector('[data-coa-checkout]');
    var restoreBtn = function (msg) {
      if (btn) {
        btn.disabled = false;
        btn.innerText = msg || options.retryLabel || 'Betalen';
      }
      syncAllButtons();
    };
    function runCart(context) {
      var fallbackUrl = buildCartPermalink(context, buildCheckoutUrl());
      return runMandatoryCartCheckout({
        context: context,
        expectedIdentity: expectedIdentity,
        isIdentityCurrent: isFlowIdentityCurrent,
        fetchImpl: fetch,
        redirect: function () {
          console.log('[CO-AI] Cart success → /checkout met pre-fill');
          window.location.href = buildCheckoutUrl();
        },
        fallbackRedirect: function (reason) {
          if (!fallbackUrl) throw new Error('cart_permalink_missing');
          console.warn('[CO-AI] Shopify cart-fetch geblokkeerd (' + reason + '), gebruik cart-permalink');
          window.location.href = fallbackUrl;
        },
        onFailure: function (reason) {
          console.warn('[CO-AI] checkout gestopt:', reason);
          if (reason === 'identity_changed') handleFlowIdentityMismatch();
          restoreBtn();
        }
      });
    }
    if (!isFlowIdentityCurrent(expectedIdentity)) {
      handleFlowIdentityMismatch();
      restoreBtn();
      return Promise.resolve({ ok: false, reason: 'identity_changed' });
    }

    var form = document.querySelector('#carplayboxform form[action*="/cart/add"]')
            || document.querySelector('#carplayboxform form')
            || document.querySelector('form[action*="/cart/add"]');
    if (!form || form.tagName !== 'FORM') {
      console.error('[CO-AI] Cart form niet gevonden', form);
      return runCart(null);
    }
    console.log('[CO-AI] Form found:', form.id || form.action);

    // 1. Bouw payload voor de hoofdvariant (main product)
    //    Variant kan in <input name="id">, <select name="id">, of als URL-param staan.
    function findMainVariantId() {
      // Probeer alle name="id" elementen binnen het form
      var nodes = form.querySelectorAll('[name="id"]');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        if (n.tagName === 'SELECT') {
          var opt = n.options[n.selectedIndex];
          if (opt && opt.value) return opt.value;
        } else if (n.value) {
          return n.value;
        }
      }
      // Buiten form (sommige themes hebben hidden input naast form)
      var docNode = document.querySelector('input[name="id"][value], select[name="id"]');
      if (docNode) {
        if (docNode.tagName === 'SELECT') {
          var o = docNode.options[docNode.selectedIndex];
          if (o && o.value) return o.value;
        } else if (docNode.value) return docNode.value;
      }
      // Fallback: ?variant=XXX in URL
      var urlMatch = (window.location.search || '').match(/[?&]variant=(\d+)/);
      if (urlMatch) return urlMatch[1];
      return null;
    }

    var mainVariantId = findMainVariantId();
    if (isBentleyInstallationProduct()) mainVariantId = getCurrentVariantIdGlobal() || mainVariantId;
    console.log('[CO-AI] mainVariantId:', mainVariantId);
    if (!mainVariantId) {
      return runCart(null);
    }
    var info = readProductInfo();

    // Properties alleen op het hoofdproduct (klant ziet dat in checkout-overzicht).
    // Andere line items spreken voor zichzelf via hun variant titel.
    var mainProps = getVwScreenRadioProperties() || {};
    if (state.selectedDate && state.selectedSlot) {
      mainProps['Afspraak'] = formatDateLabel(state.selectedDate) + ' om ' + state.selectedSlot;
    }
    if (info.location) {
      mainProps['Locatie'] = info.location;
    }

    // ai code: vangnet — main mag zelf geen installatie-variant zijn (alle
    // opties van de locatie-kiezer). Anders 2 installatie-regels, geen box
    // (incident #232014989). Properties verhuizen dan naar de installatie-regel.
    // UITZONDERING: verified install-only flow — daar is de installatie-variant
    // juist het enige product (+ origineel ordernummer als property).
    var verifiedOnly = window.CO_AI_INSTALL_VERIFIED === true;
    if (verifiedOnly && window.CO_AI_INSTALL_ONLY_ORDER && window.CO_AI_INSTALL_ONLY_ORDER.orderName) {
      mainProps['Originele order'] = window.CO_AI_INSTALL_ONLY_ORDER.orderName;
    }
    var locSelect = document.querySelector('.location_selector select');
    var installIds = {};
    if (locSelect) {
      for (var li = 0; li < locSelect.options.length; li++) {
        var lvid = locSelect.options[li].getAttribute('gpo-data-variant-id');
        if (lvid) installIds[parseInt(lvid, 10)] = true;
      }
    }
    var mainIsInstall = !verifiedOnly && installIds[parseInt(mainVariantId, 10)] === true;

    // Verified flow: main (= installatie-variant van de pagina) is het enige item.
    var items = mainIsInstall
      ? []
      : [{ id: parseInt(mainVariantId, 10), quantity: 1, properties: mainProps }];

    // Verzamel alle GPO-varianten die nu actief zijn — geen properties,
    // de variant titel is zelf-uitleg ("CarOptions - Bleiswijk", "Achteruitrijcamera incl. montage" etc.)
    var addedVariants = {};
    if (!mainIsInstall) addedVariants[parseInt(mainVariantId, 10)] = true;
    function addVariant(variantId, props) {
      if (!variantId) return;
      var idNum = parseInt(variantId, 10);
      if (!idNum || addedVariants[idNum]) return;
      addedVariants[idNum] = true;
      var it = { id: idNum, quantity: 1 };
      if (props) it.properties = props;
      items.push(it);
      console.log('[CO-AI] +variant', idNum);
    }

    // ai code: GPO-regels overslaan in verified install-only flow — exact 1 item.
    if (!verifiedOnly) {
      // a. Installatie-variant uit geselecteerde locatie-option (regular/premium/etc.)
      //    Bij mainIsInstall dragen we de afspraak-properties hierop over.
      var selectedOption = locSelect && locSelect.options[locSelect.selectedIndex];
      if (selectedOption) addVariant(selectedOption.getAttribute('gpo-data-variant-id'), mainIsInstall ? mainProps : null);

      // b. Elke gevinkte GPO-switch met variant id (achteruitrijcamera etc.)
      document.querySelectorAll('input[type="checkbox"][gpo-data-variant-id]').forEach(function (cb) {
        if (cb.checked) addVariant(cb.getAttribute('gpo-data-variant-id'));
      });

      // c. Toekomst-proof: GPO-radio met variant id
      document.querySelectorAll('input[type="radio"][gpo-data-variant-id]:checked').forEach(function (r) {
        addVariant(r.getAttribute('gpo-data-variant-id'));
      });
    }

    console.log('[CO-AI] Cart items:', items);

    // 3. Cart-attributes — onzichtbaar voor klant in checkout, wél in admin order details.
    //    Hiermee koppelt klantenservice de Shopify order aan de planning_appointment.
    var cartAttributes = getCartAttributes(state.appointmentId, 'pay_now', state.flowMode);

    // 4. Iedere cart-mutatie en de vereiste attributes moeten aantoonbaar
    // geslaagd zijn voordat checkout mag starten.
    return runCart({
      items: items,
      attributes: cartAttributes,
      requireContactAttributes: state.flowMode === FLOW_MODE.PERSONAL_CONTACT
    });
  }

  // Bouwt /checkout?... URL met alleen de klantgegevens uit de planner.
  // Shopify vraagt het bezorgadres zelf wanneer de checkout dat nodig heeft.
  function buildCheckoutUrl() {
    var f = state.form;
    var params = [];
    function p(key, val) {
      if (val) params.push(encodeURIComponent(key) + '=' + encodeURIComponent(val));
    }
    p('checkout[email]', f.email);
    p('checkout[shipping_address][first_name]', f.voornaam);
    p('checkout[shipping_address][last_name]', f.achternaam);
    p('checkout[shipping_address][company]', f.bedrijf);
    p('checkout[shipping_address][phone]', f.tel);

    return '/checkout' + (params.length ? '?' + params.join('&') : '');
  }

  // Stap 3 → PATCH appointment naar status='onbevestigd' + (bij betaal-later) Apple Cal event aanmaken
  // ai code: retourneert {ok: bool, reason?: string} zodat caller kan beslissen
  // of doorgaan naar step 4 OK is. Eerder slikte deze functie alle fouten in
  // en stuurde de klant tóch door naar de "bevestigd"-stap — daardoor 8 stille
  // mislukte bookings (zie incident 28 mei). Nu krijgt klant duidelijke fout
  // en blijft op step 3 zodat ze opnieuw kunnen klikken of contact opnemen.
  function confirmBooking() {
    if (!SUPABASE_ANON_KEY) return Promise.resolve({ ok: false, reason: 'config_missing' });
    if (state.completed === true) return Promise.resolve({ ok: false, reason: 'completed' });
    if (!isFlowIdentityCurrent(state.flowIdentity)) {
      handleFlowIdentityMismatch();
      return Promise.resolve({ ok: false, reason: 'identity_changed' });
    }
    if (!state.appointmentId || !state.editToken) {
      console.warn('[CO-AI] confirmBooking zonder appointmentId/token — saveLead eerst');
      return Promise.resolve({ ok: false, reason: 'no_appointment' });
    }
    var attempt;
    try { attempt = freezeFinalizationAttempt(); } catch (error) { state.step = 2; persistState(); return Promise.resolve({ ok: false, reason: 'revision_missing' }); }
    return requestDirectFinalization(fetch, SUPABASE_URL, SUPABASE_ANON_KEY, attempt).catch(function (error) {
      // An uncertain response retains the exact persisted attempt for an idempotent retry.
      var selectionRejected = error.sqlState === '22023' && (error.code === 'storefront_finalize_invalid_slot' ||
        error.code === 'storefront_finalize_location_unavailable');
      var capacityRejected = error.sqlState === 'P0001' && error.code === 'deposit_slot_unavailable';
      var revisionRejected = error.sqlState === '22023' && error.code === 'storefront_finalize_revision_changed';
      if (capacityRejected || revisionRejected || selectionRejected) {
        state.finalizationAttempt = null;
        state.selectedSlot = null;
        if (selectionRejected) state.selectedDate = null;
        if (error.code === 'storefront_finalize_location_unavailable') {
          state.locationId = null;
          state.locationSlug = null;
        }
        state.step = error.code === 'storefront_finalize_revision_changed' ? 2 : 1;
        persistState();
      }
      return { ok: false, reason: error.code || 'network_or_rpc' };
    });
  }

  function freezeFinalizationAttempt() {
    if (state.finalizationAttempt) return state.finalizationAttempt;
    var draft = state.leadSaveDraft;
    if (!draft || draft.id !== state.appointmentId || draft.editToken !== state.editToken ||
        draft.revision !== state.savedRevision || draft.needsExplicitRetry ||
        !Number.isSafeInteger(state.savedRevision) || state.savedRevision < 1) {
      throw new Error('Controleer en bewaar je gegevens opnieuw voordat je afrondt.');
    }
    state.finalizationAttempt = { identity: state.flowIdentity, appointmentId: state.appointmentId,
      editToken: state.editToken, paymentMethod: state.payment === 'nu' ? 'pay_now' : 'after_install',
      expectedRevision: state.savedRevision };
    if (!persistState()) { state.finalizationAttempt = null; throw new Error('Je aanvraag kon niet veilig worden bewaard.'); }
    return state.finalizationAttempt;
  }

  function finalizeContactBooking() {
    if (state.completed === true) {
      return Promise.resolve({ status: 'failed', reason: 'completed' });
    }
    if (!isFlowIdentityCurrent(state.flowIdentity)) {
      handleFlowIdentityMismatch();
      return Promise.resolve({ status: 'failed', reason: 'identity_changed' });
    }
    if (!state.appointmentId || !state.editToken) {
      return Promise.resolve({ status: 'failed' });
    }
    var attempt;
    try { attempt = freezeFinalizationAttempt(); } catch (error) { state.step = 2; persistState(); return Promise.resolve({ status: 'failed', reason: 'revision_missing' }); }
    return requestContactFinalization(fetch, SUPABASE_URL, attempt).then(function (result) {
      if (result.reason === 'revision_changed') {
        state.finalizationAttempt = null;
        state.step = 2;
        persistState();
      }
      return result;
    });
  }

  // ============================================================
  // CART BUTTON SWAP
  // ============================================================

  function getAllSubmitButtons() { return document.querySelectorAll(SUBMIT_SELECTOR); }
  function getButtonTextEl(btn) { return btn ? btn.querySelector(BTN_TEXT_SELECTOR) : null; }
  function getButtonIconEl(btn) { return btn ? btn.querySelector(BTN_ICON_SELECTOR) : null; }

  function findLaIconClass(el) {
    if (!el || !el.classList) return null;
    for (var i = 0; i < el.classList.length; i++) {
      var cls = el.classList[i];
      if (cls.indexOf('la-') === 0 && cls !== 'las' && cls !== 'lar' && cls !== 'lab') return cls;
    }
    return null;
  }

  function isSwitchChecked() {
    var sw = document.querySelector(SWITCH_SELECTOR);
    return !!(sw && sw.checked);
  }
  function getCurrentLocation() {
    if (typeof window.syncBentleyIncludedInstallation === 'function') window.syncBentleyIncludedInstallation();
    // ai code: op de losse installatie-pagina (verified install-only flow)
    // is de locatie de PAGINA-VARIANT, niet de GPO-kiezer. De verify-snippet
    // zet window.CO_AI_FORCED_LOCATION op de geselecteerde variant-titel.
    if (typeof window.CO_AI_FORCED_LOCATION === 'string' && window.CO_AI_FORCED_LOCATION) {
      return window.CO_AI_FORCED_LOCATION;
    }
    var sel = document.querySelector(LOCATION_SELECT_SELECTOR);
    return sel ? sel.value : '';
  }
  // ai code: huidige variant ID — uit URL, of uit verborgen Shopify form input
  function getCurrentVariantIdGlobal() {
    var um = (window.location.search || '').match(/[?&]variant=(\d+)/);
    if (um) return um[1];
    // Fallback: variant ID uit hidden <select name="id"> of <input name="id">
    var idInput = document.querySelector('form[action*="/cart/add"] [name="id"], select[name="id"], input[name="id"]');
    if (idInput && idInput.value) return String(idInput.value);
    return '';
  }
  function isAllowedVariant() {
    if (ALLOWED_VARIANT_IDS.length === 0) return true; // geen lijst = overal
    var current = getCurrentVariantIdGlobal();
    if (!current) return false;
    return ALLOWED_VARIANT_IDS.indexOf(current) !== -1;
  }
  function isBentleyInstallationProduct() {
    var productId = document.querySelector('input[name="product-id"]');
    var metaProduct = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    var currentProductId = productId ? productId.value : metaProduct && metaProduct.id;
    return getCurrentProductHandle() === 'carplaybox-bentley'
      && String(currentProductId) === '9705406103886';
  }
  function isBentleyIncludedInstallation() {
    return isBentleyInstallationProduct()
      && ['55032836849998', '55032571855182', '55032568054094'].indexOf(getCurrentVariantIdGlobal()) !== -1;
  }
  function passesPlanningProductGates() {
    if (typeof window.syncBentleyIncludedInstallation === 'function') window.syncBentleyIncludedInstallation();
    // ai code: vier checks:
    //   0) product mag niet op de exclude-lijst staan (losse installatie-product)
    //   1) variant moet in de allow-list
    //   2) "Montage en Installatie" switch — alleen vereist als die op de
    //      pagina aanwezig is. Niet aanwezig → niet vereist.
    // ai code: op de losse installatie-pagina mag de popup WEL na order-
    // verificatie (window.CO_AI_INSTALL_VERIFIED door installatie-verify-ai
    // snippet gezet). Zonder verificatie blijft de pagina popup-vrij.
    if (EXCLUDED_PRODUCT_HANDLES.indexOf(getCurrentProductHandle()) !== -1 && window.CO_AI_INSTALL_VERIFIED !== true) return false;
    if (!isAllowedVariant()) return false;
    if (isBentleyIncludedInstallation()) {
      var includedSelect = document.querySelector('.location_selector1.location_selector select');
      if (!includedSelect || includedSelect.disabled
          || typeof window.isBentleyIncludedInstallationReady !== 'function'
          || !window.isBentleyIncludedInstallationReady(includedSelect)) return false;
      for (var element = includedSelect; element; element = element.parentElement) {
        if (element.hidden || window.getComputedStyle(element).display === 'none') return false;
      }
      return true;
    }
    var sw = document.querySelector(SWITCH_SELECTOR);
    // Fiat 124 Spider includes installation; the hidden 500/500X switch is inapplicable.
    var fiatIncluded = getCurrentProductHandle() === 'carplay-android-auto-voor-fiat' && getCurrentVariantIdGlobal() === '55031269228878';
    if (sw && !sw.checked && !fiatIncluded) return false;
    return true;
  }

  function getCurrentFlowMode() {
    if (!passesPlanningProductGates()) return FLOW_MODE.DEFAULT_CART;
    return resolveFlowMode(getCurrentLocation(), ACTIVE_LOCATION_CONFIGS);
  }

  function getCurrentFlowIdentity() {
    var selectedLocationValue = getCurrentLocation();
    var planningMode = getCurrentFlowMode();
    var locationSlug = resolveLocationSlug(selectedLocationValue, ACTIVE_LOCATION_CONFIGS);
    return buildFlowIdentity({
      selectedLocationValue: selectedLocationValue,
      locationSlug: locationSlug || '',
      planningMode: planningMode,
      productHandle: getCurrentProductHandle(),
      variantId: getCurrentVariantIdGlobal()
    });
  }

  function isFlowIdentityCurrent(expectedIdentity) {
    return !!expectedIdentity && getCurrentFlowIdentity() === expectedIdentity;
  }

  function handleFlowIdentityMismatch() {
    var currentMode = getCurrentFlowMode();
    var currentIdentity = getCurrentFlowIdentity();
    bindFlowIdentity(state, currentIdentity);
    state.flowMode = currentMode;
    persistState();
    syncAllButtons();
    if (!currentIdentity || currentMode === FLOW_MODE.DEFAULT_CART) {
      closePopup();
      return;
    }
    render();
  }

  function requestFlowMode(selectedGpoValue) {
    if (!passesPlanningProductGates()) return Promise.resolve(FLOW_MODE.DEFAULT_CART);
    return resolveFlowModeFromConfig(selectedGpoValue, loadActiveLocationConfigs);
  }

  function shouldBeAppointmentMode() {
    return getCurrentFlowMode() === FLOW_MODE.LEGACY_DIRECT;
  }

  function applyAppointmentMode(btn) {
    var span = getButtonTextEl(btn);
    if (!span) return;
    // textContent leest ook bij verborgen elementen (sticky cart bv) — innerText geeft "" terug
    if (!btn.hasAttribute(ORIG_TEXT_ATTR)) {
      btn.setAttribute(ORIG_TEXT_ATTR, span.textContent || '');
    }
    span.textContent = APPOINTMENT_TEXT;

    var icon = getButtonIconEl(btn);
    if (icon) {
      var origIconClass = findLaIconClass(icon);
      if (origIconClass && !btn.hasAttribute(ORIG_ICON_ATTR)) btn.setAttribute(ORIG_ICON_ATTR, origIconClass);
      if (origIconClass) icon.classList.remove(origIconClass);
      icon.classList.add(APPOINTMENT_ICON_CLASS);
    }
    btn.setAttribute(STATE_ATTR, 'appointment');
  }

  function applyPersonalContactMode(btn) {
    var span = getButtonTextEl(btn);
    if (!span) return;
    if (!btn.hasAttribute(ORIG_TEXT_ATTR)) {
      btn.setAttribute(ORIG_TEXT_ATTR, span.textContent || '');
    }
    span.textContent = getFlowCta(FLOW_MODE.PERSONAL_CONTACT, span.textContent || '');

    var icon = getButtonIconEl(btn);
    if (icon) {
      icon.classList.remove(APPOINTMENT_ICON_CLASS);
      var origIconClass = btn.getAttribute(ORIG_ICON_ATTR);
      if (origIconClass) icon.classList.add(origIconClass);
    }
    btn.setAttribute(STATE_ATTR, FLOW_MODE.PERSONAL_CONTACT);
  }

  function applyDefaultMode(btn) {
    var span = getButtonTextEl(btn);
    if (!span) return;
    var orig = btn.getAttribute(ORIG_TEXT_ATTR);
    if (orig !== null) span.textContent = orig;

    var icon = getButtonIconEl(btn);
    if (icon) {
      icon.classList.remove(APPOINTMENT_ICON_CLASS);
      var origIconClass = btn.getAttribute(ORIG_ICON_ATTR);
      if (origIconClass) icon.classList.add(origIconClass);
    }
    btn.removeAttribute(STATE_ATTR);
  }

  function applyPlanningCheckMode(btn) {
    var span = getButtonTextEl(btn);
    var form = btn.form || (btn.closest && btn.closest('form'));
    var retry = form && form.querySelector('[data-co-ai-location-error]');
    if (span) {
      if (!btn.hasAttribute(ORIG_TEXT_ATTR)) btn.setAttribute(ORIG_TEXT_ATTR, span.textContent || '');
      span.textContent = retry ? 'Opnieuw proberen' : 'Installatie regelen';
    }
    btn.setAttribute(STATE_ATTR, 'planning_check');
  }

  function applyFlowModeToAllButtons(mode) {
    getAllSubmitButtons().forEach(function (btn) {
      if (mode === FLOW_MODE.LEGACY_DIRECT) {
        clearPlanningLocationRetry(btn);
        applyAppointmentMode(btn);
      } else if (mode === FLOW_MODE.PERSONAL_CONTACT) {
        clearPlanningLocationRetry(btn);
        applyPersonalContactMode(btn);
      } else if (passesPlanningProductGates() && getCurrentLocation()) applyPlanningCheckMode(btn);
      else applyDefaultMode(btn);
    });
  }

  function syncAllButtons() {
    var selectedGpoValue = getCurrentLocation();
    var immediateMode = getCurrentFlowMode();
    applyFlowModeToAllButtons(immediateMode);

    // Legacy wordt hierboven synchroon beslist en roept de loader niet aan.
    // Een niet-legacy knop blijft default totdat de exacte actieve rij bekend is.
    if (immediateMode === FLOW_MODE.LEGACY_DIRECT || !selectedGpoValue || !passesPlanningProductGates()) return;
    requestFlowMode(selectedGpoValue).then(function (resolvedMode) {
      if (getCurrentLocation() !== selectedGpoValue || !passesPlanningProductGates()) return;
      applyFlowModeToAllButtons(resolvedMode);
    });
  }

  function continueNativeCartSubmission(btn, isCurrentIntent) {
    if (isCurrentIntent && !isCurrentIntent()) return;
    if (btn && typeof btn.click === 'function') {
      setTimeout(function () {
        if (!isCurrentIntent || isCurrentIntent()) btn.click();
      }, 0);
      return;
    }
    var form = btn && (btn.form || (btn.closest && btn.closest('form')));
    if (form && typeof form.requestSubmit === 'function') {
      form.requestSubmit(btn);
      return;
    }
    if (form && typeof form.submit === 'function') form.submit();
  }

  function clearPlanningLocationRetry(btn) {
    var form = btn && (btn.form || (btn.closest && btn.closest('form')));
    var message = form && form.querySelector('[data-co-ai-location-error]');
    if (message) message.remove();
  }

  function showPlanningLocationRetry(btn) {
    var form = btn && (btn.form || (btn.closest && btn.closest('form')));
    if (!form) return;
    var message = form.querySelector('[data-co-ai-location-error]');
    if (!message) {
      message = document.createElement('p');
      message.setAttribute('data-co-ai-location-error', '');
      message.setAttribute('role', 'alert');
      form.appendChild(message);
    }
    message.textContent = 'De installatieplanning kon niet worden gecontroleerd. Probeer opnieuw.';
    applyPlanningCheckMode(btn);
  }

  function beginPlanningLocationIntent() {
    PLANNING_LOCATION_INTENT_GENERATION += 1;
    getAllSubmitButtons().forEach(function (button) {
      button._coAiLocationRequestVersion = (button._coAiLocationRequestVersion || 0) + 1;
      button.removeAttribute('data-co-ai-contact-revalidating');
    });
  }

  function revalidatePersonalContactClick(btn) {
    var selectedGpoValue = getCurrentLocation();
    var selectedVariantId = getCurrentVariantIdGlobal();
    var selectedProductHandle = getCurrentProductHandle();
    var intentGeneration = PLANNING_LOCATION_INTENT_GENERATION;
    var requestVersion = ((btn && btn._coAiLocationRequestVersion) || 0) + 1;
    if (btn) btn._coAiLocationRequestVersion = requestVersion;
    function ownsRequest() {
      return PLANNING_LOCATION_INTENT_GENERATION === intentGeneration &&
        (!btn || btn._coAiLocationRequestVersion === requestVersion);
    }
    clearPlanningLocationRetry(btn);
    return loadActiveLocationConfigs(true)
      .then(function (rows) {
        if (!ownsRequest()) return;
        if (getCurrentVariantIdGlobal() !== selectedVariantId || getCurrentProductHandle() !== selectedProductHandle) {
          syncAllButtons();
          if (passesPlanningProductGates() && getCurrentLocation()) showPlanningLocationRetry(btn);
          return;
        }
        var selectionUnchanged = getCurrentLocation() === selectedGpoValue;
        // A deliberate change to self-install retains the native cart action.
        // An unavailable or changed installation selection must never fall through.
        if (!passesPlanningProductGates() || !getCurrentLocation()) {
          applyFlowModeToAllButtons(FLOW_MODE.DEFAULT_CART);
          continueNativeCartSubmission(btn, function () {
            return ownsRequest() && getCurrentVariantIdGlobal() === selectedVariantId &&
              getCurrentProductHandle() === selectedProductHandle &&
              (!passesPlanningProductGates() || !getCurrentLocation());
          });
          return;
        }
        if (!selectionUnchanged) {
          syncAllButtons();
          showPlanningLocationRetry(btn);
          return;
        }
        var mode = resolveFlowMode(selectedGpoValue, rows);
        applyFlowModeToAllButtons(mode);
        if (mode === FLOW_MODE.PERSONAL_CONTACT || mode === FLOW_MODE.LEGACY_DIRECT) {
          openPopup();
          return;
        }
        showPlanningLocationRetry(btn);
      })
      .catch(function () { if (ownsRequest()) showPlanningLocationRetry(btn); })
      .finally(function () {
        if (btn && ownsRequest()) btn.removeAttribute('data-co-ai-contact-revalidating');
      });
  }

  // ============================================================
  // PRICE CALCULATION
  //
  // GPO heeft geen exposed totaal-API. We berekenen totaal door de base
  // product-prijs op te tellen met alle gekozen opties. De prijs van een
  // optie staat in z'n value-tekst (bv. "Door CarOptions (+€149,-)").
  // ============================================================

  function parseEuro(str) {
    if (!str) return 0;
    var s = String(str).trim();
    s = s.replace(/[^\d.,-]/g, '');
    if (!s) return 0;
    var hasComma = s.indexOf(',') !== -1;
    var hasDot = s.indexOf('.') !== -1;
    if (hasComma && hasDot) {
      // NL-format: punt = duizendtal, komma = decimaal
      s = s.replace(/\./g, '').replace(',', '.');
    } else if (hasComma) {
      s = s.replace(',', '.');
    }
    // Trailing "-" of ".-" betekent .00 (bv "149,-")
    s = s.replace(/[\s.,]-+$/, '');
    var n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  }

  function formatEuro(amount) {
    if (typeof amount !== 'number' || isNaN(amount)) return '';
    if (amount === Math.floor(amount)) return '€' + amount.toFixed(0);
    return '€' + amount.toFixed(2).replace('.', ',');
  }

  // ai code: extraheert prijs uit een optie-waarde zoals "Achteruitrijcamera (+€199,-)"
  // OF "Touch-Screen inc. montage (+199,-)" — sommige opties laten het € symbool weg,
  // dus die maken we optioneel in de regex. Voorbeelden die WERKEN:
  //   "Achteruitrijcamera (+€199,-)"        → 199
  //   "Touch-Screen inc. montage (+199,-)"  → 199 (geen €)
  //   "Door CarOptions (+ €149)"            → 149
  function parseOptionPrice(value) {
    if (!value) return 0;
    var m = String(value).match(/\(\s*\+\s*€?\s*([\d.,-]+)\s*\)/);
    if (!m) return 0;
    return parseEuro(m[1]);
  }

  // ai code: meest betrouwbare bron — Shopify thema zet bij elke GPO-optie de
  // exacte prijs in een class als "add-to-price--149" of "add-to-price--449".
  // Werkt voor ALLE varianten, ook als de zichtbare label geen prijs heeft.
  function parsePriceFromClass(el) {
    if (!el || !el.className) return 0;
    var m = String(el.className).match(/add-to-price--(\d+)(?!\d)/);
    return m ? parseInt(m[1], 10) : 0;
  }

  // Verwijder de "(+€XXX,-)" suffix uit een optie-label
  function cleanOptionLabel(value) {
    return String(value || '').replace(/\s*\(\s*\+\s*€?\s*[\d.,\-]+\s*\)\s*/, '').trim();
  }

  // ai code: huidige variant-prijs uit Shopify's eigen meta (cents → euro).
  // Dit is de SALE prijs (rekening houdend met korting). Mazda korting:
  //   ShopifyAnalytics.meta.product.variants[X].price === 34900  → €349
  //   .t4s-product-price innerText === "€424,00 €349,00"           ❌ (compare+sale gemixt)
  function getVariantPriceFromMeta() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (!meta || !meta.product || !Array.isArray(meta.product.variants)) return 0;
      var currentId = (new URLSearchParams(location.search).get('variant')) || meta.selectedVariantId;
      if (!currentId) return 0;
      var variant = meta.product.variants.find(function (v) { return String(v.id) === String(currentId); });
      if (!variant || typeof variant.price !== 'number') return 0;
      return variant.price / 100;
    } catch (_) { return 0; }
  }

  // ai code: fallback — lees prijs uit DOM. Bij Shopify sale-prijs structuur
  //   <div class="t4s-product-price"><del>€424,00</del> <ins>€349,00</ins></div>
  // pakken we de <ins> (sale prijs). Geen <ins>? Dan de hele innerText.
  function getVariantPriceFromDom() {
    var el = document.querySelector('.t4s-product-price, [data-product-price]');
    if (!el) return 0;
    var ins = el.querySelector('ins');
    var text = ins && ins.textContent ? ins.textContent : (el.textContent || el.innerText || '');
    return parseEuro(text);
  }

  function calculateTotalPrice() {
    if (typeof window.syncBentleyIncludedInstallation === 'function') window.syncBentleyIncludedInstallation();
    // 1) Base = sale-prijs van de huidige variant (cents-precision via Shopify meta)
    var base = getVariantPriceFromMeta();
    if (base <= 0) base = getVariantPriceFromDom();

    var extra = 0;
    // 2) Switch-extras — primair via add-to-price-- class (betrouwbaar), fallback op value text.
    //    :checked filter zorgt dat alleen aangevinkte switches meetellen.
    document.querySelectorAll('input[type="checkbox"][data-type="switch"]:checked').forEach(function (c) {
      var fromClass = parsePriceFromClass(c.closest('.gpo-element'));
      extra += fromClass > 0 ? fromClass : parseOptionPrice(c.value || '');
    });
    // 3) Dropdown-extras — LET OP: skip default/empty options.
    //    De location-selector ("Kies een installatie partner") heeft óók een
    //    add-to-price--XXX class op zijn parent, maar dat is de toeslag voor
    //    LOCATIE-installatie die al via de switch "Montage en Installatie" wordt
    //    geteld. Een lege selectie ("Kies een locatie", value="") mag NOOIT
    //    meetellen — anders wordt €149 onterecht opgeteld voordat je iets kiest.
    document.querySelectorAll('.gpo-app select').forEach(function (sel) {
      var opt = sel.options && sel.options[sel.selectedIndex];
      if (!opt) return;
      var optValue = (opt.value || '').trim();
      var optText = (opt.text || '').trim();
      if (!optValue) return; // skip placeholder option
      // Class-fallback alleen als de optie-label zelf geen prijs noemt.
      // Voor de location-selector geldt dit niet: die heeft geen prijs in de label,
      // dus die zou via class +149 toevoegen. Maar locatie-installatie wordt al
      // geteld via de "Montage en Installatie" switch — dus class hier negeren.
      var fromText = parseOptionPrice(optValue || optText);
      extra += fromText;
    });
    return base + extra;
  }

  // ============================================================
  // PRODUCT INFO READER
  // ============================================================

  // ai code: canonieke productdata uit Shopify zelf (/products/<handle>.js).
  // Incident #232014996: klant gebruikte Chrome auto-translate (Russisch) —
  // de popup las de VERTAALDE paginatekst en zo kwam "CarPlayBox для Lexus /
  // Поворотный регулятор" in de DB, agenda en mails. De .js-endpoint geeft
  // altijd de originele (onvertaalde) titels. DOM blijft alleen fallback.
  var shopifyProductData = null;
  function prefetchShopifyProduct() {
    try {
      var handle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
      if (!handle || shopifyProductData) return;
      fetch('/products/' + handle + '.js', { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) { if (p && p.title) shopifyProductData = p; })
        .catch(function () { /* fallback = DOM */ });
    } catch (e) { /* fallback = DOM */ }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', prefetchShopifyProduct);
  } else {
    prefetchShopifyProduct();
  }

  // ============================================================
  // ai code: WK-ACTIE / LIVE PRIJZEN
  // Per variant de actuele price + compare_at_price uit Shopify zelf
  // (/products/<handle>.js). Dekt hoofdproduct, installatie-variant én
  // upsells. Zodra compare_at > price voor de huidige selectie, toont de
  // samenvatting-kaart de actie (oranje border, WK ACTIE ribbon, was-prijs
  // doorgestreept). Compare-at weg in Shopify = actie automatisch weg.
  // Onbekende varianten → veilige fallback naar de normale weergave.
  // ============================================================
  var coPriceMap = {};
  var coPriceMapReady = false;
  var coFetchedHandles = {};

  function coAddProductToPriceMap(p) {
    if (!p || !p.variants) return;
    p.variants.forEach(function (v) {
      coPriceMap[String(v.id)] = {
        price: (typeof v.price === 'number' ? v.price : 0) / 100,
        compareAt: v.compare_at_price ? v.compare_at_price / 100 : null
      };
    });
  }

  function coFetchHandlePrices(handle) {
    if (!handle || coFetchedHandles[handle]) return Promise.resolve();
    coFetchedHandles[handle] = true;
    return fetch('/products/' + handle + '.js', { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { coAddProductToPriceMap(p); })
      .catch(function () { /* fallback = normale weergave */ });
  }

  function initPriceMap() {
    var handles = {};
    var ph = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (ph) handles[ph] = 1;
    try {
      // Handles van installatie-product + upsells uit de Globo-config van
      // de option-sets die op DIT product van toepassing zijn.
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var pid = meta && meta.product && meta.product.id;
      var optsets = window.GPOConfigs && window.GPOConfigs.options;
      if (optsets && pid) {
        Object.keys(optsets).forEach(function (k) {
          var setObj = optsets[k];
          var rule = setObj && setObj.products && setObj.products.rule;
          var ids = (rule && rule.manual && rule.manual.enable) ? (rule.manual.ids || []) : [];
          var applies = ids.indexOf(pid) !== -1 || !!(rule && rule.all && rule.all.enable);
          if (!applies) return;
          (function walk(els) {
            (els || []).forEach(function (e) {
              if (!e) return;
              if (e.type === 'group') return walk(e.elements);
              if (e.type === 'select' && e.option_values) {
                e.option_values.forEach(function (v) { if (v.product_handle) handles[v.product_handle] = 1; });
              }
              if (e.addon_product && e.addon_product.product_handle) handles[e.addon_product.product_handle] = 1;
            });
          })(setObj.elements);
        });
      }
    } catch (e) { /* prijzen blijven fallback */ }
    // ai code: óók de DOM scannen — GPO zet gpo-data-product-handle op de
    // locatie-opties/extras. Dit dekt sets die via een titel-regel (automate)
    // aan het product hangen i.p.v. via product-ids (bv. Renault).
    document.querySelectorAll('[gpo-data-product-handle]').forEach(function (el) {
      var h = el.getAttribute('gpo-data-product-handle');
      if (h) handles[h] = 1;
    });
    Promise.all(Object.keys(handles).map(coFetchHandlePrices))
      .then(function () { coPriceMapReady = true; });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPriceMap);
  } else {
    initPriceMap();
  }
  // GPO rendert async — herscan een paar keer zodat laat-geinjecteerde
  // opties (met hun product-handles) ook in de prijzen-kaart komen.
  setTimeout(initPriceMap, 1500);
  setTimeout(initPriceMap, 4000);
  setTimeout(initPriceMap, 8000);

  function coCurrentLineVariantIds() {
    if (typeof window.syncBentleyIncludedInstallation === 'function') window.syncBentleyIncludedInstallation();
    var ids = [];
    var main = parseInt(getCurrentVariantIdGlobal(), 10);
    if (main > 0) ids.push(main);
    var locSel = document.querySelector('.location_selector select');
    var opt = locSel && locSel.options[locSel.selectedIndex];
    var lv = opt && opt.getAttribute('gpo-data-variant-id');
    if (lv && parseInt(lv, 10) > 0) ids.push(parseInt(lv, 10));
    document.querySelectorAll('input[type="checkbox"][gpo-data-variant-id]').forEach(function (cb) {
      var vid = parseInt(cb.getAttribute('gpo-data-variant-id'), 10);
      if (cb.checked && vid > 0) ids.push(vid);
    });
    return ids;
  }

  function coSaleTotals() {
    if (!coPriceMapReady) return null;
    var ids = coCurrentLineVariantIds();
    if (!ids.length) return null;
    var total = 0, compare = 0;
    for (var i = 0; i < ids.length; i++) {
      var inf = coPriceMap[String(ids[i])];
      if (!inf || !(inf.price > 0)) return null; // onbekend → geen actie-weergave
      total += inf.price;
      compare += (inf.compareAt && inf.compareAt > inf.price) ? inf.compareAt : inf.price;
    }
    return { total: total, compare: compare, sale: compare > total + 0.004 };
  }

  function readProductInfo() {
    var info = {};
    var screenContext = getVwScreenBookingContext();
    if (screenContext) {
      info.vehicleBrand = screenContext.brand;
      info.serviceLabel = screenContext.serviceLabel;
    }
    info.title = (shopifyProductData && shopifyProductData.title)
      || (document.querySelector('.t4s-product__title, h1.product__title')
          || document.querySelector('h1'))?.innerText?.trim() || 'CarPlayBox Installatie';

    // Totaal incl. installatie + extras (NIET de base-prijs)
    var total = calculateTotalPrice();
    info.priceText = total > 0 ? formatEuro(total) : '';

    // ai code: altijd de CarPlayBox-placeholder gebruiken (uniform over alle
    // varianten, geen randomness van Shopify product-images in de popup).
    // URL komt van Liquid via window.CO_AI_PLACEHOLDER_IMG.
    info.imgSrc = (typeof window !== 'undefined' && window.CO_AI_PLACEHOLDER_IMG)
      ? window.CO_AI_PLACEHOLDER_IMG
      : '';

    // ai code: het kenteken zit op deze productpagina op verschillende plekken.
    // Volgorde van betrouwbaarheid (eerste match wint):
    //   1) sessionStorage 'licensePlate' (JSON met {license, brand, var}) —
    //      gezet door carplaybox_kenteken_matcher na succesvolle RDW-check.
    //   2) Hidden GPO input [data-field-name="Kenteken"] — wordt op checkout
    //      submitted, dus de "waarheid" voor pay-later flow.
    //   3) input[name="properties[Kenteken]"] — alias voor 2.
    //   4) .plate_matcher_order_rule input — visible plate matcher input.
    //   5) input[name="Kenteken"] — oudere templates.
    //   6) .kenteken / .centeredkenteken input — losse kentekencheck snippet.
    var plate = '';
    try {
      var lp = sessionStorage.getItem('licensePlate');
      if (lp) {
        var parsed = JSON.parse(lp);
        if (parsed && parsed.license) plate = parsed.license;
      }
    } catch (_) {}
    if (!plate) {
      var hidden = document.querySelector('.gpo-hidden-inputs [data-field-name="Kenteken"]')
                || document.querySelector('input[name="properties[Kenteken]"]');
      if (hidden && hidden.value) plate = hidden.value;
    }
    if (!plate) {
      var vis = document.querySelector('.plate_matcher_order_rule input')
             || document.querySelector('input[name="Kenteken"]')
             || document.querySelector('.kenteken input[type="text"]')
             || document.querySelector('.centeredkenteken input[type="text"]');
      if (vis && vis.value) plate = vis.value;
    }
    info.kenteken = plate ? plate.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') : '';

    // ai code: variant-titel — EERST uit canonieke Shopify productdata (vertaal-
    // proof, zie incident #232014996), anders uit SELECT option (swatch toont
    // vaak een afgekorte versie waardoor alias-lookup misloopt).
    var fullTitle = '';
    if (shopifyProductData && shopifyProductData.variants) {
      var curVid = parseInt(getCurrentVariantIdGlobal(), 10);
      var curVar = shopifyProductData.variants.find
        ? shopifyProductData.variants.find(function (v) { return v.id === curVid; })
        : null;
      if (curVar && curVar.title) fullTitle = curVar.title;
    }
    if (!fullTitle) {
      var fullOpt = document.querySelector('.is-t4s-name__model select option:checked')
                 || document.querySelector('select[name="id"] option:checked');
      fullTitle = fullOpt && (fullOpt.text || fullOpt.textContent || '').trim();
    }
    if (!fullTitle) {
      var swEl = document.querySelector('.is-t4s-name__model [data-swatch-item].is--selected');
      fullTitle = (swEl && (swEl.innerText || swEl.dataset?.value || '')).trim();
    }
    info.model = fullTitle || '';

    // Geselecteerde extra opties (uit GPO checkboxes anders dan switch-1 = installatie zelf
    // en switch-2 = MENU-knop bevestiging)
    info.extras = [];
    document.querySelectorAll('input[type="checkbox"][data-type="switch"]:checked').forEach(function (c) {
      if (c.id !== 'switch-1' && c.id !== 'switch-2') {
        var label = cleanOptionLabel(c.value);
        if (label) info.extras.push(label);
      }
    });

    info.location = getCurrentLocation().replace(/^CarOptions\s*-\s*/, '');

    return info;
  }

  // ============================================================
  // SVG ICONS (inline)
  // ============================================================

  function svgIcon(name, attrs) {
    attrs = attrs || '';
    var defs = {
      chevronLeft: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><polyline points="15 6 9 12 15 18"/></svg>',
      chevronRight: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><polyline points="9 6 15 12 9 18"/></svg>',
      arrowRight: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><line x1="4" y1="12" x2="20" y2="12"/><polyline points="14 6 20 12 14 18"/></svg>',
      arrowLeft: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><line x1="20" y1="12" x2="4" y2="12"/><polyline points="10 18 4 12 10 6"/></svg>',
      check: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><polyline points="20 6 9 17 4 12"/></svg>',
      clock: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 16 14"/></svg>',
      lock: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>',
      close: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>',
      calendarMini: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect x="3" y="5" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="16" y1="3" x2="16" y2="7"/></svg>',
      hand: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M3 14c0-2 1.6-3.5 3.5-3.5h7L17 7l2 2-3 3h2.5a2 2 0 0 1 0 4H10c-1 0-1.6.4-2.4 1.2L6 19H3v-5z"/><circle cx="12.5" cy="9.5" r="1.2" fill="currentColor" stroke="none"/></svg>',
      // ai code: lucide clock — voor "Na installatie betalen"
      clock: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
      // ai code: lucide credit-card — voor "Nu betalen"
      card: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/></svg>',
      shield: '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"/><polyline points="9 12 11 14 15 10"/></svg>',
      headset: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M4 14v-2a8 8 0 0 1 16 0v2"/><rect x="3" y="14" width="4" height="6" rx="1.5"/><rect x="17" y="14" width="4" height="6" rx="1.5"/><path d="M20 18v1a3 3 0 0 1-3 3h-2"/></svg>',
      pin: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/><circle cx="12" cy="9" r="2.5"/></svg>',
      car: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M3 14l2-5a2 2 0 0 1 1.9-1.4h10.2A2 2 0 0 1 19 9l2 5"/><path d="M3 14h18v4a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1H6v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"/><circle cx="7" cy="16.5" r="1" fill="currentColor" stroke="none"/><circle cx="17" cy="16.5" r="1" fill="currentColor" stroke="none"/></svg>',
      plate: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect x="2.5" y="7" width="19" height="10" rx="1.5"/><path d="M6 11h1M6 13h1M9 11h1.5M9 13h1.5M13 11h1.5M13 13h1.5M17 11h1M17 13h1"/></svg>',
      box: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M3 7.5L12 3l9 4.5v9L12 21l-9-4.5v-9z"/><path d="M3 7.5L12 12l9-4.5"/><line x1="12" y1="12" x2="12" y2="21"/></svg>',
      camera: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.5"/></svg>',
      calendar: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect x="3" y="5" width="18" height="16" rx="2"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="3" x2="8" y2="7"/><line x1="16" y1="3" x2="16" y2="7"/></svg>',
      cardInline: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><rect x="3" y="6" width="18" height="13" rx="2"/><line x1="3" y1="11" x2="21" y2="11"/></svg>',
      euro: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M18 6.5A7 7 0 0 0 7 12a7 7 0 0 0 11 5.5"/><line x1="4" y1="10" x2="13" y2="10"/><line x1="4" y1="14" x2="13" y2="14"/></svg>',
      user: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><circle cx="12" cy="8" r="3.5"/><path d="M5 20c0-3.5 3-6 7-6s7 2.5 7 6"/></svg>',
      phone: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
      help: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" '+attrs+'><circle cx="12" cy="12" r="10"/><path d="M9.1 9.5a3 3 0 1 1 4.8 2.4c-.9.6-1.9 1-1.9 2.1"/><line x1="12" y1="17" x2="12" y2="17.01"/></svg>',
      geschikt: '<svg width="21px" height="21px" viewBox="0 0 21 21" version="1.1" xmlns="http://www.w3.org/2000/svg" '+attrs+'><title>Geschikt</title><g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g id="Afspraak-maken-1.1" transform="translate(-396.000000, -245.000000)" stroke="#00DB00" stroke-width="1.5"><g id="Group-3" transform="translate(209.000000, 179.000000)"><g id="Group-12" transform="translate(189.000000, 68.000000)"><circle id="Oval-Copy-9" cx="8.5" cy="8.5" r="9.25"></circle><polyline id="Path-18" stroke-linecap="round" stroke-linejoin="round" points="4.40160197 7.60080344 7.24107154 11.0730844 12.401602 5.07308439"></polyline></g></g></g></g></svg>'
    };
    return defs[name] || '';
  }

  // ============================================================
  // SHARED COMPONENTS
  // ============================================================

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function plateHTML(value) {
    var v = escapeHTML(value || '');
    var stars = '';
    for (var i = 0; i < 8; i++) {
      var a = (i / 8) * Math.PI * 2 - Math.PI / 2;
      var x = (12 + Math.cos(a) * 7).toFixed(2);
      var y = (12 + Math.sin(a) * 7).toFixed(2);
      stars += '<circle cx="' + x + '" cy="' + y + '" r="0.9" fill="#FFCE2E"/>';
    }
    return '' +
      '<div class="coa-plate">' +
      '  <div class="coa-plate-eu">' +
      '    <div class="coa-plate-eu-stars">' +
      '      <svg viewBox="0 0 24 24" width="14" height="14"><circle cx="12" cy="12" r="11" fill="#003399"/>' + stars + '</svg>' +
      '    </div>' +
      '    <div class="coa-plate-eu-nl">NL</div>' +
      '  </div>' +
      '  <div class="coa-plate-text">' + v + '</div>' +
      '</div>';
  }

  function geschiktBadgeHTML() {
    return '' +
      '<div class="coa-geschikt">' +
      '  <span class="coa-geschikt-circle">' + svgIcon('check') + '</span>' +
      '  <span>Geschikt</span>' +
      '</div>';
  }

  function productThumbHTML(imgSrc) {
    if (imgSrc) {
      return '<div class="coa-product-thumb"><img src="' + escapeHTML(imgSrc) + '" alt=""></div>';
    }
    // Default SVG box
    return '' +
      '<div class="coa-product-thumb">' +
      '  <svg viewBox="0 0 80 80" width="100%" height="100%">' +
      '    <defs>' +
      '      <linearGradient id="coa-boxg" x1="0" y1="0" x2="1" y2="1">' +
      '        <stop offset="0%" stop-color="#3D7BFF"/>' +
      '        <stop offset="100%" stop-color="#1B45F0"/>' +
      '      </linearGradient>' +
      '    </defs>' +
      '    <rect x="14" y="28" width="52" height="28" rx="3" fill="url(#coa-boxg)"/>' +
      '    <rect x="14" y="28" width="52" height="6" fill="#0F2FBE" opacity="0.55"/>' +
      '    <rect x="18" y="40" width="22" height="2" rx="1" fill="#9DBBFF" opacity="0.7"/>' +
      '    <rect x="18" y="45" width="14" height="2" rx="1" fill="#9DBBFF" opacity="0.5"/>' +
      '    <circle cx="58" cy="46" r="2.2" fill="#9DBBFF" opacity="0.7"/>' +
      '    <rect x="14" y="54" width="52" height="3" fill="#0B2799" opacity="0.7"/>' +
      '  </svg>' +
      '</div>';
  }

  // ai code: visuele stepper — 3 groepen ipv 4. Interne state.step blijft 1-4
  // (paginas zelf onveranderd) maar betaling (step 3) zit visueel bij Gegevens.
  // Stappen zijn clickable voor TERUG navigatie (= vorige stap of huidige);
  // forward klikken is uitgeschakeld om validatie te respecteren.
  function stepperHTML(currentStep) {
    var groups = [
      { label: state.flowMode === FLOW_MODE.PERSONAL_CONTACT ? 'Planning' : 'Agenda', jumpTo: 1, activeWhen: [1] },
      { label: 'Gegevens', jumpTo: 2, activeWhen: [2] },
      { label: 'Afronden', jumpTo: 3, activeWhen: [3, 4] },
    ];
    // Bepaal welke visuele groep nu actief is (index)
    var activeIdx = -1;
    for (var k = 0; k < groups.length; k++) {
      if (groups[k].activeWhen.indexOf(currentStep) !== -1) { activeIdx = k; break; }
    }
    var html = '<div class="coa-stepper" role="navigation">';
    for (var i = 0; i < groups.length; i++) {
      var g = groups[i];
      var isActive = i === activeIdx;
      var isPast = activeIdx >= 0 && i < activeIdx;
      var canClick = isPast || isActive; // alleen back-nav + huidige
      var activeCls = isActive ? ' coa-active' : '';
      var doneCls = isPast ? ' coa-done' : '';
      var clickableAttrs = canClick
        ? ' data-coa-step-jump="' + g.jumpTo + '" role="button" tabindex="0" style="cursor:pointer"'
        : ' aria-disabled="true"';
      html += '<div class="coa-step' + (canClick ? ' coa-clickable' : '') + '"' + clickableAttrs + '>' +
              '  <div class="coa-step-circle' + activeCls + doneCls + '">' + (i + 1) + '</div>' +
              '  <div class="coa-step-label' + activeCls + doneCls + '">' + g.label + '</div>' +
              '</div>';
      if (i < groups.length - 1) html += '<div class="coa-step-line"></div>';
    }
    html += '</div>';
    return html;
  }

  // ai code: vroeger "Veilig en vrijblijvend reserveren" footer — uitgezet
  // omdat het visueel te druk werd. Functie laten staan als no-op zodat
  // bestaande call-sites blijven werken zonder errors.
  function safeFooterHTML() { return ''; }

  // ai code: company phone uit theme.liquid schema.org block.
  var COA_PHONE_NUM = '085 060 6177';
  var COA_PHONE_HREF = '+31850606177';

  // ai code: in-popup help-pagina — 3 stappen kort uitgelegd + bel-CTA.
  // Vervangt het oude "Hulp nodig?" dat naar /pages/contact opende.
  // Terug-knop bovenaan brengt klant weer in de normale stap-flow.
  function helpHTML() {
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      return '' +
        '<div class="coa-card coa-help-card">' +
        '  <button type="button" class="coa-help-back" data-coa-help-close aria-label="Terug">' +
        '    ' + svgIcon('chevronLeft') + '<span>Terug</span>' +
        '  </button>' +
        '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
        '  <div class="coa-help-body">' +
        '    <div class="coa-help-icon">' + svgIcon('help') + '</div>' +
        '    <h2 id="co-ai-planner-title" class="coa-help-h">Hulp nodig?</h2>' +
        '    <p class="coa-help-sub">Zo werkt jouw installatieaanvraag in 3 stappen.</p>' +
        '    <ol class="coa-help-steps">' +
        '      <li><div class="coa-help-step-n">1</div><div class="coa-help-step-body"><div class="coa-help-step-h">Vraag installatie aan</div><div class="coa-help-step-p">Wij stemmen de planning persoonlijk met je af.</div></div></li>' +
        '      <li><div class="coa-help-step-n">2</div><div class="coa-help-step-body"><div class="coa-help-step-h">Vul je gegevens in</div><div class="coa-help-step-p">Naam, e-mail of telefoon — zodat we je kunnen bereiken.</div></div></li>' +
        '      <li><div class="coa-help-step-n">3</div><div class="coa-help-step-body"><div class="coa-help-step-h">Kies je betaalwijze</div><div class="coa-help-step-p">Binnen één werkdag nemen we contact met je op.</div></div></li>' +
        '    </ol>' +
        '    <div class="coa-help-cta-block">' +
        '      <div class="coa-help-cta-h">Kom je er niet uit?</div>' +
        '      <div class="coa-help-cta-p">We helpen je graag, bel ons direct.</div>' +
        '      <a class="coa-help-call" href="tel:' + COA_PHONE_HREF + '">' + svgIcon('phone') + '<span>' + COA_PHONE_NUM + '</span></a>' +
        '    </div>' +
        '  </div>' +
        '</div>';
    }
    return '' +
      '<div class="coa-card coa-help-card">' +
      '  <button type="button" class="coa-help-back" data-coa-help-close aria-label="Terug">' +
      '    ' + svgIcon('chevronLeft') + '<span>Terug</span>' +
      '  </button>' +
      '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
      '  <div class="coa-help-body">' +
      '    <div class="coa-help-icon">' + svgIcon('help') + '</div>' +
      '    <h2 class="coa-help-h">Hulp nodig?</h2>' +
      '    <p class="coa-help-sub">Zo werkt het reserveren in 3 stappen.</p>' +
      '    <ol class="coa-help-steps">' +
      '      <li>' +
      '        <div class="coa-help-step-n">1</div>' +
      '        <div class="coa-help-step-body">' +
      '          <div class="coa-help-step-h">Kies je moment</div>' +
      '          <div class="coa-help-step-p">Pak een dag en tijdslot in de agenda.</div>' +
      '        </div>' +
      '      </li>' +
      '      <li>' +
      '        <div class="coa-help-step-n">2</div>' +
      '        <div class="coa-help-step-body">' +
      '          <div class="coa-help-step-h">Vul je gegevens in</div>' +
      '          <div class="coa-help-step-p">Naam, e-mail of telefoon — voor de bevestiging.</div>' +
      '        </div>' +
      '      </li>' +
      '      <li>' +
      '        <div class="coa-help-step-n">3</div>' +
      '        <div class="coa-help-step-body">' +
      '          <div class="coa-help-step-h">Bevestig je afspraak</div>' +
      '          <div class="coa-help-step-p">Klaar! Je krijgt een bevestiging per mail. Eventueel direct betalen kan ook.</div>' +
      '        </div>' +
      '      </li>' +
      '    </ol>' +
      '    <div class="coa-help-cta-block">' +
      '      <div class="coa-help-cta-h">Kom je er niet uit?</div>' +
      '      <div class="coa-help-cta-p">We helpen je graag, bel ons direct.</div>' +
      '      <a class="coa-help-call" href="tel:' + COA_PHONE_HREF + '">' +
                 svgIcon('phone') + '<span>' + COA_PHONE_NUM + '</span>' +
      '      </a>' +
      '    </div>' +
      '  </div>' +
      '</div>';
  }

  // ai code: maakt extras-labels kort & schoon voor de chip-weergave.
  // Strip "(+199,-)" pricing, "incl./inc. montage" ruis, dubbele spaties.
  function cleanExtraLabel(raw) {
    return String(raw || '')
      .replace(/\s*\(\s*\+?\s*[€]?\s*\d[\d.,\-\s]*\)/g, '') // (+199,-) (€199,-)
      .replace(/\bincl?\.?\s*montage\b/gi, '')              // inc/incl. montage
      .replace(/\s{2,}/g, ' ')
      .trim()
      .replace(/[,;:\-]+$/, '')                              // trailing comma/dash
      .trim();
  }

  function summaryCardHTML(opts) {
    opts = opts || {};
    var info = readProductInfo();
    var compact = opts.compact ? ' coa-compact' : '';

    var metaPins = '';
    var hasPlanningStatus = false;
    if (opts.includeLocation) {
      metaPins += '<div class="coa-meta-pin"><span class="coa-meta-i">' + svgIcon('pin') + '</span>' + escapeHTML(info.location) + '</div>';
    }
    if (opts.includeDateTime && state.selectedDate && state.selectedSlot) {
      hasPlanningStatus = true;
      metaPins += '<div class="coa-meta-pin" data-coa-summary-status="appointment"><span class="coa-meta-i" data-coa-summary-icon="calendar">' + svgIcon('calendarMini') + '</span>' + escapeHTML(formatDateLabel(state.selectedDate)) + ' • ' + escapeHTML(state.selectedSlot) + '</div>';
    }
    if (opts.includeContactPlanning && state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      hasPlanningStatus = true;
      metaPins += '<div class="coa-meta-pin" data-coa-summary-status="contact"><span class="coa-meta-i" data-coa-summary-icon="headset">' + svgIcon('headset') + '</span>' + escapeHTML(CONTACT_SUMMARY_TEXT) + '</div>';
    }

    // ai code: extras als compacte pill-chips ipv lange comma-zin. Iedere
    // upsell krijgt z'n eigen pill, gestript van "inc. montage" / "(+199,-)"
    // ruis zodat 't kort en overzichtelijk blijft, ook bij 3+ opties.
    // Label "Extra optie(s):" prefix zodat klant snapt wat ie ziet.
    var extraBlock = '';
    if (info.extras && info.extras.length) {
      var chips = info.extras.map(function (raw) {
        return '<span class="coa-extra-chip">' + escapeHTML(cleanExtraLabel(raw)) + '</span>';
      }).join('');
      var label = info.extras.length === 1 ? 'Extra optie:' : 'Extra opties:';
      extraBlock = '<div class="coa-summary-extras">' +
                   '  <span class="coa-summary-extras-label">' + label + '</span>' +
                   chips +
                   '</div>';
    }
    var metaBlock = metaPins
      ? '<div class="coa-summary-meta"' + (hasPlanningStatus ? ' style="margin-top:10px!important;"' : '') + '>' + metaPins + '</div>'
      : '';
    // ai code: compacte 1-regel installatielocatie onder de titel.
    // Klein, muted, met pin-icoon — neemt nauwelijks ruimte.
    var locLine = info.location
      ? '<div class="coa-summary-loc">' + svgIcon('pin') + '<span>Installatie: ' + escapeHTML(info.location) + '</span></div>'
      : '';

    // ai code: nieuwe layout — top row = thumb + titel + extras,
    // bottom row = kenteken links / totaalprijs rechts, gescheiden door
    // een dunne lijn. Geen "Geschikt" badge meer.
    // ai code: WK-actie — alleen actief als de live Shopify-prijzen voor de
    // volledige selectie (box + installatie + upsells) een compare_at boven
    // de actuele prijs hebben. Anders exact de oude weergave.
    var sale = coSaleTotals();
    var saleActive = !!(sale && sale.sale);
    var prijsHTML = saleActive
      ? '<span class="coa-prijs-was">' + escapeHTML(formatEuro(sale.compare)) + '</span>' +
        '<span class="coa-prijs">' + escapeHTML(formatEuro(sale.total)) + '</span>'
      : '<span class="coa-prijs">' + escapeHTML(info.priceText || '') + '</span>';

    return '' +
      '<div class="coa-summary' + compact + (saleActive ? ' coa-wk-card' : '') + '">' +
      (saleActive ? '<span class="coa-wk-ribbon">ZOMER ACTIE</span>' : '') +
      '  <div class="coa-summary-top">' +
      '    <div class="coa-summary-thumb">' + productThumbHTML(info.imgSrc) + '</div>' +
      '    <div class="coa-summary-mid">' +
      '      <div class="coa-summary-title">' + escapeHTML(info.title) + '</div>' +
             locLine +
             extraBlock +
             metaBlock +
             (info.kenteken ? '<div class="coa-summary-plate">' + plateHTML(info.kenteken) + '<div class="coa-plate-sub-label">'+ svgIcon('geschikt') + '<span>Geschikt</span></div></div>' : '') +
      '    </div>' +
      '  </div>' +
      '  <div class="coa-summary-bottom">' +
      '    <div class="coa-summary-price">' +
      '      <span class="coa-totaal">totaal</span>' +
             prijsHTML +
      '    </div>' +
      '  </div>' +
      '</div>';
  }

  // ============================================================
  // DATE HELPERS
  // ============================================================

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function fmtDateKey(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }
  function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }
  function dayState(date) {
    var today = startOfDay(new Date());
    var d = startOfDay(date);
    if (d < today) return 'past';
    // ai code: een dag is 'available' alleen als er capaciteit-blokken zijn.
    // Tijdens initial loading (state.slotCounts nog niet gevuld) tonen we
    // 'available' optimistic zodat de UI niet leeg flitst.
    if (!state.slotCounts) return 'available';
    return getSlotsForDate(d).length > 0 ? 'available' : 'closed';
  }
  // ai code: check of een dag nog minstens 1 vrij slot heeft (rekening
  // houdend met bezetting + verstreken tijd voor vandaag). Gebruikt door
  // calendarGridHTML om bv. vandaag te disablen wanneer 17:00+ is geweest.
  function dayHasFreeSlot(date) {
    if (!state.slotCounts) return true; // config nog niet geladen — niet disablen
    if (dayState(date) !== 'available') return false;
    var slots = getSlotsForDate(date);
    if (!slots.length) return false;
    var counts = state.slotCounts || {};
    var maxMap = state.slotMaxMap || {};
    var locDefault = state.maxPerHour || 2;
    var key = fmtDateKey(date);
    for (var i = 0; i < slots.length; i++) {
      var slotKey = key + '|' + slots[i];
      var occ = counts[slotKey] || 0;
      // effective_max uit RPC, anders locatie-default
      var effMax = (typeof maxMap[slotKey] === 'number') ? maxMap[slotKey] : locDefault;
      if (occ < effMax) return true;
    }
    return false;
  }
  function formatDateLabel(dateKey) {
    if (!dateKey) return '';
    var parts = dateKey.split('-');
    var d = new Date(parseInt(parts[0],10), parseInt(parts[1],10) - 1, parseInt(parts[2],10));
    return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + ' ' + MONTH_NAMES[d.getMonth()].toLowerCase();
  }
  function syncViewMonthToDate(dateKey) {
    if (!dateKey) return false;
    var parts = dateKey.split('-');
    var year = parseInt(parts[0], 10);
    var month = parseInt(parts[1], 10);
    if (parts.length !== 3 || !isFinite(year) || !isFinite(month) || month < 1 || month > 12) return false;
    state.viewMonth = new Date(year, month - 1, 1);
    return true;
  }

  // ============================================================
  // STEP 1 — AGENDA
  // ============================================================

  function calendarGridHTML() {
    var month = state.viewMonth || new Date();
    var firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    var startWeekday = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    var daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();

    var today = startOfDay(new Date());
    var selectedDate = state.selectedDate ? new Date(
      parseInt(state.selectedDate.slice(0,4),10),
      parseInt(state.selectedDate.slice(5,7),10) - 1,
      parseInt(state.selectedDate.slice(8,10),10)
    ) : null;

    var html = '';
    for (var spacer = 0; spacer < startWeekday; spacer++) {
      html += '<span class="coa-cal-spacer" aria-hidden="true"></span>';
    }
    for (var day = 1; day <= daysInMonth; day++) {
      var d = new Date(month.getFullYear(), month.getMonth(), day);
      var st = dayState(d);
      var classes = ['coa-cal-cell'];
      var disabled = false;

      // Alle niet-bookable dagen krijgen dezelfde grijze stijl (no more red alarms)
      if (st === 'past' || st === 'closed' || st === 'unavailable') {
        classes.push('coa-out');
        disabled = true;
      } else if (st === 'available' && !dayHasFreeSlot(d)) {
        // ai code: dag is technisch open, maar alle slots zijn vol of in het
        // verleden (vandaag na sluitingstijd). Disablen zodat klanten niet
        // op een dag klikken die leeg is.
        classes.push('coa-out');
        disabled = true;
      }

      if (isSameDay(d, today)) classes.push('coa-blue', 'coa-underline');
      if (selectedDate && isSameDay(d, selectedDate) && !disabled) classes.push('coa-selected');

      var key = fmtDateKey(d);
      var badge = DATE_BADGES[key];
      if (badge && st === 'available') classes.push('coa-sale');

      html += '<button type="button"'
            + ' class="' + classes.join(' ') + '"'
            + ' data-coa-day="' + key + '"'
            + (disabled ? ' disabled' : '')
            + '>'
            + '<span class="coa-cal-num">' + d.getDate() + '</span>'
            + (badge && st === 'available' ? '<span class="coa-sale-pill">' + escapeHTML(badge.label) + '</span>' : '')
            + '</button>';
    }
    return html;
  }

  // ai code: rolling dag-strip — default view voor stap 1 op mobile.
  // Start vanaf de eerste dag MET vrije slots (vandaag als die nog plek
  // heeft, anders de volgende beschikbare dag). Strip rendert 120 dagen
  // vooruit zodat klanten 'infinite' horizontaal kunnen scrollen.
  // Past in de toekomst (>120d) → klant gebruikt "Volledige kalender →".
  var STRIP_DAYS = 120;
  function dateStripHTML() {
    var today = startOfDay(new Date());
    var selDate = state.selectedDate ? new Date(
      parseInt(state.selectedDate.slice(0,4),10),
      parseInt(state.selectedDate.slice(5,7),10) - 1,
      parseInt(state.selectedDate.slice(8,10),10)
    ) : null;

    // ai code: strip-start = eerste beschikbare dag. Fallback today.
    var firstISO = firstAvailableDate ? firstAvailableDate() : null;
    var stripStart = firstISO
      ? new Date(parseInt(firstISO.slice(0,4),10), parseInt(firstISO.slice(5,7),10)-1, parseInt(firstISO.slice(8,10),10))
      : today;

    // ai code: strip altijd lang genoeg om geselecteerde datum te bevatten.
    // Min STRIP_DAYS, max +14 voorbij selected.
    var stripLen = STRIP_DAYS;
    if (selDate) {
      var diffMs = selDate - stripStart;
      var diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays + 14 > stripLen) stripLen = diffDays + 14;
    }

    var html = '';

    html += '<div class="coa-strip-wrap">';
    html += '<div class="coa-strip-header">';
    html += '<div class="coa-strip-h">KIES JE DAG</div>';
    html += '<button type="button" class="coa-strip-link" data-coa-view-toggle="calendar">Volledige kalender →</button>';
    html += '</div>';

    html += '<div class="coa-strip">';
    for (var i = 0; i < stripLen; i++) {
      var d = new Date(stripStart);
      d.setDate(stripStart.getDate() + i);
      var key = fmtDateKey(d);
      var st = dayState(d);
      // ai code: zelfde check als de maand-kalender — een dag die open is maar
      // waar alle slots vol zijn moet OOK in de strip disabled/grijs zijn.
      // (Bug: 12 juni Bleiswijk was vol → desktop grijs, mobiel klikbaar.)
      var disabled = st !== 'available' || !dayHasFreeSlot(d);
      var isToday = isSameDay(d, today);
      var isSelected = selDate && isSameDay(d, selDate);

      var classes = ['coa-strip-day'];
      if (isToday) classes.push('coa-today');
      if (isSelected) classes.push('coa-on');
      if (disabled) classes.push('coa-disabled');

      html += '<button type="button" class="' + classes.join(' ') + '"'
            + ' data-coa-day="' + key + '"'
            + (disabled ? ' disabled' : '')
            + '>'
            + '<span class="coa-strip-wd">' + DAY_NAMES[d.getDay()].slice(0,2) + '</span>'
            + '<span class="coa-strip-num">' + d.getDate() + '</span>'
            + '<span class="coa-strip-mo">' + MONTH_NAMES[d.getMonth()].slice(0,3).toLowerCase() + '</span>'
            + '</button>';
    }
    html += '</div>';
    html += '</div>';
    return html;
  }

  function calendarViewHTML() {
    var month = state.viewMonth || new Date();
    var todayMonth = startOfDay(new Date());
    todayMonth = new Date(todayMonth.getFullYear(), todayMonth.getMonth(), 1);
    var prevDisabled = month <= todayMonth ? ' disabled' : '';

    return '<button type="button" class="coa-strip-back" data-coa-view-toggle="strip">' +
           svgIcon('chevronLeft') + '<span>Terug naar dagen</span></button>' +
           '<div class="coa-cal-head">' +
           '  <button type="button" class="coa-cal-nav" data-coa-cal-prev aria-label="Vorige maand"' + prevDisabled + '>' + svgIcon('chevronLeft') + '</button>' +
           '  <div class="coa-cal-month">' + MONTH_NAMES[month.getMonth()] + ' ' + month.getFullYear() + '</div>' +
           '  <button type="button" class="coa-cal-nav" data-coa-cal-next aria-label="Volgende maand">' + svgIcon('chevronRight') + '</button>' +
           '</div>' +
           '<div class="coa-cal-weekdays">' +
           '  <div>Ma</div><div>Di</div><div>Wo</div><div>Do</div><div>Vr</div><div>Za</div><div>Zo</div>' +
           '</div>' +
           '<div class="coa-cal-grid">' + calendarGridHTML() + '</div>';
  }

  function plannerFrameStylesHTML() {
    return '' +
      '<style data-coa-planner-frame>' +
      '  #co-ai-planner{padding:8px!important;}' +
      '  #co-ai-planner .coa-modal-wrap{max-height:calc(100dvh - 16px)!important;}' +
      '  #co-ai-planner .coa-planner-frame{box-sizing:border-box!important;width:min(760px,calc(100vw - 16px))!important;max-width:min(760px,calc(100vw - 16px))!important;min-width:0!important;flex-shrink:0!important;}' +
      '  #co-ai-planner .coa-planner-frame .coa-body{overflow-y:hidden!important;overscroll-behavior:none!important;}' +
      '  #co-ai-planner .coa-planner-frame .coa-body.coa-body-scrollable{overflow-y:auto!important;-webkit-overflow-scrolling:touch;}' +
      '  @media(min-width:601px){#co-ai-planner .coa-planner-frame{height:min(720px,calc(100dvh - 16px))!important;min-height:min(720px,calc(100dvh - 16px))!important;max-height:min(720px,calc(100dvh - 16px))!important;}}' +
      '  @media(max-width:600px){#co-ai-planner .coa-planner-frame{height:calc(100dvh - 16px)!important;min-height:calc(100dvh - 16px)!important;max-height:calc(100dvh - 16px)!important;}}' +
      '</style>';
  }

  function step2FitStylesHTML() {
    return '' +
      '<style data-coa-step2-fit>' +
      '  #co-ai-planner .coa-step2-responsive .coa-body{overflow-y:hidden!important;overscroll-behavior:none!important;justify-content:flex-start!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-field{height:64px!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-field input{padding:24px 14px 7px!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-step2-date{margin-top:12px!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-step2-h{margin:24px 0 18px!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-form,#co-ai-planner .coa-step2-responsive .coa-form-row{gap:18px!important;}' +
      '  #co-ai-planner .coa-step2-responsive .coa-privacy-note{margin:18px 0 14px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-stepper{padding-bottom:8px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-step2-date{margin-top:0!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-step2-h{margin:7px 0 6px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-form,#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-form-row,#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-form-row.cols-3{gap:8px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-privacy-note{margin:5px 0 0!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-action-row{gap:8px!important;margin-top:6px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step2-date{padding:6px 14px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step2-h{font-size:20px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field{height:52px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field input{padding:19px 12px 4px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field-label{top:6px!important;left:12px!important;right:12px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-privacy-note{font-size:12px!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-action-row{display:grid!important;grid-template-columns:minmax(88px,1fr) minmax(0,2fr)!important;}' +
      '  #co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-btn{min-height:45px!important;height:45px!important;padding:8px 10px!important;}' +
      '  @media(max-width:600px){#co-ai-planner .coa-step2-responsive .coa-step2-date{margin:6px auto 8px!important;}#co-ai-planner .coa-step2-responsive .coa-step2-h{margin:8px 0 14px!important;}#co-ai-planner .coa-step2-responsive .coa-form,#co-ai-planner .coa-step2-responsive .coa-form-row{gap:14px!important;}#co-ai-planner .coa-step2-responsive .coa-field{height:52px!important;}#co-ai-planner .coa-step2-responsive .coa-field input{padding:19px 10px 4px!important;font-size:16px!important;}#co-ai-planner .coa-step2-responsive .coa-field-label{top:6px!important;left:10px!important;right:10px!important;font-size:11.5px!important;}#co-ai-planner .coa-step2-responsive .coa-privacy-note{margin:12px 0 8px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-stepper{padding:7px 44px 8px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-step2-date{margin:2px auto 5px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-step2-h{margin:4px 0 8px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-form,#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-form-row{gap:10px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-tight .coa-privacy-note{margin:6px 0 4px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-stepper{padding:3px 38px 6px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step-circle{width:26px;height:26px;font-size:11px;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step-label{font-size:10px;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step-line{transform:translateY(-8px);}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step2-date{padding:5px 10px!important;font-size:11.5px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step2-date svg{width:14px;height:14px;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-step2-h{font-size:16px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field{height:44px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field input{padding:15px 9px 2px!important;font-size:14px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-field-label{top:4px!important;left:9px!important;right:9px!important;font-size:10px!important;}#co-ai-planner .coa-step2-responsive.coa-step2-fit-compact .coa-privacy-note{font-size:9px!important;line-height:1.15!important;}}' +
      '</style>';
  }

  function contactFlowLayoutStylesHTML() {
    return '' +
      '<style>' +
      '  @media(min-width:601px){#co-ai-planner .coa-contact-flow-step2 .coa-step2-date{margin-top:2px!important;padding:8px 16px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-h{margin:12px 0 8px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-recap-row{min-height:34px;padding-top:4px;padding-bottom:4px;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-card{margin-top:8px;padding-top:8px;padding-bottom:8px;}}' +
      '  #co-ai-planner .coa-contact-flow-card .coa-body{overflow-y:hidden!important;overscroll-behavior:none!important;}' +
      '  #co-ai-planner .coa-contact-flow-card .coa-body.coa-body-scrollable{overflow-y:auto!important;-webkit-overflow-scrolling:touch;}' +
      '  #co-ai-planner .coa-contact-design-card .coa-body{justify-content:flex-start!important;gap:14px;}' +
      '  #co-ai-planner .coa-clear-intro{display:grid;grid-template-columns:48px minmax(0,1fr);gap:16px;align-items:center;padding:20px;border:1.5px solid #DDE3F3;border-radius:16px;background:#fff;}' +
      '  #co-ai-planner .coa-clear-intro h2{margin:0 0 5px;font-size:23px;line-height:1.15;letter-spacing:-.015em;color:#0E1B3D;}' +
      '  #co-ai-planner .coa-clear-intro p{margin:0;color:#606B88;font-size:15px;line-height:1.45;}' +
      '  #co-ai-planner .coa-clear-note{display:inline-flex;align-items:center;gap:7px;margin-top:10px;padding:6px 10px;border-radius:999px;background:#EEF2FF;color:#1B45F0;font-size:13px;font-weight:750;}' +
      '  #co-ai-planner .coa-clear-steps{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin:0;padding:0;list-style:none;}' +
      '  #co-ai-planner .coa-clear-step{display:grid;grid-template-columns:24px minmax(0,1fr);grid-template-rows:auto auto;column-gap:8px;align-content:start;padding:15px 13px 14px;border:1px solid #E1E6F4;border-radius:14px;background:#F8FAFF;min-height:94px;}' +
      '  #co-ai-planner .coa-clear-step-number{grid-column:1;grid-row:1 / span 2;align-self:center;width:24px;height:24px;border-radius:50%;display:grid;place-items:center;background:#1B45F0;color:#fff;font-size:12px;font-weight:800;}' +
      '  #co-ai-planner .coa-clear-step strong{grid-column:2;grid-row:1;display:block;margin-bottom:4px;color:#0E1B3D;font-size:14.5px;line-height:1.25;}' +
      '  #co-ai-planner .coa-clear-step-description{grid-column:2;grid-row:2;display:block;color:#69738F;font-size:12.5px;line-height:1.35;}' +
      '  @media(max-width:600px){#co-ai-planner .coa-contact-design-card .coa-body{gap:12px;}#co-ai-planner .coa-clear-intro{grid-template-columns:40px minmax(0,1fr);gap:12px;align-items:start;padding:16px 14px;}#co-ai-planner .coa-clear-intro .coa-contact-icon{margin-top:2px;}#co-ai-planner .coa-clear-intro h2{font-size:19px;line-height:1.18;}#co-ai-planner .coa-clear-intro p{font-size:13.5px;line-height:1.45;}#co-ai-planner .coa-clear-note{max-width:100%;font-size:11.5px;line-height:1.25;margin-top:9px;padding:6px 10px;white-space:nowrap;}#co-ai-planner .coa-clear-steps{grid-template-columns:1fr;gap:8px;margin-top:10px;}#co-ai-planner .coa-clear-step{grid-template-columns:26px minmax(0,1fr);column-gap:8px;min-height:0;padding:12px;}#co-ai-planner .coa-clear-step-number{width:26px;height:26px;}#co-ai-planner .coa-clear-step strong{font-size:14px;margin-bottom:2px;}#co-ai-planner .coa-clear-step-description{font-size:12px;line-height:1.4;}#co-ai-planner .coa-contact-design-card .coa-action-row{gap:10px;}#co-ai-planner .coa-contact-design-card .coa-action-row .coa-btn{min-height:44px;flex-shrink:0;}}' +
      '  @media(max-width:600px){#co-ai-planner .coa-contact-flow-card .coa-btn{min-height:44px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-steps{margin-bottom:26px;}#co-ai-planner .coa-contact-flow-step1 .coa-action-row{margin-top:0;}#co-ai-planner .coa-contact-design-card .coa-action-row .coa-btn{min-height:48px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary.coa-compact{margin-bottom:0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-sub{margin-bottom:4px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-trust-seal{margin-top:2px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-meta{margin-top:10px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-meta .coa-meta-pin{align-items:center!important;line-height:12px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-meta .coa-meta-i{flex-shrink:0;}}' +
      '  @media(max-width:600px) and (min-height:701px){#co-ai-planner .coa-contact-flow-step1 .coa-clear-steps{flex:1 1 auto;grid-template-rows:repeat(3,minmax(0,1fr));}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step{align-content:center;}}' +
      '  #co-ai-planner .coa-summary-meta .coa-meta-pin[data-coa-summary-status="contact"]{align-items:center!important;}#co-ai-planner .coa-summary-meta [data-coa-summary-icon="headset"]{display:inline-flex!important;align-items:center!important;justify-content:center!important;line-height:0!important;flex-shrink:0;}#co-ai-planner .coa-summary-meta [data-coa-summary-icon="headset"] svg{display:block;}' +
      '  @media(min-width:721px) and (max-height:700px){#co-ai-planner .coa-contact-flow-card .coa-stepper{padding-bottom:8px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-date{margin-top:0!important;padding:6px 14px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-h{margin:7px 0 6px!important;font-size:20px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-form,#co-ai-planner .coa-contact-flow-step2 .coa-form-row,#co-ai-planner .coa-contact-flow-step2 .coa-form-row.cols-3{gap:8px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field{height:52px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field input{padding:19px 12px 4px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field-label{top:6px!important;left:12px!important;right:12px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-privacy-note{font-size:12px!important;margin:5px 0 0!important;}#co-ai-planner .coa-contact-flow-step2 .coa-action-row{margin-top:8px!important;}}' +
      '  @media(min-width:601px) and (max-width:720px) and (max-height:700px){#co-ai-planner .coa-contact-flow-card .coa-action-row{display:grid!important;grid-template-columns:minmax(140px,1fr) 2fr!important;gap:10px!important;}#co-ai-planner .coa-contact-flow-card .coa-stepper{padding-bottom:8px!important;}}' +
      '  @media(max-width:600px) and (max-height:700px){#co-ai-planner .coa-contact-flow-step1 .coa-stepper{padding:8px 44px;}#co-ai-planner .coa-contact-flow-step1 .coa-body{gap:8px;}#co-ai-planner .coa-contact-flow-step1 .coa-summary{padding:8px 12px;}#co-ai-planner .coa-contact-flow-step1 .coa-summary-top{grid-template-columns:42px minmax(0,1fr);}#co-ai-planner .coa-contact-flow-step1 .coa-summary-thumb{width:42px;height:42px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-intro{padding:11px 12px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-intro h2{font-size:18px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-note{margin-top:6px;padding:5px 8px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-steps{gap:6px;margin-top:9px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step{padding-top:9px;padding-bottom:9px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step:before{top:8px;}#co-ai-planner .coa-contact-flow-step1 .coa-action-row{gap:8px;}#co-ai-planner .coa-contact-flow-step1 .coa-action-row .coa-btn{min-height:45px!important;}}' +
      '  @media(max-width:600px) and (max-height:680px){#co-ai-planner{padding:8px!important;}#co-ai-planner .coa-contact-flow-card{height:calc(100dvh - 16px)!important;min-height:0!important;max-height:calc(100dvh - 16px)!important;padding:8px 10px!important;}#co-ai-planner .coa-contact-flow-card .coa-stepper{padding:3px 38px 6px!important;}#co-ai-planner .coa-contact-flow-card .coa-step-circle{width:26px;height:26px;font-size:11px;}#co-ai-planner .coa-contact-flow-card .coa-step-label{font-size:10px;}#co-ai-planner .coa-contact-flow-card .coa-step-line{transform:translateY(-8px);}#co-ai-planner .coa-contact-flow-card .coa-action-row{display:grid!important;grid-template-columns:minmax(88px,1fr) minmax(0,2fr)!important;gap:8px!important;margin-top:6px!important;}#co-ai-planner .coa-contact-flow-card .coa-btn{min-height:45px!important;height:45px!important;padding:8px 10px!important;font-size:13px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-body{gap:5px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-summary{padding:5px 9px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-summary-top{grid-template-columns:36px minmax(0,1fr)!important;gap:8px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-summary-thumb{width:36px!important;height:36px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-summary-title{font-size:13px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-summary-loc{font-size:11px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-intro{grid-template-columns:32px minmax(0,1fr);gap:8px;padding:8px 9px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-contact-icon{width:32px;height:32px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-intro h2{font-size:16px;line-height:1.15;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-intro p{font-size:11.5px;line-height:1.3;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-note{margin-top:4px;padding:4px 7px;font-size:10px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-steps{gap:4px;margin:4px 0 0!important;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step{grid-template-columns:21px minmax(0,1fr);column-gap:7px;padding:6px 7px!important;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step-number{width:21px;height:21px;font-size:10px;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step strong{font-size:12.5px;line-height:1.15;}#co-ai-planner .coa-contact-flow-step1 .coa-clear-step-description{font-size:10.5px;line-height:1.2;}#co-ai-planner .coa-contact-flow-step1 .coa-action-row .coa-btn{min-height:45px!important;height:45px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-date{margin:0 auto 3px!important;padding:5px 10px!important;font-size:11.5px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-date svg{width:14px;height:14px;}#co-ai-planner .coa-contact-flow-step2 .coa-step2-h{margin:2px 0 4px!important;font-size:16px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-form,#co-ai-planner .coa-contact-flow-step2 .coa-form-row{gap:4px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field{height:44px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field input{padding:15px 9px 2px!important;font-size:14px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-field-label{top:4px!important;left:9px!important;right:9px!important;font-size:10px!important;}#co-ai-planner .coa-contact-flow-step2 .coa-privacy-note{font-size:9px!important;line-height:1.15!important;margin:3px 0 0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary{padding:6px 9px!important;gap:5px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-top{grid-template-columns:36px minmax(0,1fr)!important;gap:8px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-thumb{width:36px!important;height:36px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-h{margin:4px 0 2px!important;font-size:15px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-sub{font-size:11px!important;margin:0 0 3px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-options{gap:5px!important;margin:3px 0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-card{padding:6px 8px!important;min-height:52px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-icon{width:30px!important;height:30px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-title{font-size:12.5px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-pay-sub-line{font-size:10.5px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-trust-seal{margin-top:2px!important;padding:4px 7px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-trust-seal-label{font-size:9px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-trust-seal-logos img{height:14px!important;max-height:14px!important;max-width:26px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-terms-note{font-size:9px!important;line-height:1.2!important;margin:3px 2px 0!important;}#co-ai-planner .coa-contact-flow-step4 .coa-success-check{margin:0!important;}#co-ai-planner .coa-contact-flow-step4 .coa-success-check svg{width:38px!important;height:38px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-step4-h{font-size:16px!important;margin:0 0 2px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-step4-sub{font-size:10.5px!important;line-height:1.25!important;margin:0 0 5px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-recap-row{grid-template-columns:24px 70px minmax(0,1fr)!important;min-height:24px!important;padding:2px 7px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-recap-label,#co-ai-planner .coa-contact-flow-step4 .coa-recap-value{font-size:10.5px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-card{grid-template-columns:30px minmax(0,1fr) auto!important;gap:6px!important;margin-top:5px!important;padding:5px 7px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-icon{width:30px;height:30px;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-h{font-size:12px;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-p{font-size:10.5px;}#co-ai-planner .coa-contact-flow-step4 .coa-contact-btn{padding:7px 8px;font-size:11px;}#co-ai-planner .coa-contact-flow-step4 .coa-btn.coa-block{margin-top:6px!important;}}' +
      '  @media(max-width:600px) and (max-height:680px){#co-ai-planner .coa-contact-flow-step3 .coa-summary-mid{margin-left:0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-title,#co-ai-planner .coa-contact-flow-step3 .coa-summary-loc{margin-left:0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-meta{width:calc(100% - 68px)!important;max-width:calc(100% - 68px)!important;gap:3px!important;margin-top:4px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-price{right:8px!important;bottom:6px!important;}#co-ai-planner .coa-contact-flow-step3 .coa-prijs{font-size:18px!important;}#co-ai-planner .coa-contact-flow-step4 .coa-recap-row{column-gap:6px!important;grid-template-columns:20px 78px minmax(0,1fr)!important;}}' +
      '  @media(max-width:600px){#co-ai-planner .coa-contact-flow-step3 .coa-summary-mid{margin-left:0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-title,#co-ai-planner .coa-contact-flow-step3 .coa-summary-loc{margin-left:0!important;}#co-ai-planner .coa-contact-flow-step3 .coa-summary-meta{width:calc(100% - 68px)!important;max-width:calc(100% - 68px)!important;}}' +
      '</style>';
  }

  function step1HTML() {
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      var contactError = state.formErrorMsg
        ? '<div class="coa-form-error" role="alert" style="background:#FEF2F2;border:1px solid #DC2626;color:#7F1D1D;padding:10px 12px;border-radius:8px;margin:0;font-size:13px;line-height:1.4;">' + escapeHTML(state.formErrorMsg) + '</div>'
        : '';
      return '' +
        '<div class="coa-card coa-planner-frame coa-contact-design-card coa-contact-flow-card coa-contact-flow-step1" data-coa-contact-design="clear">' +
           plannerFrameStylesHTML() +
           contactFlowLayoutStylesHTML() +
        '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
           stepperHTML(1) +
        '  <div class="coa-body">' +
             summaryCardHTML({ compact: true }) +
             contactError +
        '    <section class="coa-clear-intro" aria-labelledby="co-ai-planner-title">' +
        '      <div class="coa-contact-icon" aria-hidden="true">' + svgIcon('user') + '</div>' +
        '      <div>' +
        '        <h2 id="co-ai-planner-title">' + escapeHTML(CONTACT_COPY.step1Heading) + '</h2>' +
        '        <p>Je hoeft nu nog geen datum te kiezen.</p>' +
        '        <div class="coa-clear-note">Contact binnen één werkdag</div>' +
        '      </div>' +
        '    </section>' +
        '    <ol class="coa-clear-steps" aria-label="Zo werkt persoonlijk plannen">' +
        '      <li class="coa-clear-step"><span class="coa-clear-step-number" aria-hidden="true">1</span><strong>Rond je aanvraag af</strong><span class="coa-clear-step-description">Vul je gegevens in en kies je betaalwijze.</span></li>' +
        '      <li class="coa-clear-step"><span class="coa-clear-step-number" aria-hidden="true">2</span><strong>Wij nemen contact op</strong><span class="coa-clear-step-description">Binnen één werkdag nemen we contact met je op.</span></li>' +
        '      <li class="coa-clear-step"><span class="coa-clear-step-number" aria-hidden="true">3</span><strong>Samen plannen</strong><span class="coa-clear-step-description">We kiezen een geschikt installatiemoment.</span></li>' +
        '    </ol>' +
        '  </div>' +
        '  <div class="coa-action-row">' +
        '    <button type="button" class="coa-btn coa-ghost" data-coa-help>Hulp nodig?</button>' +
        '    <button type="button" class="coa-btn coa-primary" data-coa-next>' +
        '      <span>' + escapeHTML(CONTACT_COPY.step1Cta) + '</span>' + svgIcon('arrowRight') +
        '    </button>' +
        '  </div>' +
           safeFooterHTML() +
        '</div>';
    }
    // ai code: tijdens initial loading (slotCounts nog niet binnen) tonen we
    // alleen een loading state — geen kalender. Voorkomt "alle dagen open"
    // flash voordat de echte data binnen is.
    if (state.slotCounts === null || state.slotCounts === undefined) {
      return '' +
        '<div class="coa-card coa-planner-frame">' +
           plannerFrameStylesHTML() +
        '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
           stepperHTML(1) +
        '  <div class="coa-body">' +
             summaryCardHTML() +
        '    <div class="coa-no-slots" style="padding:40px 16px;text-align:center;">' +
        '      <div style="opacity:0.6;font-size:13px;">Beschikbaarheid laden…</div>' +
        '    </div>' +
        '  </div>' +
        '  <div class="coa-action-row">' +
        '    <button type="button" class="coa-btn coa-ghost" data-coa-help>Hulp nodig?</button>' +
        '    <button type="button" class="coa-btn coa-primary" disabled>' +
        '      <span>Volgende stap</span>' + svgIcon('arrowRight') +
        '    </button>' +
        '  </div>' +
        '</div>';
    }
    // ai code: validatie van persisted selectedDate — als de datum (bv. door
    // wegklikken/heropenen) inmiddels geen vrije slots meer heeft, clear 'm
    // en pak automatisch de eerstvolgende beschikbare. Voorkomt "Alle slots
    // op deze datum zijn vol" boodschap bij heropenen.
    if (state.selectedDate) {
      var checkParts = state.selectedDate.split('-');
      var checkDate = new Date(parseInt(checkParts[0],10), parseInt(checkParts[1],10)-1, parseInt(checkParts[2],10));
      if (!dayHasFreeSlot(checkDate)) {
        state.selectedDate = null;
        state.selectedSlot = null;
      }
    }
    if (!state.selectedDate) {
      var firstAvail = firstAvailableDate(); // returnt al een YYYY-MM-DD string
      if (firstAvail) {
        state.selectedDate = firstAvail;
        syncViewMonthToDate(firstAvail);
      }
    }

    var slotsHTML = '';
    if (state.selectedDate) {
      var parts = state.selectedDate.split('-');
      var selDate = new Date(parseInt(parts[0],10), parseInt(parts[1],10)-1, parseInt(parts[2],10));
      var slots = getSlotsForDate(selDate);
      var counts = state.slotCounts || {};
      var maxMap = state.slotMaxMap || {};
      var locDefault = state.maxPerHour || 2;

      var availableSlots = slots.filter(function (t) {
        var slotKey = state.selectedDate + '|' + t;
        var occ = counts[slotKey] || 0;
        var effMax = (typeof maxMap[slotKey] === 'number') ? maxMap[slotKey] : locDefault;
        return occ < effMax;
      });

      if (slots.length === 0) {
        slotsHTML = '<div class="coa-no-slots">Geen beschikbare tijdsloten op deze datum.</div>';
      } else if (availableSlots.length === 0) {
        slotsHTML = '<div class="coa-no-slots">Alle tijdsloten op deze datum zijn vol.</div>';
      } else {
        // ai code: alleen beschikbare slots renderen (volle slots worden verborgen, niet doorgestreept)
        slotsHTML = '<div class="coa-time-row">';
        for (var i = 0; i < availableSlots.length; i++) {
          var t = availableSlots[i];
          var active = state.selectedSlot === t ? ' coa-active' : '';
          var classNames = 'coa-time-btn' + active;
          slotsHTML += '<button type="button" class="' + classNames + '"'
                     + ' data-coa-slot="' + t + '"'
                     + '>' + t + '</button>';
        }
        slotsHTML += '</div>';
      }
    }

    // ai code: knop is in stap 1 ALTIJD actief — klik zonder datum/tijd
    // toont een shake-notice (zie click-handler) i.p.v. een grijze knop.
    var nextDisabled = '';

    // ai code: layout-logic:
    //   Mobiel (<720px): strip is default, calendar is "Volledige kalender →"
    //     toggle. In calendar mode geen slots — na dag-keuze switch je terug
    //     naar strip met slot view.
    //   Desktop/tablet (≥720px): ALTIJD volledige kalender + slots eronder.
    //     Geen strip, geen toggle.
    var isMobileLayout = (window.innerWidth || 1024) < 720;
    var dateAreaHTML;
    var showSlots;
    if (!isMobileLayout) {
      // Desktop / tablet — calendar + slots, altijd
      dateAreaHTML = calendarViewHTML();
      showSlots = true;
    } else {
      dateAreaHTML = state.viewMode === 'calendar'
        ? calendarViewHTML()
        : dateStripHTML();
      showSlots = state.viewMode !== 'calendar';
    }

    return '' +
      '<div class="coa-card coa-planner-frame">' +
         plannerFrameStylesHTML() +
      '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
         stepperHTML(1) +
      // ai code: geen statische .coa-body-scrollable — JS adjustBodyScrollable()
      // beslist op basis van ECHTE overflow of scroll nodig is. Voorkomt
      // "1mm scroll" wanneer content toevallig perfect past.
      '  <div class="coa-body">' +
           summaryCardHTML({ compact: true }) +
           dateAreaHTML +
      (showSlots && state.selectedDate
        ? '<div class="coa-install-row">' + svgIcon('clock') + '<span>Installatie tijd:</span><span class="coa-install-val">±2 uur</span></div>' + slotsHTML
        : (showSlots && !state.selectedDate ? slotsHTML : '')
      ) +
      '  </div>' +
      '  <div class="coa-action-row">' +
      '    <button type="button" class="coa-btn coa-ghost" data-coa-help>Hulp nodig?</button>' +
      '    <button type="button" class="coa-btn coa-primary" data-coa-next' + nextDisabled + '>' +
      '      <span>Volgende stap</span>' + svgIcon('arrowRight') +
      '    </button>' +
      '  </div>' +
         safeFooterHTML() +
      '</div>';
  }

  // ============================================================
  // STEP 2 — GEGEVENS
  // ============================================================

  function step2HTML() {
    var f = state.form;
    var isPersonalContact = state.flowMode === FLOW_MODE.PERSONAL_CONTACT;
    var dateLine = isPersonalContact
      ? CONTACT_COPY.step2Chip
      : (state.selectedDate
        ? formatDateLabel(state.selectedDate) + ' • ' + (state.selectedSlot || '')
        : '');
    return '' +
      '<div class="coa-card coa-planner-frame coa-step2-responsive' + (isPersonalContact ? ' coa-contact-flow-card coa-contact-flow-step2' : '') + '">' +
         plannerFrameStylesHTML() +
      (isPersonalContact ? contactFlowLayoutStylesHTML() : '') +
         step2FitStylesHTML() +
      '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
         stepperHTML(2) +
      '  <div class="coa-body">' +
      '    <div class="coa-step2-date">' + svgIcon(isPersonalContact ? 'phone' : 'calendarMini') + '<span>' + escapeHTML(dateLine) + '</span></div>' +
      '    <h2' + (isPersonalContact ? ' id="co-ai-planner-title"' : '') + ' class="coa-step2-h">Laat je gegevens achter</h2>' +
      // ai code: error banner verwijderd op verzoek — rode field borders zijn genoeg
      // (banner triggerde scroll op mobile als hij verscheen).
      '    <div class="coa-form">' +
      '      <div class="coa-form-row cols-2">' +
               fieldHTML('voornaam', 'Voornaam', f.voornaam, 'Jan') +
               fieldHTML('achternaam', 'Achternaam', f.achternaam, 'Janssen', 'text', { required: true }) +
      '      </div>' +
             fieldHTML('bedrijf', 'Bedrijf (optioneel)', f.bedrijf, 'Vul in bij zakelijke aankoop') +
             fieldHTML('email', 'E-mailadres', f.email, 'voorbeeld@email.nl', 'email', { markRequired: true }) +
             fieldHTML('tel', 'Telefoonnummer', f.tel, '06 12345678', 'tel', { markRequired: true }) +
      '    </div>' +
      '  </div>' +
      // ai code: privacy-note BUITEN .coa-body — anders wordt 'ie geclipt door
      // overflow:hidden op smalle/korte schermen (Samsung S21 etc). Hier zit
      // 'ie altijd zichtbaar boven de actie-knoppen, ongeacht viewport.
      '  <div class="coa-privacy-note">' +
      '    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>' +
      '    <span>Je gegevens worden enkel gebruikt voor administratieve doeleinden.</span>' +
      '  </div>' +
      '  <div class="coa-action-row">' +
      '    <button type="button" class="coa-btn coa-ghost" data-coa-prev>Vorige</button>' +
      '    <button type="button" class="coa-btn coa-primary" data-coa-next>' +
      '      <span>Volgende</span>' + svgIcon('arrowRight') +
      '    </button>' +
      '  </div>' +
         safeFooterHTML() +
      '</div>';
  }

  function fieldHTML(key, label, value, placeholder, type, opts) {
    type = type || 'text';
    opts = opts || {};
    var hasError = state.formErrors && state.formErrors[key];
    var autocomplete = { voornaam: 'given-name', achternaam: 'family-name', bedrijf: 'organization', email: 'email', tel: 'tel' };
    var inputAttrs = ' data-coa-field="' + key + '"'
                   + ' value="' + escapeHTML(value) + '"'
                   + ' placeholder="' + escapeHTML(placeholder) + '"'
                   + ' autocomplete="' + (autocomplete[key] || 'off') + '"';
    if (autocomplete[key]) inputAttrs += ' name="' + escapeHTML(key) + '"';
    if (opts.readonly) inputAttrs += ' readonly';
    if (opts.required) inputAttrs += ' required';
    var classes = ['coa-field'];
    if (hasError) classes.push('coa-field-error');
    if (opts.autoFilled) classes.push('coa-readonly-wrap');
    // ai code: showMark = visueel sterretje zonder HTML5 required (handig voor
    // velden waar bv. OF email OF telefoon verplicht is — beide hebben *).
    var showMark = opts.required || opts.markRequired;
    var labelHTML = escapeHTML(label) + (showMark ? '<span class="coa-required-mark"> *</span>' : '');
    return '<label class="' + classes.join(' ') + '">' +
           '  <span class="coa-field-label">' + labelHTML + '</span>' +
           '  <input type="' + type + '"' + inputAttrs + '>' +
           '</label>';
  }

  // ai code: validatie voor step 2 → step 3.
  // Markeert ontbrekende fields in state.formErrors (object key→bool) zodat
  // de UI ze inline rood kan kleuren, en geeft een banner-tekst terug.
  // Achternaam verplicht. Email OF telefoonnummer verplicht (min 1 van 2).
  function validateStep2() {
    var f = state.form;
    var errors = {};
    var msgs = [];
    if (!f.achternaam || !f.achternaam.trim()) {
      errors.achternaam = true;
      msgs.push('achternaam');
    }
    var hasEmail = f.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim());
    var hasTel = f.tel && f.tel.replace(/\D/g, '').length >= 9;
    if (!hasEmail && !hasTel) {
      errors.email = true;
      errors.tel = true;
      msgs.push('e-mail of telefoonnummer');
    }
    state.formErrors = errors;
    if (msgs.length === 0) return null;
    return 'Vul de ontbrekende gegevens in: ' + msgs.join(' en ') + '.';
  }

  // ============================================================
  // STEP 3 — BETALING
  // ============================================================

  function payOptionHTML(opts) {
    var selected = opts.selected ? ' coa-selected' : '';
    var radioOn = opts.selected ? ' coa-on' : '';
    var dot = opts.selected ? '<span class="coa-radio-dot"></span>' : '';
    var subtitle = opts.subtitle ? '<div class="coa-pay-sub-line">' + escapeHTML(opts.subtitle) + '</div>' : '';

    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      return '<div role="radio" aria-checked="' + (opts.selected ? 'true' : 'false') + '" tabindex="' + (opts.selected ? '0' : '-1') + '" class="coa-pay-card' + selected + '" data-coa-pay="' + opts.value + '">' +
             '  <div class="coa-pay-radio"><span class="coa-radio' + radioOn + '">' + dot + '</span></div>' +
             '  <div class="coa-pay-icon">' + opts.icon + '</div>' +
             '  <div class="coa-pay-body">' +
             '    <div class="coa-pay-title">' + escapeHTML(opts.title) + '</div>' +
                  subtitle +
             '  </div>' +
             '</div>';
    }

    return '<div class="coa-pay-card' + selected + '" data-coa-pay="' + opts.value + '">' +
           '  <div class="coa-pay-radio"><span class="coa-radio' + radioOn + '">' + dot + '</span></div>' +
           '  <div class="coa-pay-icon">' + opts.icon + '</div>' +
           '  <div class="coa-pay-body">' +
           '    <div class="coa-pay-title">' + escapeHTML(opts.title) + '</div>' +
                subtitle +
           '  </div>' +
           '</div>';
  }

  function step3HTML() {
    var isPersonalContact = state.flowMode === FLOW_MODE.PERSONAL_CONTACT;
    var na = payOptionHTML({
      value: 'na', selected: state.payment === 'na', icon: svgIcon('clock'),
      title: 'Na installatie betalen',
      subtitle: 'Betaal op locatie na de installatie'
    });
    var nu = payOptionHTML({
      value: 'nu', selected: state.payment === 'nu', icon: svgIcon('card'),
      title: 'Nu betalen',
      subtitle: 'Direct afgerond'
    });

    var nextLabel = isPersonalContact
      ? CONTACT_COPY.step3Cta
      : (state.payment === 'nu' ? 'Bevestigen en betalen' : 'Afronden');

    // ai code: trust-seal — vertrouwen wekken vlak voor de "Nu betalen" keuze.
    // CDN-paden komen 1-op-1 uit het thema's t4s trust seal block, alleen Nederlandse
    // bank/wallet logos die voor CarOptions relevant zijn.
    var trustSealHTML =
      '    <div class="coa-trust-seal" aria-label="Geaccepteerde betaalmethoden">' +
      '      <span class="coa-trust-seal-label">Betaal veilig met</span>' +
      '      <div class="coa-trust-seal-logos">' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/ideal.svg?v=1752489844" alt="iDEAL" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/klarna.svg?v=1752489845" alt="Klarna" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/american-express.svg?v=1752489845" alt="American Express" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/maestro.svg?v=1752489845" alt="Maestro" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/mastercard.svg?v=1752489845" alt="Mastercard" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0932/1512/9949/files/visa.svg?v=1752489845" alt="Visa" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0727/7477/9171/files/paypal_payment.png?v=1764279275" alt="PayPal" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0727/7477/9171/files/apple_payment.png?v=1764279322" alt="Apple Pay" loading="lazy" />' +
      '        <img src="https://cdn.shopify.com/s/files/1/0727/7477/9171/files/bancontact_payment.png?v=1764279340" alt="Bancontact" loading="lazy" />' +
      '      </div>' +
      '    </div>';

    // ai code: error banner — verschijnt als confirmBooking 0 rijen
    // patcht (RLS denial) of network error. Voorheen werd state.formErrorMsg
    // wel gezet maar niet gerenderd → klant zag "knop doet niks". Audit C1.
    var errBanner = state.formErrorMsg
      ? '    <div class="coa-form-error" role="alert" style="background:#FEF2F2;border:1px solid #DC2626;color:#7F1D1D;padding:10px 12px;border-radius:8px;margin-bottom:12px;font-size:13px;line-height:1.4;">' +
        '      <strong>Let op:</strong> ' + escapeHTML(state.formErrorMsg) +
        '    </div>'
      : '';
    var finalizationBanner = isPersonalContact && state.finalizationMessage
      ? '    <div role="status" style="background:' + (state.finalizationStatus === 'failed' ? '#FEF2F2' : '#EFF6FF') + ';border:1px solid ' + (state.finalizationStatus === 'failed' ? '#DC2626' : '#93C5FD') + ';color:' + (state.finalizationStatus === 'failed' ? '#7F1D1D' : '#1E3A8A') + ';padding:10px 12px;border-radius:8px;margin-bottom:12px;font-size:13px;line-height:1.4;">' + escapeHTML(state.finalizationMessage) + '</div>'
      : '';

    return '' +
      '<div class="coa-card coa-planner-frame' + (isPersonalContact ? ' coa-contact-flow-card coa-contact-flow-step3' : '') + '">' +
         plannerFrameStylesHTML() +
      (isPersonalContact ? contactFlowLayoutStylesHTML() : '') +
      '  <button type="button" class="coa-close coa-circle" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
         stepperHTML(3) +
      // ai code: geen statische scrollable class — JS bepaalt 'm dynamisch
      '  <div class="coa-body coa-body-short">' +
           errBanner +
           finalizationBanner +
           summaryCardHTML({ compact: true, includeDateTime: true, includeContactPlanning: true }) +
      '    <h2' + (isPersonalContact ? ' id="co-ai-planner-title"' : '') + ' class="coa-pay-h">Wanneer wil je betalen?</h2>' +
      '    <p class="coa-pay-sub">Kies de betaaloptie die het beste bij jou past.</p>' +
      '    <div class="coa-pay-options"' + (isPersonalContact ? ' role="radiogroup" aria-labelledby="co-ai-planner-title"' : '') + '>' + na + nu + '</div>' +
           trustSealHTML +
      '  </div>' +
      '  <div class="coa-action-row">' +
      '    <button type="button" class="coa-btn coa-ghost" data-coa-prev>Vorige</button>' +
      '    <button type="button" class="coa-btn coa-primary" data-coa-next' + (state.finalizationInFlight ? ' disabled' : '') + '>' +
      '      <span>' + escapeHTML(state.finalizationInFlight ? 'Aanvraag verwerken…' : nextLabel) + '</span>' + svgIcon('arrowRight') +
      '    </button>' +
      '  </div>' +
      '  <div class="coa-terms-note">' +
      '    Door op <strong>' + escapeHTML(nextLabel) + '</strong> te klikken ga je akkoord met onze algemene voorwaarden.' +
      '  </div>' +
         safeFooterHTML() +
      '</div>';
  }

  // ============================================================
  // STEP 4 — AFRONDEN
  // ============================================================

  function recapRowHTML(icon, label, value, isLast) {
    var lastCls = isLast ? ' coa-last' : '';
    return '<div class="coa-recap-row' + lastCls + '">' +
           '  <div class="coa-recap-icon">' + icon + '</div>' +
           '  <div class="coa-recap-label">' + escapeHTML(label) + '</div>' +
           '  <div class="coa-recap-value">' + value + '</div>' +
           '</div>';
  }

  function step4HTML() {
    var info = readProductInfo();
    var brandModel = info.vehicleBrand
      ? info.vehicleBrand + (info.model ? ' ' + info.model : '')
      : info.title.replace(/^CarPlayBox voor\s*/i, '') || info.model || '—';
    var paymentLabel = state.payment === 'na' ? 'Na installatie betalen' : 'Nu betalen';
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      var contactLocation = state.locationFullAddress
        ? (state.locationCompany ? state.locationCompany + ', ' : '') + state.locationFullAddress
        : info.location;
      var contactRows = [
        [svgIcon('car'), 'Auto', escapeHTML(brandModel)],
        [svgIcon('box'), 'Dienst', info.serviceLabel || 'CarPlayBox Installatie'],
        [svgIcon('pin'), 'Locatie', escapeHTML(contactLocation)],
        [svgIcon('phone'), 'Planning', 'Persoonlijk afgestemd'],
        [svgIcon('cardInline'), 'Betaalmethode', escapeHTML(paymentLabel)],
        [svgIcon('euro'), 'Totaal', '<span class="coa-euro-blue">' + escapeHTML(info.priceText || '') + '</span> <span class="coa-euro-sub">inclusief btw</span>']
      ];
      var contactRecap = '<div class="coa-recap">';
      for (var contactIndex = 0; contactIndex < contactRows.length; contactIndex++) {
        contactRecap += recapRowHTML(
          contactRows[contactIndex][0],
          contactRows[contactIndex][1],
          contactRows[contactIndex][2],
          contactIndex === contactRows.length - 1
        );
      }
      contactRecap += '</div>';
      return '' +
        '<div class="coa-card coa-planner-frame coa-contact-flow-card coa-contact-flow-step4">' +
           plannerFrameStylesHTML() +
           contactFlowLayoutStylesHTML() +
        '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
           stepperHTML(4) +
        '  <div class="coa-body coa-body-short coa-body-scrollable">' +
        '    <div class="coa-success-check">' +
        '      <svg viewBox="0 0 96 96" width="96" height="96"><circle cx="48" cy="48" r="44" fill="none" stroke="#1B45F0" stroke-width="3"/><polyline points="30 49 43 62 66 36" fill="none" stroke="#1B45F0" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '    </div>' +
        '    <h2 id="co-ai-planner-title" class="coa-step4-h">' + escapeHTML(CONTACT_COPY.successHeading) + '</h2>' +
        '    <p class="coa-step4-sub">Onze planner neemt binnen één werkdag contact met je op om samen een geschikt moment te kiezen.</p>' +
             contactRecap +
        '    <div class="coa-contact-card"><div class="coa-contact-icon">' + svgIcon('headset') + '</div><div><div class="coa-contact-h">Vragen?</div><div class="coa-contact-p">We helpen je graag verder.</div></div><button type="button" class="coa-contact-btn" data-coa-help>Neem contact op</button></div>' +
        '  </div>' +
        '  <button type="button" class="coa-btn coa-primary coa-block" data-coa-finish>Sluiten</button>' +
        '</div>';
    }
    var dateTime = state.selectedDate
      ? formatDateLabel(state.selectedDate) + ' om ' + (state.selectedSlot || '')
      : '—';
    // ai code: ook in stap 4 recap chip-vorm — schoner bij 2-3 upsells
    var extras = info.extras.length
      ? info.extras.map(function (e) {
          return '<span class="coa-extra-chip coa-extra-chip-recap">' + escapeHTML(cleanExtraLabel(e)) + '</span>';
        }).join('')
      : '—';
    var price = info.priceText || '';
    // Volledig adres van de geselecteerde locatie (geladen in loadAvailability)
    var locationLabel = state.locationFullAddress
      ? (state.locationCompany ? state.locationCompany + ', ' : '') + state.locationFullAddress
      : escapeHTML(info.location);

    var rows = [
      [svgIcon('car'), 'Auto', escapeHTML(brandModel)],
      [svgIcon('box'), 'Dienst', info.serviceLabel || 'CarPlayBox Installatie'],
      // ai code: extras is al gerenderde chip-HTML (of '—'), niet nog 'n keer escapen
      [svgIcon('camera'), 'Extra optie', extras === '—' ? '—' : '<div class="coa-recap-extras">' + extras + '</div>'],
      [svgIcon('pin'), 'Locatie', escapeHTML(locationLabel)],
      [svgIcon('calendar'), 'Datum & tijd', escapeHTML(dateTime)],
      [svgIcon('cardInline'), 'Betaalmethode', escapeHTML(paymentLabel)],
      [svgIcon('euro'), 'Totaal', '<span class="coa-euro-blue">' + escapeHTML(price) + '</span> <span class="coa-euro-sub">inclusief btw</span>']
    ];

    var recapHTML = '<div class="coa-recap">';
    for (var i = 0; i < rows.length; i++) {
      recapHTML += recapRowHTML(rows[i][0], rows[i][1], rows[i][2], i === rows.length - 1);
    }
    recapHTML += '</div>';

    var isPayNow = state.payment === 'nu';
    var headline = isPayNow
      ? 'Bevestigd — laatste stap: betaling'
      : 'Je afspraak is bevestigd';
    var subline = isPayNow
      ? 'Klik hieronder om naar de beveiligde checkout te gaan en je afspraak definitief te maken.'
      : 'Je ontvangt via de mail en/of WhatsApp je afspraak-bevestiging.';
    var primaryBtn = isPayNow
      ? '<button type="button" class="coa-btn coa-primary coa-block" data-coa-checkout>Betalen</button>'
      : '<button type="button" class="coa-btn coa-primary coa-block" data-coa-finish>Sluiten</button>';
    // ai code: "Terug naar productpagina" knop verwijderd — overbodig naast Sluiten,
    // gebruiker is na bevestiging klaar en kan de popup gewoon dichtklikken.
    var secondaryBtn = '';

    return '' +
      '<div class="coa-card coa-planner-frame">' +
         plannerFrameStylesHTML() +
      '  <button type="button" class="coa-close" data-coa-close aria-label="Sluiten">' + svgIcon('close') + '</button>' +
         stepperHTML(4) +
      '  <div class="coa-body coa-body-short coa-body-scrollable">' +
      '    <div class="coa-success-check">' +
      '      <svg viewBox="0 0 96 96" width="96" height="96">' +
      '        <circle cx="48" cy="48" r="44" fill="none" stroke="#1B45F0" stroke-width="3"/>' +
      '        <polyline points="30 49 43 62 66 36" fill="none" stroke="#1B45F0" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>' +
      '      </svg>' +
      '    </div>' +
      '    <h2 class="coa-step4-h">' + escapeHTML(headline) + '</h2>' +
      '    <p class="coa-step4-sub">' + subline + '</p>' +
           recapHTML +
      '    <div class="coa-contact-card">' +
      '      <div class="coa-contact-icon">' + svgIcon('headset') + '</div>' +
      '      <div>' +
      '        <div class="coa-contact-h">Vragen?</div>' +
      '        <div class="coa-contact-p">We helpen je graag verder.</div>' +
      '      </div>' +
      '      <button type="button" class="coa-contact-btn" data-coa-help>Neem contact op</button>' +
      '    </div>' +
      '  </div>' +
         primaryBtn +
         secondaryBtn +
      '</div>';
  }

  // ============================================================
  // RENDER + EVENT WIRING
  // ============================================================

  function render(operation) {
    if (operation && !currentFlowOperation(operation)) return false;
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return false;
    var content = popup.querySelector('[data-coa-content]');
    if (!content) return false;
    // ai code: bewaar scroll-positie zodat klanten die op stap 1 omlaag scrollen
    // en een slot klikken hun positie niet verliezen (popup re-rendert hele body).
    var prevBody = content.querySelector('.coa-body, .coa-scroll');
    var prevScrollTop = prevBody ? prevBody.scrollTop : 0;
    // ai code: ook horizontale scroll van de date-strip bewaren — anders
    // springt 'ie naar links bij elke render (bv. na dag-keuze of slot-klik).
    var prevStrip = content.querySelector('.coa-strip');
    var prevStripScrollLeft = prevStrip ? prevStrip.scrollLeft : 0;

    var html = '';
    if (state.helpOpen) {
      html = helpHTML();
    } else if (state.step === 1) html = step1HTML();
    else if (state.step === 2) html = step2HTML();
    else if (state.step === 3) html = step3HTML();
    else if (state.step === 4) html = step4HTML();
    if (operation && !currentFlowOperation(operation)) return false;
    content.innerHTML = html;
    if (prevScrollTop > 0) {
      var newBody = content.querySelector('.coa-body, .coa-scroll');
      if (newBody) newBody.scrollTop = prevScrollTop;
    }
    if (prevStripScrollLeft > 0) {
      var newStrip = content.querySelector('.coa-strip');
      if (newStrip) newStrip.scrollLeft = prevStripScrollLeft;
    }
    // ai code: enable scroll ONLY when content écht overflowt (>12px).
    // Anders zie je een "1mm scroll" op apparaten waar alles toevallig net past
    // — lelijk + nodeloos. requestAnimationFrame zodat layout berekend is.
    if (window.requestAnimationFrame) {
      requestAnimationFrame(function () {
        if (operation && !currentFlowOperation(operation)) return;
        fitStep2Layout(content);
        adjustBodyScrollable(content);
        if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT
            && popup.classList.contains('is-open')
            && (typeof popup.contains !== 'function' || !popup.contains(document.activeElement))) {
          focusContactDialog(popup);
        }
      });
    } else {
      setTimeout(function () {
        if (operation && !currentFlowOperation(operation)) return;
        fitStep2Layout(content);
        adjustBodyScrollable(content);
        if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT
            && popup.classList.contains('is-open')
            && (typeof popup.contains !== 'function' || !popup.contains(document.activeElement))) {
          focusContactDialog(popup);
        }
      }, 0);
    }
    return true;
  }

  // ai code: voegt/verwijdert .coa-body-scrollable o.b.v. ECHTE overflow.
  // Drempel 12px = onder dit verschil hoef je niet te kunnen scrollen
  // (kleine sub-pixel/border verschillen tellen niet als "scroll nodig").
  function adjustBodyScrollable(rootEl) {
    if (!rootEl) return;
    var body = rootEl.querySelector('.coa-body');
    if (!body) return;
    var overflow = body.scrollHeight - body.clientHeight;
    // Personal-contact past op alle ondersteunde desktop-, tablet- en mobile-
    // viewports zonder interne scroll. De directe kalenderflow behoudt haar
    // bestaande overflowroute.
    var isPersonalContact = state.flowMode === FLOW_MODE.PERSONAL_CONTACT;
    var isStep2 = state.step === 2;
    var isStep4 = state.step === 4;
    var shouldScroll = !isPersonalContact && !isStep2 && (isStep4 || overflow > 12);
    body.classList.toggle('coa-body-scrollable', shouldScroll);
  }

  function contactBodyHasClipping(body) {
    if (!body) return false;
    if (body.scrollHeight > body.clientHeight + 1) return true;
    if (typeof body.getBoundingClientRect !== 'function') return false;
    var bodyRect = body.getBoundingClientRect();
    var children = body.children || [];
    for (var i = 0; i < children.length; i++) {
      if (typeof children[i].getBoundingClientRect !== 'function') continue;
      var childRect = children[i].getBoundingClientRect();
      if (childRect.top < bodyRect.top - 0.5 || childRect.bottom > bodyRect.bottom + 0.5) return true;
    }
    return false;
  }

  function fitStep2Layout(rootEl) {
    if (!rootEl || state.step !== 2) return;
    var card = rootEl.querySelector('.coa-step2-responsive');
    var body = rootEl.querySelector('.coa-body');
    if (!card || !body || typeof window === 'undefined') return;

    card.classList.remove('coa-step2-fit-tight', 'coa-step2-fit-compact');
    var initialOverflow = contactBodyHasClipping(body);
    if (initialOverflow) card.classList.add('coa-step2-fit-tight');
    var tightOverflow = initialOverflow && contactBodyHasClipping(body);
    var plan = getStep2FitPlan({
      initialOverflow: initialOverflow,
      tightOverflow: tightOverflow
    });
    card.classList.toggle('coa-step2-fit-tight', plan.tight);
    if (plan.compact) card.classList.add('coa-step2-fit-compact');
  }

  function scheduleStep2Fit() {
    if (step2FitScheduled) return;
    step2FitScheduled = true;
    var run = function () {
      step2FitScheduled = false;
      if (state.step !== 2) return;
      var popup = document.getElementById(POPUP_ID);
      var content = popup && popup.querySelector('[data-coa-content]');
      if (!content) return;
      fitStep2Layout(content);
      adjustBodyScrollable(content);
    };
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(run);
    } else {
      setTimeout(run, 0);
    }
  }

  function goToStep(n) {
    if (state.completed === true && n !== 4) return;
    // ai code: funnel tracking — completed van vorige stap + viewed van nieuwe stap.
    // Alleen vooruit-gang als 'completed' tellen (back-button telt niet als drop).
    var prev = state.step;
    if (typeof prev === 'number' && n < prev) advanceFlowGeneration(state);
    if (typeof prev === 'number' && n > prev) {
      trackFunnel('step_' + prev + '_completed');
    }
    var viewedEvent = getViewedFunnelEvent(state.flowMode, n);
    if (viewedEvent) trackFunnel(viewedEvent);
    state.step = n;
    state.completed = n === 4;

    // ai code: bij terug naar stap 1 (via Vorige of stepper-jump) — sync
    // viewMonth met selectedDate zodat de gekozen dag direct zichtbaar is
    // in de juiste maand. Anders zou je terugkomen in de huidige maand en
    // denken dat je selectie verloren is.
    if (n === 1 && state.selectedDate) {
      var p = state.selectedDate.split('-');
      state.viewMonth = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, 1);
    }

    persistState();
    render();
    var popup = document.getElementById(POPUP_ID);
    if (popup) popup.scrollTop = 0;
    // ai code: ook interne scroll-area resetten bij echte stap-wissel
    var body = popup && popup.querySelector('.coa-body, .coa-scroll');
    if (body) body.scrollTop = 0;

    // ai code: bij stap 1 — scroll geselecteerde dag in beeld (strip
    // horizontaal centreren, calendar cell verticaal in view).
    if (n === 1) {
      setTimeout(function () {
        var popupNow = document.getElementById(POPUP_ID);
        if (!popupNow) return;
        var strip = popupNow.querySelector('.coa-strip');
        var stripSel = popupNow.querySelector('.coa-strip-day.coa-on');
        if (strip && stripSel) {
          var target = stripSel.offsetLeft - (strip.clientWidth / 2) + (stripSel.offsetWidth / 2);
          try { strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' }); }
          catch (_) { strip.scrollLeft = Math.max(0, target); }
        }
        var calSel = popupNow.querySelector('.coa-cal-cell.coa-selected');
        if (calSel && typeof calSel.scrollIntoView === 'function') {
          calSel.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 60);
    }

    // ai code: stap 3 op smalle/korte schermen (S21 etc) — de betaal-cards
    // vallen onder de fold. Doe een SLOW, gentle auto-scroll naar onderaan
    // zodat klant:
    //   1) eerst de summary card en prijs ziet (eerste ~900ms — geen scroll)
    //   2) daarna langzaam meeloopt naar de betaalopties + afronden-knop
    // Op brede schermen waar alles al past is dit een no-op (geen scrollHeight overlap).
    if (n === 3) {
      setTimeout(function () {
        var popupNow = document.getElementById(POPUP_ID);
        if (!popupNow) return;
        var body3 = popupNow.querySelector('.coa-body');
        if (!body3) return;
        var maxScroll = body3.scrollHeight - body3.clientHeight;
        if (maxScroll <= 4) return; // alles past al, geen scroll nodig
        // Smooth scroll naar bottom. CSS `scroll-behavior: smooth` werkt op .coa-body
        // sinds 're trager scrollt op mobile dan de default `behavior: 'smooth'` van scrollTo.
        try { body3.scrollTo({ top: maxScroll, behavior: 'smooth' }); }
        catch (_) { body3.scrollTop = maxScroll; }
      }, 900);
    }

    // ai code: confetti viering bij afspraak-bevestiging (betaal-later) — niet bij pay-now,
    // daar is checkout de volgende stap, geen "klaar"-moment.
    if (n === 4 && state.flowMode !== FLOW_MODE.PERSONAL_CONTACT && state.payment !== 'nu') {
      try { setTimeout(fireConfetti, 80); } catch (e) { /* nooit fataal */ }
    }
  }

  // ai code: lichtgewicht confetti — ~60 deeltjes, één canvas, auto-cleanup na ~3.5s.
  // Geen library, geen network. Werkt op elk modern device.
  function fireConfetti() {
    if (!window.requestAnimationFrame) return;
    var c = document.createElement('canvas');
    c.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:2147483647';
    document.body.appendChild(c);
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var W = window.innerWidth, H = window.innerHeight;
    c.width = W * dpr; c.height = H * dpr;
    var ctx = c.getContext('2d');
    if (!ctx) { c.remove(); return; }
    ctx.scale(dpr, dpr);

    var COLORS = ['#1B45F0', '#22C55E', '#F59E0B', '#EC4899', '#A855F7', '#06B6D4'];
    var pieces = [];
    function spawn(x) {
      for (var i = 0; i < 35; i++) {
        pieces.push({
          x: x, y: H * 0.35,
          vx: (Math.random() - 0.5) * 10,
          vy: -7 - Math.random() * 7,
          size: 5 + Math.random() * 5,
          rot: Math.random() * Math.PI * 2,
          spin: (Math.random() - 0.5) * 0.35,
          color: COLORS[(Math.random() * COLORS.length) | 0],
          shape: Math.random() < 0.5 ? 'rect' : 'circle'
        });
      }
    }
    // Twee bursts vanuit twee zijden voor een vol effect
    spawn(W * 0.3);
    spawn(W * 0.7);

    var start = performance.now();
    function frame(now) {
      var dt = now - start;
      ctx.clearRect(0, 0, c.width, c.height);
      var fade = dt > 2200 ? Math.max(0, 1 - (dt - 2200) / 1300) : 1;
      var alive = false;
      for (var i = 0; i < pieces.length; i++) {
        var p = pieces[i];
        p.vx *= 0.99;
        p.vy = p.vy * 0.99 + 0.35;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.spin;
        if (p.y > H + 30 || fade <= 0) continue;
        alive = true;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.fillStyle = p.color;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.shape === 'rect') ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.45);
        else { ctx.beginPath(); ctx.arc(0, 0, p.size * 0.35, 0, Math.PI * 2); ctx.fill(); }
        ctx.restore();
      }
      if (alive && dt < 4500) requestAnimationFrame(frame);
      else c.remove();
    }
    requestAnimationFrame(frame);
  }

  function resetStateAfterFinish() {
    resetFlowBoundState(state);
    state.flowMode = FLOW_MODE.LEGACY_DIRECT;
    state.flowIdentity = null;
    state.form = createStorefrontCustomerForm();
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) {}
  }

  // ai code: stap 1 zonder datum/tijd → nette notice met Apple-achtige shake.
  // Verdwijnt vanzelf bij de eerstvolgende re-render (datum/tijd-keuze).
  function coShakeStep1Notice(msg) {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return;
    var row = popup.querySelector('.coa-action-row');
    if (!row || !row.parentNode) return;
    var n = popup.querySelector('.coa-step1-notice');
    if (!n) {
      n = document.createElement('div');
      n.className = 'coa-step1-notice';
      n.setAttribute('role', 'alert');
      row.parentNode.insertBefore(n, row);
    }
    n.textContent = msg;
    n.classList.remove('coa-shake');
    void n.offsetWidth; // reflow zodat de animatie opnieuw afspeelt
    n.classList.add('coa-shake');
  }

  function getContactDialogFocusables(popup) {
    if (!popup || typeof popup.querySelectorAll !== 'function') return [];
    return Array.prototype.slice.call(popup.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )).filter(function (element) {
      return element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true';
    });
  }

  function focusContactDialog(popup) {
    if (state.flowMode !== FLOW_MODE.PERSONAL_CONTACT || !popup || !popup.classList.contains('is-open')) return;
    var focusables = getContactDialogFocusables(popup);
    if (focusables.length > 0 && typeof focusables[0].focus === 'function') focusables[0].focus();
  }

  function trapContactDialogFocus(ev, popup) {
    if (ev.key !== 'Tab' || state.flowMode !== FLOW_MODE.PERSONAL_CONTACT
        || !popup || !popup.classList.contains('is-open')) return false;
    var focusables = getContactDialogFocusables(popup);
    if (focusables.length === 0) {
      ev.preventDefault();
      return true;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    var active = document.activeElement;
    if (ev.shiftKey && (active === first || !popup.contains(active))) {
      ev.preventDefault();
      last.focus();
      return true;
    }
    if (!ev.shiftKey && (active === last || !popup.contains(active))) {
      ev.preventDefault();
      first.focus();
      return true;
    }
    return false;
  }

  function handleContactPaymentKeydown(ev) {
    if (state.finalizationAttempt) return;
    if (state.flowMode !== FLOW_MODE.PERSONAL_CONTACT) return;
    var target = ev.target && ev.target.closest
      ? ev.target.closest('[data-coa-pay][role="radio"]')
      : null;
    if (!target) return;
    var popup = document.getElementById(POPUP_ID);
    if (!popup || !popup.classList.contains('is-open')
        || typeof popup.querySelectorAll !== 'function') return;
    var options = Array.prototype.slice.call(
      popup.querySelectorAll('[data-coa-pay][role="radio"]')
    );
    var currentIndex = options.indexOf(target);
    var nextIndex = getContactPaymentKeyboardTarget(ev.key, currentIndex, options.length);
    if (nextIndex === null) return;
    ev.preventDefault();
    var value = options[nextIndex].getAttribute('data-coa-pay');
    state.payment = value;
    persistState();
    render();
    requestAnimationFrame(function () {
      var rendered = popup.querySelector('[data-coa-pay="' + value + '"][role="radio"]');
      if (rendered && typeof rendered.focus === 'function') rendered.focus();
    });
  }

  function handlePopupClick(ev) {
    var popup = document.getElementById(POPUP_ID);
    if (!popup || !popup.classList.contains('is-open')) return;

    var t = ev.target;
    if (!t || !t.closest) return;
    var match = function (sel) { return t.closest(sel); };

    // Sluiten via X-knop
    var closeBtn = match('[data-coa-close]');
    if (closeBtn) {
      ev.preventDefault();
      closePopup();
      return;
    }

    // Sluiten via backdrop (alleen als directe target de backdrop zelf is)
    if (t.matches && t.matches('[data-coa-close-bg]')) {
      ev.preventDefault();
      closePopup();
      return;
    }

    // Help / contact — open de in-popup help-pagina (3 stappen + bel-CTA)
    if (match('[data-coa-help]')) {
      ev.preventDefault();
      state.helpOpen = true;
      render();
      return;
    }
    // Terug uit de help-pagina naar de normale stap-flow
    if (match('[data-coa-help-close]')) {
      ev.preventDefault();
      state.helpOpen = false;
      render();
      return;
    }

    // Unknown/finalized outcomes may only retry the same persisted request.
    if (state.finalizationAttempt && !state.completed && !match('[data-coa-next]')) {
      ev.preventDefault();
      return;
    }

    // ai code: stap 1 — view toggle (strip ↔ volledige kalender)
    var toggleBtn = match('[data-coa-view-toggle]');
    if (toggleBtn) {
      ev.preventDefault();
      var newMode = toggleBtn.getAttribute('data-coa-view-toggle');
      state.viewMode = newMode;
      // Bij switch naar kalender: toon de maand van de gekozen dag. Zonder
      // selectie valt de kalender terug op de huidige maand.
      if (newMode === 'calendar' && !syncViewMonthToDate(state.selectedDate)) {
        var currentMonth = new Date();
        state.viewMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      }
      render();
      // ai code: lightweight fade-in animatie op de body na switch
      var popupAnim = document.getElementById(POPUP_ID);
      var bodyAnim = popupAnim && popupAnim.querySelector('.coa-body');
      if (bodyAnim) {
        bodyAnim.classList.remove('coa-anim-toggle');
        // force reflow zodat animation opnieuw afspeelt
        void bodyAnim.offsetWidth;
        bodyAnim.classList.add('coa-anim-toggle');
        setTimeout(function () {
          if (bodyAnim) bodyAnim.classList.remove('coa-anim-toggle');
        }, 260);
      }
      // ai code: bij switch naar calendar → scroll zodat de hele kalender in beeld komt.
      if (newMode === 'calendar') {
        setTimeout(function () {
          var popupCal = document.getElementById(POPUP_ID);
          var grid = popupCal && popupCal.querySelector('.coa-cal-grid');
          var bodyCal = popupCal && popupCal.querySelector('.coa-body');
          if (grid && bodyCal && typeof grid.scrollIntoView === 'function') {
            // ai code: scroll naar de kalender-header zodat ook de maand-nav zichtbaar is.
            var head = popupCal.querySelector('.coa-cal-head');
            (head || grid).scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        }, 30);
      }
      return;
    }

    // Stap 1: kalender prev
    var calPrev = match('[data-coa-cal-prev]');
    if (calPrev) {
      ev.preventDefault();
      if (calPrev.disabled) return;
      state.viewMonth = new Date(state.viewMonth.getFullYear(), state.viewMonth.getMonth() - 1, 1);
      var prevCalendarOperation = beginCurrentFlowOperation('calendar');
      render(prevCalendarOperation);
      Promise.all([
        loadSlotCounts(prevCalendarOperation),
        loadAvailability(prevCalendarOperation)
      ]).then(function (results) {
        var stale = results.some(function (result) { return result && result.kind === 'identity_changed'; });
        if (!stale && currentFlowOperation(prevCalendarOperation)) render(prevCalendarOperation);
      });
      return;
    }

    // Stap 1: kalender next
    if (match('[data-coa-cal-next]')) {
      ev.preventDefault();
      state.viewMonth = new Date(state.viewMonth.getFullYear(), state.viewMonth.getMonth() + 1, 1);
      var nextCalendarOperation = beginCurrentFlowOperation('calendar');
      render(nextCalendarOperation);
      Promise.all([
        loadSlotCounts(nextCalendarOperation),
        loadAvailability(nextCalendarOperation)
      ]).then(function (results) {
        var stale = results.some(function (result) { return result && result.kind === 'identity_changed'; });
        if (!stale && currentFlowOperation(nextCalendarOperation)) render(nextCalendarOperation);
      });
      return;
    }

    // Stap 1: dag-keuze
    var dayBtn = match('[data-coa-day]');
    if (dayBtn) {
      if (dayBtn.disabled) return;
      ev.preventDefault();
      state.selectedDate = dayBtn.getAttribute('data-coa-day');
      // ai code: alleen MOBIEL switcht van calendar → strip na dag-keuze
      // (zodat slots zichtbaar worden). Desktop/tablet blijft in calendar +
      // slots renderen sowieso onder de kalender.
      var isMobileClick = (window.innerWidth || 1024) < 720;
      var switchedToStrip = false;
      if (isMobileClick && state.viewMode === 'calendar') {
        state.viewMode = 'strip';
        switchedToStrip = true;
      }
      persistState();
      render();
      // ai code: na render: (1) bij mobile switch naar strip → horizontaal
      // scrollen zodat gekozen dag gecentreerd staat. (2) verticaal scrollen
      // zodat slots zichtbaar zijn, ook bij 2-3 rijen.
      setTimeout(function () {
        var popupSlot = document.getElementById(POPUP_ID);
        if (!popupSlot) return;
        if (switchedToStrip) {
          var strip = popupSlot.querySelector('.coa-strip');
          var selBtn = popupSlot.querySelector('.coa-strip-day.coa-on');
          if (strip && selBtn) {
            var target = selBtn.offsetLeft - (strip.clientWidth / 2) + (selBtn.offsetWidth / 2);
            try {
              strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
            } catch (_) {
              strip.scrollLeft = Math.max(0, target);
            }
          }
        }
        var slotRow = popupSlot.querySelector('.coa-time-row');
        if (slotRow && typeof slotRow.scrollIntoView === 'function') {
          var installRow = popupSlot.querySelector('.coa-install-row');
          (installRow || slotRow).scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }, switchedToStrip ? 60 : 30);
      return;
    }

    // Stap 1: tijdslot
    var slotBtn = match('[data-coa-slot]');
    if (slotBtn) {
      ev.preventDefault();
      state.selectedSlot = slotBtn.getAttribute('data-coa-slot');
      persistState();
      render();
      return;
    }

    // Stap 3: betaaloptie
    var payBtn = match('[data-coa-pay]');
    if (payBtn) {
      ev.preventDefault();
      state.payment = payBtn.getAttribute('data-coa-pay');
      persistState();
      render();
      return;
    }

    // Volgende stap
    var nextBtn = match('[data-coa-next]');
    if (nextBtn) {
      if (nextBtn.disabled) return;
      ev.preventDefault();
      if (state.step === 1 || state.step === 2) {
        if (state.step === 2 && _saveLeadDebounce) {
          clearTimeout(_saveLeadDebounce);
          _saveLeadDebounce = null;
        }
        runStepOrchestration({
          state: state,
          isIdentityCurrent: isFlowIdentityCurrent,
          hasDate: !!state.selectedDate,
          hasSlot: !!state.selectedSlot,
          validateStep2: validateStep2,
          saveDeadlineMs: 30000,
          syncForm: syncCustomerFormFromDom,
          saveLead: function (saveOptions) {
            state.formErrors = {};
            state.formErrorMsg = null;
            return saveLead(saveOptions);
          },
          triggerSaveLead: triggerSaveLead,
          goToStep: goToStep,
          setBusy: function (busy) { nextBtn.disabled = busy; },
          onMissingSelection: function () {
            coShakeStep1Notice(!state.selectedDate
              ? 'Kies eerst een datum en tijd om verder te gaan.'
              : 'Kies ook nog een tijd om verder te gaan.');
          },
          onValidationError: function (message) {
            state.formErrorMsg = message;
            render();
            var popup2 = document.getElementById(POPUP_ID);
            var body2 = popup2 && popup2.querySelector('.coa-scroll, .coa-body');
            if (body2) body2.scrollTop = 0;
          },
          onSaveError: function () {
            state.formErrorMsg = 'Je gegevens konden nog niet worden opgeslagen. Probeer het opnieuw.';
            render();
          },
          onIdentityMismatch: handleFlowIdentityMismatch
        });
        return;
      }
      if (state.step === 3) {
        var finalizationPromise = runFinalizationOrchestration({
          state: state,
          isIdentityCurrent: isFlowIdentityCurrent,
          requestContactFinalization: finalizeContactBooking,
          confirmBooking: confirmBooking,
          startCheckout: function (expectedIdentity) {
            nextBtn.innerText = 'Bezig met doorsturen...';
            return checkoutAndRedirect({
              triggerButton: nextBtn,
              retryLabel: CONTACT_COPY.step3Cta,
              expectedIdentity: expectedIdentity
            });
          },
          goToStep: goToStep,
          track: function (eventName) {
            trackFunnel(eventName, {
              payment: eventName === 'booking_confirmed'
                ? state.payment
                : (state.payment === 'nu' ? 'pay_now' : 'after_install')
            });
          },
          onDirectFailure: function () {
            state.formErrorMsg = 'Oeps, het bevestigen lukte niet. Probeer opnieuw of bel ons op ' + COA_PHONE_NUM + ' — uw slot is nog gereserveerd.';
            render();
            var popupEl = document.getElementById(POPUP_ID);
            var bodyEl = popupEl && popupEl.querySelector('.coa-scroll, .coa-body');
            if (bodyEl) bodyEl.scrollTop = 0;
          },
          onIdentityMismatch: handleFlowIdentityMismatch,
          render: render
        });
        if (state.finalizationInFlight) {
          nextBtn.disabled = true;
          if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
            nextBtn.innerText = 'Aanvraag verwerken…';
          }
        }
        finalizationPromise.finally(function () {
          nextBtn.disabled = false;
        });
      }
      return;
    }

    // ai code: clickable stepper — spring naar visuele stap. Alleen back-nav:
    // jumpTo target moet <= huidige interne stap zijn, anders no-op.
    var jumpEl = match('[data-coa-step-jump]');
    if (jumpEl) {
      ev.preventDefault();
      // Een bevestigde afspraak is definitief. Terugspringen vanuit stap 4
      // kon dezelfde appointment opnieuw bevestigen en een tweede order maken.
      if (state.completed === true || state.step === 4) return;
      var jumpTo = parseInt(jumpEl.getAttribute('data-coa-step-jump'), 10);
      if (canNavigateBackward(state.step, jumpTo, state.completed)) {
        goToStep(jumpTo);
      }
      return;
    }

    // Vorige stap
    if (match('[data-coa-prev]')) {
      ev.preventDefault();
      if (state.completed === true || state.step === 4) return;
      if (canNavigateBackward(state.step, state.step - 1, state.completed)) goToStep(state.step - 1);
      return;
    }

    // Stap 4: afronden — reset alles + sluit (alleen "Na installatie betalen" flow)
    if (match('[data-coa-finish]')) {
      ev.preventDefault();
      resetStateAfterFinish();
      closePopup();
      return;
    }

    // Stap 4: betalen → cart + redirect naar Shopify checkout (alleen "Nu betalen")
    var checkoutBtn = match('[data-coa-checkout]');
    if (checkoutBtn) {
      ev.preventDefault();
      checkoutBtn.disabled = true;
      checkoutBtn.innerText = 'Bezig met doorsturen...';
      checkoutAndRedirect();
      return;
    }

    // Stap 4: terug naar productpagina (zelfde gedrag als afronden)
    if (match('[data-coa-back-to-product]')) {
      ev.preventDefault();
      resetStateAfterFinish();
      closePopup();
      return;
    }
  }

  function syncCustomerFormFromDom() {
    var popup = document.getElementById(POPUP_ID);
    if (!popup || state.step !== 2) return;
    popup.querySelectorAll('input[data-coa-field]').forEach(function (field) {
      var key = field.getAttribute('data-coa-field');
      if (Object.prototype.hasOwnProperty.call(state.form, key)) state.form[key] = field.value;
    });
    persistState();
  }

  function handleFormInput(ev) {
    if (state.finalizationAttempt) return;
    var t = ev.target;
    if (!t || !t.matches || !t.matches('[data-coa-field]')) return;
    var key = t.getAttribute('data-coa-field');
    if (key in state.form) {
      state.form[key] = t.value;
      persistState();
      // ai code: clear error van dit veld zodra user typt
      // (geen banner meer — rode field border is genoeg)
      if (state.formErrors && state.formErrors[key]) {
        delete state.formErrors[key];
        if (Object.keys(state.formErrors).length === 0) state.formErrorMsg = null;
        // Toggle class direct ipv re-render (zou focus verliezen)
        var field = t.closest && t.closest('.coa-field');
        if (field) field.classList.remove('coa-field-error');
      }
      // ai code: realtime save naar Supabase op elk veld — debounced + queue
      scheduleSaveLead();
    }
  }

  // ai code: debounced + queued saveLead.
  // - debounce: 700ms na laatste keystroke om te voorkomen dat we elke letter een PATCH sturen
  // - queue: maximaal 1 save in flight, daarna 1 rerun als er nieuwe input was tijdens de save
  var _saveLeadDebounce = null;
  var _saveLeadInFlight = false;
  var _saveLeadDirty = false;

  function scheduleSaveLead() {
    if (_saveLeadDebounce) clearTimeout(_saveLeadDebounce);
    _saveLeadDebounce = setTimeout(function () {
      _saveLeadDebounce = null;
      triggerSaveLead();
    }, 700);
  }

  function triggerSaveLead() {
    if (state.completed === true || state.finalizationAttempt) return;
    // Directe planning vereist een gekozen datum/tijd. Contactplanning heeft
    // pas na een bevestigde Gegevens-save een lead; daarna mogen wijzigingen
    // zonder afspraakvelden via dezelfde versiebeveiligde opslag worden bewaard.
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      if (!state.appointmentId || !state.editToken) return;
    } else if (!state.selectedDate || !state.selectedSlot) return;
    if (_saveLeadInFlight) { _saveLeadDirty = true; return; }
    _saveLeadInFlight = true;
    _saveLeadDirty = false;
    saveLead().finally(function () {
      _saveLeadInFlight = false;
      if (_saveLeadDirty) {
        _saveLeadDirty = false;
        scheduleSaveLead();
      }
    });
  }

  // ============================================================
  // POPUP OPEN/CLOSE
  // ============================================================

  function openPopup() {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return;
    var currentMode = getCurrentFlowMode();
    var currentIdentity = getCurrentFlowIdentity();
    if (!currentIdentity || currentMode === FLOW_MODE.DEFAULT_CART) {
      syncAllButtons();
      return;
    }
    var binding = bindFlowIdentity(state, currentIdentity);
    state.flowMode = currentMode;
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      _lastContactTrigger = document.activeElement && typeof document.activeElement.focus === 'function'
        ? document.activeElement
        : null;
    }
    advanceFlowGeneration(state);
    var openOperation = beginCurrentFlowOperation('open');
    if (binding.changed) persistState();
    if (state.flowMode === FLOW_MODE.PERSONAL_CONTACT) {
      state.selectedDate = null;
      state.selectedSlot = null;
    }
    // ai code: default viewMode per device.
    //   Mobile (<720px) → 'strip'   — geen scroll, alles in één oogopslag
    //   Desktop (>=720px) → 'calendar' — volledige maand past prima
    state.viewMode = (window.innerWidth || 1024) < 720 ? 'strip' : 'calendar';
    // ai code: help-pagina altijd dicht starten bij nieuwe open
    state.helpOpen = false;

    // ai code: behoud selectedDate/Slot bij heropenen — ongeacht op welke
    // stap je weggeklikt was. Alleen wissen als de datum in het verleden ligt
    // (anders bookt klant per ongeluk op een verlopen datum).
    if (state.selectedDate) {
      var parts = state.selectedDate.split('-');
      var sel = new Date(parseInt(parts[0],10), parseInt(parts[1],10)-1, parseInt(parts[2],10));
      if (sel < startOfDay(new Date())) {
        state.selectedDate = null;
        state.selectedSlot = null;
      }
    }

    // ai code: sync viewMonth NA de selectedDate cleanup.
    // Als er een selectedDate is (bv. 4 juni terwijl we nu in mei zijn),
    // open de kalender direct op die maand zodat de gekozen dag zichtbaar
    // is — anders zou de user terugkomen in de huidige maand en denken
    // dat z'n selectie verloren is gegaan.
    if (!syncViewMonthToDate(state.selectedDate) && !state.viewMonth) {
      var today = new Date();
      state.viewMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    // ai code: body-scroll lock — iOS Safari respecteert overflow:hidden
    // niet, dus position:fixed met saved scrollY.
    _savedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + _savedScrollY + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // ai code: render content, dan in volgende frame .is-open zetten
    // zodat de CSS-transition cleanly start vanuit de from-state.
    render(openOperation);
    requestAnimationFrame(function () {
      if (!currentFlowOperation(openOperation)) return;
      popup.classList.add('is-open');
      focusContactDialog(popup);
    });

    // ai code: funnel tracking — fresh session per popup-open. Genereer nieuwe
    // sessionId zodat elke open een nieuwe customer-journey is.
    if (!commitCurrentFlowOperation(openOperation, function () {
      state.funnelSessionId = null;
    })) return;
    trackFunnel('popup_opened', null, openOperation);
    var openedViewedEvent = getViewedFunnelEvent(state.flowMode, state.step);
    if (openedViewedEvent) trackFunnel(openedViewedEvent, null, openOperation);

    // ai code: alle data parallel laden + ÉÉN gecombineerde re-render NA
    // de open-animatie (220ms transition). Voorkomt content-shifts tijdens
    // de fade-in. firstAvailableDate gebruikt verse data.
    var openedAt = Date.now();
    var ANIM_MS = 240;
    var finalRender = function () {
      if (!currentFlowOperation(openOperation)) return;
      render(openOperation);
      // ai code: na open-render: zorg dat de gekozen dag altijd ZICHTBAAR is.
      //   - Strip mode (mobile): horizontaal scrollen naar geselecteerde dag.
      //   - Calendar mode: viewMonth staat al op de juiste maand (zie openPopup
      //     boven), dus de cell is sowieso zichtbaar — alleen smooth scroll
      //     binnen de body om hem in beeld te brengen.
      //   - Daarna: scroll naar tijdsloten zodat onderste rij niet wegvalt.
      setTimeout(function () {
        if (!currentFlowOperation(openOperation)) return;
        var popupOpen = document.getElementById(POPUP_ID);
        if (!popupOpen) return;

        // Strip-view: gekozen dag centreren in horizontale scroll
        var strip = popupOpen.querySelector('.coa-strip');
        var stripSel = popupOpen.querySelector('.coa-strip-day.coa-on');
        if (strip && stripSel) {
          var target = stripSel.offsetLeft - (strip.clientWidth / 2) + (stripSel.offsetWidth / 2);
          try { strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' }); }
          catch (_) { strip.scrollLeft = Math.max(0, target); }
        }

        // Calendar-view: gekozen cell pulse-flash zodat user ziet dat hij geselecteerd is
        var calSel = popupOpen.querySelector('.coa-cal-cell.coa-selected');
        if (calSel && typeof calSel.scrollIntoView === 'function') {
          // Geen scroll nodig — viewMonth synct al, maar zorg dat row in beeld is
          calSel.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }

        // Verticaal: tijdsloten in beeld
        var slotRow = popupOpen.querySelector('.coa-time-row');
        if (slotRow && typeof slotRow.scrollIntoView === 'function') {
          var installRow = popupOpen.querySelector('.coa-install-row');
          (installRow || slotRow).scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }, 40);
    };
    var popupDataPromise = state.flowMode === FLOW_MODE.PERSONAL_CONTACT
      ? Promise.resolve()
      : Promise.all([
          loadLocationConfig(openOperation),
          loadSlotCounts(openOperation),
          loadAvailability(openOperation)
        ]);
    popupDataPromise.then(function (results) {
      if (!currentFlowOperation(openOperation)) return;
      if (Array.isArray(results)) {
        var stale = results.some(function (result) { return result && result.kind === 'identity_changed'; });
        if (stale) return;
      }
      if (state.flowMode !== FLOW_MODE.PERSONAL_CONTACT && !state.selectedDate) {
        var first = firstAvailableDate();
        if (first && !commitCurrentFlowOperation(openOperation, function () {
          state.selectedDate = first;
          syncViewMonthToDate(first);
        })) return;
      }
      if (!currentFlowOperation(openOperation)) return;
      var elapsed = Date.now() - openedAt;
      var wait = Math.max(0, ANIM_MS - elapsed);
      setTimeout(finalRender, wait);
    }).catch(function () {
      if (!currentFlowOperation(openOperation)) return;
      var elapsed = Date.now() - openedAt;
      var wait = Math.max(0, ANIM_MS - elapsed);
      setTimeout(finalRender, wait);
    });

  }

  // ai code: zoek de eerste dag binnen 60 dagen vooruit met minstens 1 vrij slot.
  // Gebruikt uitsluitend state.slotCounts (= bron-van-waarheid uit RPC).
  // Geen getDayConfig meer — die oude opening hours zijn vervangen door
  // capaciteit-blokken in het dashboard. Als slotCounts rijen voor een datum
  // heeft, betekent dat: er is capaciteit voor die dag.
  function firstAvailableDate() {
    if (!state.slotCounts) return null;
    var counts = state.slotCounts;
    var maxMap = state.slotMaxMap || {};
    var today = startOfDay(new Date());
    for (var i = 0; i < 60; i++) {
      var d = new Date(today);
      d.setDate(today.getDate() + i);
      var slots = getSlotsForDate(d);
      if (slots.length === 0) continue;
      var key = fmtDateKey(d);
      var hasFree = slots.some(function (t) {
        var slotKey = key + '|' + t;
        var occ = counts[slotKey] || 0;
        var effMax = (typeof maxMap[slotKey] === 'number') ? maxMap[slotKey] : 1;
        return occ < effMax;
      });
      if (hasFree) return key;
    }
    return null;
  }

  function closePopup() {
    var popup = document.getElementById(POPUP_ID);
    if (!popup) return;
    // ai code: funnel tracking — log waar de gebruiker uitstapte (drop-off).
    // keepalive zorgt dat dit ook lukt als de tab daarna sluit.
    if (popup.classList.contains('is-open')) {
      trackFunnel('popup_closed', { atStep: state.step });
    }
    advanceFlowGeneration(state);
    popup.classList.remove('is-open');
    // ai code: body styles terugzetten + scroll-positie restoren
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    if (typeof _savedScrollY === 'number') {
      window.scrollTo(0, _savedScrollY);
    }
    if (_lastContactTrigger && _lastContactTrigger.isConnected
        && typeof _lastContactTrigger.focus === 'function') {
      _lastContactTrigger.focus();
    }
    _lastContactTrigger = null;
  }
  var _savedScrollY = 0;
  var _lastContactTrigger = null;

  // Opt-in runtime seam voor de storefronttests. In het thema bestaat deze
  // global niet, waardoor er in productie geen extra API wordt aangemaakt.
  if (typeof window !== 'undefined' && window.CO_AI_TEST_HOOKS
      && typeof window.CO_AI_TEST_HOOKS === 'object') {
    window.CO_AI_TEST_HOOKS.setFlowMode = function (mode) {
      state.flowMode = mode;
      if (mode === FLOW_MODE.PERSONAL_CONTACT) {
        state.selectedDate = null;
        state.selectedSlot = null;
      }
    };
    window.CO_AI_TEST_HOOKS.renderStep = function (step) {
      state.step = step;
      if (step === 1) return step1HTML();
      if (step === 2) return step2HTML();
      if (step === 3) return step3HTML();
      if (step === 4) return step4HTML();
      return '';
    };
    window.CO_AI_TEST_HOOKS.renderHelp = function () {
      return helpHTML();
    };
    window.CO_AI_TEST_HOOKS.getFlowState = function () {
      return {
        step: state.step,
        completed: state.completed,
        flowIdentity: state.flowIdentity,
        appointmentId: state.appointmentId,
        editToken: state.editToken
      };
    };
    window.CO_AI_TEST_HOOKS.bindCurrentFlow = function () {
      var identity = getCurrentFlowIdentity();
      var mode = getCurrentFlowMode();
      var result = bindFlowIdentity(state, identity);
      state.flowMode = mode;
      return result;
    };
    window.CO_AI_TEST_HOOKS.setAsyncFlowState = function (patch) {
      if (!patch || typeof patch !== 'object') return;
      Object.keys(patch).forEach(function (key) { state[key] = patch[key]; });
    };
    window.CO_AI_TEST_HOOKS.getAsyncFlowState = function () {
      return {
        selectedDate: state.selectedDate,
        selectedSlot: state.selectedSlot,
        locationCompany: state.locationCompany || null,
        locationFullAddress: state.locationFullAddress || null,
        locationHours: state.locationHours,
        dateOverrides: state.dateOverrides,
        capacityRules: state.capacityRules,
        defaultDurationMinutes: state.defaultDurationMinutes,
        maxPerHour: state.maxPerHour,
        autoBlockHolidays: state.autoBlockHolidays,
        slotCounts: state.slotCounts,
        slotMaxMap: state.slotMaxMap || {},
        availability: state.availability
      };
    };
    window.CO_AI_TEST_HOOKS.getOperationState = function () {
      return {
        flowMode: state.flowMode,
        flowIdentity: state.flowIdentity,
        flowGeneration: state.flowGeneration,
        selectedDate: state.selectedDate,
        selectedSlot: state.selectedSlot
      };
    };
    window.CO_AI_TEST_HOOKS.loadLocationConfig = loadLocationConfig;
    window.CO_AI_TEST_HOOKS.loadSlotCounts = loadSlotCounts;
    window.CO_AI_TEST_HOOKS.loadAvailability = loadAvailability;
    window.CO_AI_TEST_HOOKS.openPopup = openPopup;
    window.CO_AI_TEST_HOOKS.closePopup = closePopup;
  }

  // ============================================================
  // INIT
  // ============================================================

  function attach() {
    loadState();

    var popup = document.getElementById(POPUP_ID);
    if (popup) {
      popup.addEventListener('click', handlePopupClick);
      popup.addEventListener('input', handleFormInput);
      popup.addEventListener('change', handleFormInput);
      popup.addEventListener('keydown', handleContactPaymentKeydown);
      // ai code: brute-force tap-target fix — Kalles theme overschrijft soms
      // de native <label>→input focus binding. Click ergens binnen .coa-field
      // → forceer focus op het input. Werkt onafhankelijk van theme CSS.
      popup.addEventListener('pointerdown', function (ev) {
        var field = ev.target && ev.target.closest && ev.target.closest('.coa-field');
        if (!field) return;
        var input = field.querySelector('input, textarea');
        if (!input) return;
        if (document.activeElement === input) return;
        // preventDefault zodat de native click-handling van label niet ook
        // nog eens focust en mogelijk caret-position resetten.
        ev.preventDefault();
        input.focus();
        // Plaats caret aan einde — anders blijft hij waar de tap landde
        try {
          var len = input.value ? input.value.length : 0;
          input.setSelectionRange(len, len);
        } catch (e) { /* number/email types geven error op setSelectionRange — niet erg */ }
      });
    }

    window.addEventListener('resize', scheduleStep2Fit);
    window.addEventListener('orientationchange', scheduleStep2Fit);
    if (window.visualViewport && window.visualViewport.addEventListener) {
      window.visualViewport.addEventListener('resize', scheduleStep2Fit);
    }
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(scheduleStep2Fit, function () {});
    }

    // Cart-button live mode swap
    document.addEventListener('change', function (ev) {
      var t = ev.target;
      if (!t || !t.matches) return;
      if (t.matches(SWITCH_SELECTOR) || t.matches(LOCATION_SELECT_SELECTOR)) {
        setTimeout(syncAllButtons, 0);
      }
      // ai code: variant-picker wisselt URL of hidden input — re-sync
      if (t.matches('[name="id"], select[name="id"], input[name="id"]')) {
        setTimeout(syncAllButtons, 0);
      }
    }, true);

    // ai code: variant-change via Shopify event of URL update (history.pushState)
    document.addEventListener('variant:change', function () { setTimeout(syncAllButtons, 0); });
    window.addEventListener('popstate', function () { setTimeout(syncAllButtons, 0); });
    // Theme variants pushen vaak via JS zonder events — poll als laatste vangnet
    var lastVariantId = getCurrentVariantIdGlobal();
    setInterval(function () {
      var cur = getCurrentVariantIdGlobal();
      if (cur !== lastVariantId) {
        lastVariantId = cur;
        syncAllButtons();
      }
    }, 800);

    // Shared click/submit handler. The early head bridge runs before app cart guards.
    function handleInstallationIntent(ev) {
      var target = ev.target;
      if (ev.type === 'submit') {
        target = ev.submitter || (ev.target && ev.target.querySelector && ev.target.querySelector(SUBMIT_SELECTOR));
        if (target && target.form !== ev.target) return;
      }
      var btn = target && target.closest && target.closest(SUBMIT_SELECTOR);
      if (!btn) return;
      // Decide from current installation inputs, including before an async
      // location lookup has applied a button marker.
      if (!passesPlanningProductGates() || !getCurrentLocation()) {
        // This customer action supersedes an older asynchronous planning click.
        beginPlanningLocationIntent();
        if (btn.hasAttribute(STATE_ATTR)) syncAllButtons();
        clearPlanningLocationRetry(btn);
        return;
      }
      ev.preventDefault();
      ev.stopImmediatePropagation();
      clearPlanningLocationRetry(btn);
      if (shouldBeAppointmentMode()) {
        beginPlanningLocationIntent();
        applyFlowModeToAllButtons(FLOW_MODE.LEGACY_DIRECT);
        openPopup();
        return;
      }
      if (btn.hasAttribute('data-co-ai-contact-revalidating')) return;
      beginPlanningLocationIntent();
      btn.setAttribute('data-co-ai-contact-revalidating', 'true');
      revalidatePersonalContactClick(btn);
    }
    window.CO_AI_HANDLE_INSTALLATION_INTENT = handleInstallationIntent;
    document.addEventListener('click', handleInstallationIntent, true);
    document.addEventListener('submit', handleInstallationIntent, true);

    // Contactplanning houdt toetsenbordfocus binnen de dialoog. De directe
    // Bleiswijk-/Enkhuizenroute behoudt exact haar bestaande focusgedrag.
    document.addEventListener('keydown', function (ev) {
      var p = document.getElementById(POPUP_ID);
      if (!p || !p.classList.contains('is-open')) return;
      if (ev.key === 'Escape') {
        closePopup();
        return;
      }
      trapContactDialogFocus(ev, p);
    });

    // Initial sync (mocht state al gezet zijn na page load)
    syncAllButtons();
    setTimeout(syncAllButtons, 500);
    setTimeout(syncAllButtons, 1500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})();
// rebuild 1779627856
// rebuild 1779628165
// rebuild 1779628230
// rebuild 1779628466
// rebuild 1779628600
// rebuild 1779628911
// rebuild 1779629469
// rebuild 1779629701
// rebuild 1779630036
// rebuild 1779630322
// rebuild 1779630644
// rebuild 1779631590
// rebuild 1779632151
// rebuild 1779635136
// rebuild 1779635818
// rebuild 1779636048
// rebuild 1779637483 — error banner removed, mobile UI tightened
// rebuild 1779637623
// rebuild 1779638246
// rebuild 1779638591
// rebuild 1779639244
// rebuild 1779639677
// rebuild 1779641495
// rebuild 1779641878
// rebuild 1779642044
// rebuild 1779642147
// rebuild 1779642424
// rebuild 1779642724
// rebuild 1779660797
// rebuild 1779662989
